import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, formatTime, formatTimeAgo, formatDateTime, formatDuration, formatClock, todayET, getDatePreset } from '../utils/formatters';
import { computeBreakScore, avgPriceForScore, scoreColor, scoreBg } from '../utils/breakScore';
import ThreadPanel from '../components/ThreadPanel';
import ScoreCell from '../components/ScoreCell';
import DateRangeFilter from '../components/DateRangeFilter';
import CustomerDetailModal from '../components/CustomerDetailModal';
import InfoTip from '../components/InfoTip';
import BreakPriceTimeline from '../components/BreakPriceTimeline';
import BreakOrderEditor from '../components/BreakOrderEditor';
import BreakTimeline, { FILL_TIME_INFO, BREAK_TOTAL_INFO } from '../components/BreakTimeline';
import { buildSpotRows, downloadSpotsCsv as downloadBreakSpotsCsv } from '../utils/breakSpots';
import useIsMobile from '../hooks/useIsMobile';

function getItemName(p) { return p.product_id ? p.product_name : p.custom_name; }
function getItemPrice(p) { return p.product_id ? (p.price || 0) : (p.custom_price || 0); }
function getItemCogs(p) { return p.product_id ? p.cogs : p.custom_cogs; }
function getItemSku(p) { return p.product_id ? p.sku : null; }

// `hidePrice` drops the Price column for lists where the items have no sale
// price at all — freebie COGS entries, where the whole point is that the
// product went out for free. Everything else stays identical.
function ProductTable({ products, onUpdateQty, onRemove, readOnly, useIndex, hidePrice, emptyText = 'No products added' }) {
  if (!products.length) return <div style={{ fontSize: '13px', color: 'var(--text-light)', padding: '8px 0' }}>{emptyText}</div>;
  const totalPrice = products.reduce((s, p) => s + getItemPrice(p) * p.quantity, 0);
  const totalCogs = products.reduce((s, p) => s + (getItemCogs(p) || 0) * p.quantity, 0);
  return (
    <div className="table-container">
      <table>
        <thead><tr><th>Product</th><th style={{ textAlign: 'center' }}>Qty</th>{!hidePrice && <th style={{ textAlign: 'right' }}>Price</th>}<th style={{ textAlign: 'right' }}>COGS</th>{!readOnly && <th></th>}</tr></thead>
        <tbody>
          {products.map((p, idx) => {
            const key = useIndex ? idx : p.id;
            const itemId = useIndex ? idx : p.id;
            const isCustom = !p.product_id;
            return (
              <tr key={key}>
                <td>
                  <div style={{ fontWeight: 500, fontSize: '13px' }}>
                    {getItemName(p)}
                    {p.is_free ? <span className="badge badge-info" style={{ marginLeft: '6px' }}>Free</span> : null}
                    {(isCustom || p.category === 'Custom') && <span className="badge badge-warning" style={{ marginLeft: '6px' }}>Custom</span>}
                    {/* Only saved templates carry this field. Flags a row that an
                        admin has since switched off in Admin -> Break Products, so
                        it's clear why loading the template will leave it out. */}
                    {p.available_for_breaks === 0 && (
                      <span className="badge badge-warning" style={{ marginLeft: '6px' }} title="Excluded in Admin → Break Products; loading this template will leave it out">Unavailable</span>
                    )}
                  </div>
                  {getItemSku(p) && <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{getItemSku(p)}</div>}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {readOnly ? p.quantity : (
                    <input type="number" min="1" value={p.quantity === 0 ? '' : p.quantity}
                      onChange={e => onUpdateQty(itemId, e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                      onBlur={e => { if (!e.target.value || parseInt(e.target.value) < 1) onUpdateQty(itemId, 1); }}
                      style={{ width: '60px', textAlign: 'center', padding: '4px' }} />
                  )}
                </td>
                {!hidePrice && <td style={{ textAlign: 'right' }}>{formatCurrency(getItemPrice(p) * p.quantity)}</td>}
                <td style={{ textAlign: 'right' }}>{getItemCogs(p) != null ? formatCurrency(getItemCogs(p) * p.quantity) : '--'}</td>
                {!readOnly && <td><button className="btn btn-danger btn-sm" onClick={() => onRemove(itemId)}>Remove</button></td>}
              </tr>
            );
          })}
          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
            <td>Totals</td>
            <td style={{ textAlign: 'center' }}>{products.reduce((s, p) => s + p.quantity, 0)}</td>
            {!hidePrice && <td style={{ textAlign: 'right' }}>{formatCurrency(totalPrice)}</td>}
            <td style={{ textAlign: 'right' }}>{formatCurrency(totalCogs)}</td>
            {!readOnly && <td></td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const BREAK_STATUSES = ['planned', 'live', 'pending', 'completed', 'cancelled'];
const BREAK_CATEGORIES = ['Football', 'Basketball', 'Baseball', 'Multi-Sport'];
const SURPRISE_SET_CATEGORIES = ['Football', 'Basketball', 'Baseball', 'Multi-Sport'];
const SPOT_TYPES = ['Team', 'Division'];
const statusBadgeClass = s => s === 'completed' ? 'badge-success' : s === 'cancelled' ? 'badge-danger' : s === 'live' ? 'badge-info' : 'badge-warning';

// Small wrapper that shows an instant tooltip on hover — the native title
// attribute delays ~1s, which felt sluggish for the Trade / Trade In buttons.
function HoverTip({ text, width = 240, children }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      {children}
      {show && (
        <span
          role="tooltip"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: '50%',
            transform: 'translateX(-50%)',
            background: '#1f2937', color: 'white',
            padding: '6px 10px', borderRadius: 6,
            fontSize: 12, lineHeight: 1.4, fontWeight: 500,
            whiteSpace: 'normal', width, textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 3000, pointerEvents: 'none',
          }}
        >{text}</span>
      )}
    </span>
  );
}

// Break-buyer roster for one scope, keyed by lowered handle, plus the
// resolver the info icons call per row. Held client-side so a name resolves as
// it is typed rather than on save.
//
// `enabled` keeps closed surfaces from fetching. Exactly one scope narrowing
// is used per caller:
//   excludeBreakId — the Break Spots sheet leaves its own break out, so a name
//     typed there never counts as its own history.
//   beforeDate — the Freebies surfaces count only breaks dated before the day
//     in scope, so everyone buying their first spot that day reads as a
//     first-timer for the whole day and as returning from the next day on.
//
// The roster stays null until it loads, and after a failed fetch: showing no
// icon beats calling a returning buyer a first-timer.
function useBreakBuyerRoster({ excludeBreakId = null, beforeDate = null, enabled = true }) {
  const [roster, setRoster] = useState(null);
  const [canOpenCustomerSummary, setCanOpenCustomerSummary] = useState(false);

  const refresh = useCallback(() => {
    if (!enabled) { setRoster(null); return Promise.resolve(); }
    const params = {};
    if (excludeBreakId != null) params.excludeBreakId = excludeBreakId;
    if (beforeDate) params.beforeDate = beforeDate;
    return api.get('/api/breaks/lifetime-break-buyers', { params })
      .then(({ data }) => {
        const byHandle = new Map();
        for (const b of data.buyers || []) byHandle.set((b.customer || '').trim().toLowerCase(), b);
        setRoster(byHandle);
        setCanOpenCustomerSummary(!!data.can_open_customer_summary);
      })
      .catch(() => {});
  }, [excludeBreakId, beforeDate, enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  // 'none' (nothing typed yet, or the roster hasn't loaded) / 'returning' /
  // 'first-time', plus the matched roster row the summary opens on.
  const resolve = useCallback((name) => {
    const key = (name || '').trim().toLowerCase();
    if (!key || !roster) return { state: 'none', buyer: null };
    const buyer = roster.get(key) || null;
    return { state: buyer ? 'returning' : 'first-time', buyer };
  }, [roster]);

  return { resolve, canOpenCustomerSummary, refresh, ready: roster != null };
}

const SPOT_ICON_SLOT = 16;

// Info icon beside a customer on the Break Spots sheet and the two Freebies
// tables.
//
// Black  = the handle is in the roster its surface asked for, i.e. they have
//          bought a break spot before (before this day, on the Freebies
//          tables). Clicking opens their customer summary — the same panel the
//          Top Customers grid opens — when the viewer may see customer data
//          and the handle resolves to a TikTok Shop buyer record.
// Yellow = no break purchases behind them: a first time break buyer. Hover or
//          click both just say so; there is nothing to open.
//
// The slot is rendered at a fixed width even when empty so the customer input
// doesn't jump sideways as a name is typed.
function SpotCustomerIcon({ state, buyer, canOpen, onOpen }) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!pinned) return;
    function handleAway(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setPinned(false);
    }
    document.addEventListener('mousedown', handleAway);
    document.addEventListener('touchstart', handleAway);
    return () => {
      document.removeEventListener('mousedown', handleAway);
      document.removeEventListener('touchstart', handleAway);
    };
  }, [pinned]);

  if (state === 'none') return <span style={{ width: SPOT_ICON_SLOT, flexShrink: 0 }} />;

  const returning = state === 'returning';
  const opens = returning && canOpen && !!buyer?.buyer_id;
  const tip = !returning
    ? 'First time break buyer'
    : opens
      ? 'View customer summary'
      : canOpen
        ? 'No linked TikTok Shop customer record'
        : 'Returning break buyer';
  const color = returning ? '#111827' : '#eab308';
  const show = hovered || pinned;

  return (
    <span ref={wrapRef}
      style={{ position: 'relative', display: 'inline-flex', width: SPOT_ICON_SLOT, flexShrink: 0, alignItems: 'center' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <svg width="14" height="14" viewBox="0 0 24 24"
        role="button" tabIndex={0} aria-label={tip} title={tip}
        style={{ cursor: 'pointer', display: 'block' }}
        onClick={(e) => {
          e.stopPropagation();
          // Opening the summary is the whole action; every other state has
          // nothing behind the icon, so the click pins the tooltip instead
          // (which is also how it reads on touch, where there is no hover).
          if (opens) onOpen(buyer);
          else setPinned(v => !v);
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          if (opens) onOpen(buyer);
          else setPinned(v => !v);
        }}>
        <circle cx="12" cy="12" r="10" fill={color} />
        <line x1="12" y1="11" x2="12" y2="17" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="12" y1="7.5" x2="12.01" y2="7.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      {show && (
        <span role="tooltip"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0,
            background: '#1f2937', color: 'white',
            padding: '5px 9px', borderRadius: 6,
            fontSize: 12, lineHeight: 1.4, fontWeight: 500,
            whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 3000, pointerEvents: 'none',
          }}>{tip}</span>
      )}
    </span>
  );
}

// Username cell for the two Freebies tables — the info icon, then the name.
// A row with no spots in scope (a freebie sent to someone outside the day's
// breaks) can't be a first time break buyer ON that day, so it shows the black
// icon when they bought before and nothing at all when they never have.
//
// `expanded` / `onToggle` drive the chevron that opens the customer's
// per-break dropdown; rows with no breaks in scope get an empty slot instead
// so the names stay lined up.
function FreebieCustomerName({ customer, inScope, roster, onOpen, expanded, onToggle, children }) {
  const info = roster.resolve(customer);
  const state = !inScope && info.state === 'first-time' ? 'none' : info.state;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {onToggle ? (
        <button type="button" onClick={onToggle}
          aria-expanded={!!expanded}
          aria-label={expanded ? `Hide ${customer}'s breaks` : `Show ${customer}'s breaks`}
          title={expanded ? 'Hide breaks' : 'Show breaks'}
          style={{
            width: SPOT_ICON_SLOT, flexShrink: 0, padding: 0, border: 'none', background: 'none',
            cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1,
          }}>{expanded ? '▼' : '▶'}</button>
      ) : (
        <span style={{ width: SPOT_ICON_SLOT, flexShrink: 0 }} />
      )}
      <SpotCustomerIcon state={state} buyer={info.buyer}
        canOpen={roster.canOpenCustomerSummary} onOpen={onOpen} />
      <span>{customer}</span>
      {children}
    </div>
  );
}

// The yellow-icon rule on the Freebies tables: spots in scope and no break
// spot bought before the day in scope. Backs the "Show only first time
// buyers" toggle, so the filter and the icons can never disagree.
function isFreebieFirstTimer(roster, customer, inScope) {
  return !!inScope && roster.resolve(customer).state === 'first-time';
}

// Dropdown row under a customer on the Freebies tables — one line per break
// they bought into within the table's scope, with that break's spend, spots,
// hit count, and the cards hit and spot freebies logged on its sheet.
function FreebieBreakDetailsRow({ details, colSpan, showSpend }) {
  const cell = { padding: '4px 8px', fontSize: 12, verticalAlign: 'top', borderBottom: '1px solid #f1f1f1' };
  const head = { ...cell, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' };
  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: '4px 8px 10px 30px', borderBottom: '1px solid #eee', background: 'var(--bg)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', border: '1px solid var(--border)', borderRadius: 6 }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: 'left' }}>Break</th>
              <th style={{ ...head, textAlign: 'right' }}>Spend</th>
              <th style={{ ...head, textAlign: 'right' }}>Spots</th>
              <th style={{ ...head, textAlign: 'right' }}>Hits</th>
              <th style={{ ...head, textAlign: 'left' }}>Cards Hit</th>
              <th style={{ ...head, textAlign: 'left' }}>Freebies</th>
            </tr>
          </thead>
          <tbody>
            {details.map(d => (
              <tr key={d.break_id}>
                <td style={cell}>#{d.break_number || '--'} {d.name}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{showSpend ? formatCurrency(d.spend) : '—'}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{d.spots}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{d.hits.length}</td>
                <td style={cell}>{d.hits.length ? d.hits.join(', ') : '—'}</td>
                <td style={cell}>{d.freebies.length ? d.freebies.join(', ') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

// "Show only first time buyers" switch above both Freebies tables. Only
// filters what's on screen; hidden rows keep their freebies.
function FirstTimeBuyersToggle({ checked, onChange, roster, shown, total }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 0, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ width: 'auto', margin: 0 }} />
      Show only first time buyers
      {checked && (
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {roster.ready ? `(${shown} of ${total})` : '(checking…)'}
        </span>
      )}
    </label>
  );
}

function BreakSpotsSheet({ breakId, category, spotType, breakName, breakNumber, breakStatus, finalScore, targetPrice, breakCogs, onClose, onNameChange, onStatusChange, onProductsChange, nextBreak, onStartNextBreak, onBreakEnded, timing, showHighBidOverlay: initialShowHighBidOverlay }) {
  const { user, hasPermission } = useAuth();
  const canSeeFinancials = !!user && ['admin', 'partner'].includes(user.role);
  const canPostNotifications = hasPermission('post_break_note');
  // Once a break is completed, only admins may edit anything on this page.
  // Used to readOnly the spot inputs and hide trade/title/post-note actions
  // for everyone else. Server-side guards enforce the same on PUT/POST.
  const lockedByCompletion = breakStatus === 'completed' && user?.role !== 'admin';
  // Mirrors what the break-products routes enforce: the manage_breaks
  // permission, and not a completed break the user may no longer edit.
  const canManageProducts = hasPermission('manage_breaks') && !lockedByCompletion;
  const [spots, setSpots] = useState([]);
  // Lifetime Top Break Customers roster behind the info icon in each customer
  // cell, with this break left out so a name entered here never counts as its
  // own history.
  const {
    resolve: customerIconFor,
    canOpenCustomerSummary,
    refresh: refreshLifetimeBreakBuyers,
  } = useBreakBuyerRoster({ excludeBreakId: breakId });
  // Buyer row whose customer summary is open over the sheet, or null.
  const [summaryBuyer, setSummaryBuyer] = useState(null);
  // Sort-order index of the spot that currently "holds" the high-bid lineage
  // (computed server-side). Renders show a gold border on that one customer cell.
  // The overlay's own { amount, customer } (computeHighBid on the server),
  // so the Stats bar's High Bid always matches what's on stream.
  const [highBid, setHighBid] = useState(null);
  const [highBidSortOrder, setHighBidSortOrder] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [showStats, setShowStats] = useState(true);
  // Phones get their own layout for this sheet (stacked header, collapsible
  // stats, one card per spot) — the desktop spreadsheet is far wider than the
  // screen. The stats start collapsed there, behind a one-line summary, so the
  // spots are what fills the screen on open. Desktop keeps `showStats`.
  const isMobile = useIsMobile();
  const [mobileStatsOpen, setMobileStatsOpen] = useState(false);
  const statsVisible = isMobile ? mobileStatsOpen : showStats;
  const [isStreaming, setIsStreaming] = useState(false);
  const [startingNext, setStartingNext] = useState(false);
  const [confirmNextBreak, setConfirmNextBreak] = useState(false);
  // End Break is the one timing stamp that isn't a by-product of work the
  // breaker is already doing, so it's the one that gets forgotten. A ticking
  // clock is the cheapest way to make a still-running break impossible to
  // ignore, and it makes the forgotten ones obvious at a glance.
  const [endingBreak, setEndingBreak] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  // Stamps returned by the most recent spot save, layered over the prop so the
  // clock reflects a promotion that happened seconds ago. The parent's copy
  // wins again as soon as it refetches, which is why this is a patch and not a
  // replacement.
  const [timingPatch, setTimingPatch] = useState(null);
  const liveTiming = timingPatch ? { ...timing, ...timingPatch } : timing;

  // The break is on the clock from its first spot selling until End Break is
  // pressed. Starts earlier than the server's `is_running` flag, because the
  // fill is part of the break's total — but stops on the same catch-all: a
  // completed or cancelled break is finished whatever its stamps say, so an
  // old break that predates this tracking must not tick away forever.
  // Mirrors isBreakRunning()'s allowlist: only a live or pending break can
  // have a rip in progress. Anything else is finished or not yet underway, and
  // its clock must not tick — an old break predating this tracking would
  // otherwise count up forever.
  const breakIsFinished = !['live', 'pending'].includes(breakStatus);
  const clockRunning = !!liveTiming?.break_started_at
    && !liveTiming?.break_ended_at
    && !breakIsFinished;
  // End Break stays gated on a filled board: nothing has been ripped yet.
  const breakIsRunning = !!liveTiming?.is_running;
  useEffect(() => {
    if (!clockRunning) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [clockRunning]);

  // Seconds between two stamps. A missing end stamp means "still running" and
  // is measured against the ticking clock, so the segment in progress counts
  // up live — but ONLY while the break really is running. On a finished break
  // a missing end stamp means nobody recorded it, and measuring to now would
  // invent a duration that grows forever. Returns null in that case, and until
  // its own start stamp exists.
  const elapsedBetween = (fromStamp, toStamp) => {
    if (!fromStamp) return null;
    const from = Date.parse(fromStamp);
    if (!Number.isFinite(from)) return null;
    if (!toStamp && !clockRunning) return null;
    const to = toStamp ? Date.parse(toStamp) : nowTick;
    if (!Number.isFinite(to)) return null;
    return Math.max(0, Math.round((to - from) / 1000));
  };

  // A card logged before the board filled is not a rip start, so the panel
  // must not show a rip from it — same rule the server applies.
  const panelRipStart = (() => {
    const { rip_started_at: rip, fill_ended_at: fill } = liveTiming || {};
    if (!rip || !fill) return null;
    return Date.parse(rip) >= Date.parse(fill) ? rip : null;
  })();

  const runningElapsed = elapsedBetween(liveTiming?.break_started_at, liveTiming?.break_ended_at);

  async function endBreak(source = 'button') {
    if (endingBreak) return;
    setEndingBreak(true);
    try {
      const { data } = await api.post(`/api/breaks/${breakId}/end`, { source });
      if (typeof onBreakEnded === 'function') onBreakEnded(data.break);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to end break');
    } finally {
      setEndingBreak(false);
    }
  }
  const [showHighBidOverlay, setShowHighBidOverlay] = useState(!!initialShowHighBidOverlay);
  // The Airtable case(s) this break rips (GET /api/breaks/:id/cases). Until
  // one is linked, an overlay prompts the host to link it; it can be
  // dismissed, leaving the warning in the case bar, and the spots stay
  // editable. Dismissing is local to this open sheet — every other user, and
  // this one next time the sheet opens, is prompted again until a case is
  // linked. Once one is, every spot's Card(s) box is a Reveal button until pressed —
  // on every spot, hit or not, so the buttons give nothing away — and the
  // buttons go away for good once the break is completed.
  const [caseInfo, setCaseInfo] = useState({ enabled: false, case_required: false, cases: [], reveals: [], unmatched: [] });
  const [casePickerOpen, setCasePickerOpen] = useState(false);
  const [linkableCases, setLinkableCases] = useState(null);
  const [caseSearch, setCaseSearch] = useState('');
  const [caseBusy, setCaseBusy] = useState(false);
  const [caseError, setCaseError] = useState('');
  const [revealing, setRevealing] = useState(null);
  // Brief bottom-of-screen message: a skunk on reveal, or a locked Reveal.
  const [toast, setToast] = useState(null);
  const [caseOverlayDismissed, setCaseOverlayDismissed] = useState(false);
  const [hidingHits, setHidingHits] = useState(false);
  const canManageCases = hasPermission('manage_breaks') && !lockedByCompletion && !['completed', 'cancelled'].includes(breakStatus);
  const caseRequired = !!caseInfo.case_required;
  const revealMode = caseInfo.cases.length > 0 && !['completed', 'cancelled'].includes(breakStatus);
  const isRevealPending = (idx) => revealMode && !caseInfo.reveals.includes(idx);
  // Whether a spot reads as a hit on screen. A spot under its Reveal button
  // never does — after Hide All its cards are still in Card(s), and a ticked
  // Hit box or a green row would give them away.
  const hitShown = (idx) => !isRevealPending(idx) && !!spots[idx]?.hit?.trim();
  const canReveal = hasPermission('manage_breaks') && !lockedByCompletion;
  // Nothing is revealed until every spot is sold: the break is pending once
  // the board fills. The save that fills it (here or in another tab) brings
  // the new status back in its timing, so the buttons unlock without a reload.
  const revealUnlocked = (timingPatch?.status || breakStatus) === 'pending';
  const REVEAL_LOCKED_MSG = 'Locked until the break is filled';
  const showCaseBar = caseInfo.enabled && (caseInfo.cases.length > 0 || canManageCases);

  const loadCaseInfo = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/breaks/${breakId}/cases`);
      setCaseInfo(data);
    } catch { /* the page still works without it */ }
  }, [breakId]);
  // Re-read on status changes too: case_required only applies while planned.
  useEffect(() => { loadCaseInfo(); }, [loadCaseInfo, breakStatus]);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(id);
  }, [toast]);

  const loadLinkableCases = async () => {
    setCaseSearch('');
    setCaseError('');
    setLinkableCases(null);
    try {
      const { data } = await api.get('/api/airtable/cases');
      setLinkableCases(data.cases || []);
    } catch (err) {
      setLinkableCases([]);
      setCaseError(err.response?.data?.error || 'Could not load cases from Airtable.');
    }
  };

  const openCasePicker = () => {
    setCasePickerOpen(true);
    loadLinkableCases();
  };

  // Start Next Break checks the NEXT break's case before anything moves. With
  // none linked, the confirmation becomes the place to pick it: Link Case &
  // Start Break stays disabled until one is chosen, and the link is made
  // first, so a failed link never ends this break or opens the next. A grey
  // Start Without Case beside Cancel skips the pick.
  const [nextCaseInfo, setNextCaseInfo] = useState(null);
  const [nextCaseChoice, setNextCaseChoice] = useState(null);
  const nextNeedsCase = !!nextCaseInfo?.enabled && (nextCaseInfo.cases || []).length === 0;
  useEffect(() => {
    if (!confirmNextBreak || !nextBreak) return;
    let cancelled = false;
    setNextCaseInfo(null);
    setNextCaseChoice(null);
    setCaseError('');
    api.get(`/api/breaks/${nextBreak.id}/cases`).then(({ data }) => {
      if (cancelled) return;
      setNextCaseInfo(data);
      if (data.enabled && (data.cases || []).length === 0) loadLinkableCases();
    }).catch(() => { if (!cancelled) setNextCaseInfo({ enabled: false, cases: [] }); });
    return () => { cancelled = true; };
  }, [confirmNextBreak, nextBreak?.id]);

  const linkCase = async (caseId) => {
    if (caseBusy) return;
    setCaseBusy(true);
    setCaseError('');
    try {
      const { data } = await api.post(`/api/breaks/${breakId}/cases`, { case_id: caseId });
      setCaseInfo(prev => ({ ...prev, cases: data.cases, case_required: data.case_required, unmatched: data.unmatched }));
      setCasePickerOpen(false);
    } catch (err) {
      setCaseError(err.response?.data?.error || 'Could not link that case.');
    } finally {
      setCaseBusy(false);
    }
  };

  // Hide All: every revealed spot goes back under its Reveal button, in every
  // open tab, so the reveal can be run again.
  const hideHits = async () => {
    if (hidingHits) return;
    setHidingHits(true);
    try {
      const { data } = await api.post(`/api/breaks/${breakId}/hide-hits`);
      setCaseInfo(prev => ({ ...prev, reveals: data.reveals || [] }));
    } catch (err) {
      alert(err.response?.data?.error || 'Could not hide hits.');
    } finally {
      setHidingHits(false);
    }
  };

  // Works after reveals too — the fix for a wrong case noticed mid-rip. The
  // server takes the case's cards back out of Card(s) and puts every spot back
  // under Reveal, so the confirmation says so first.
  const unlinkCase = async (c) => {
    const revealed = caseInfo.has_revealed || caseInfo.reveals.length > 0;
    const message = revealed
      ? `Unlink ${c.case_number}?\n\nSpots have already been revealed from this break. ${c.case_number}'s cards will be removed from Card(s) `
        + `(anything typed by hand stays), and every spot will go back to Reveal so the right case can be revealed.`
      : `Remove ${c.case_number} from this break?`;
    if (caseBusy || !window.confirm(message)) return;
    setCaseBusy(true);
    try {
      const { data } = await api.delete(`/api/breaks/${breakId}/cases/${c.id}`);
      setCaseInfo(prev => ({ ...prev, cases: data.cases, case_required: data.case_required, unmatched: data.unmatched, reveals: [], has_revealed: false }));
      // The unlinked case is free again, so the picker's list must say so.
      if (casePickerOpen) loadLinkableCases();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not remove that case.');
    } finally {
      setCaseBusy(false);
    }
  };
  const [tradeMode, setTradeMode] = useState(false);
  const [tradeSelection, setTradeSelection] = useState([]);
  // Holds the preview shown in the Confirm Trade modal. null = no modal open.
  const [tradeConfirm, setTradeConfirm] = useState(null);
  const [executingTrade, setExecutingTrade] = useState(false);
  // Trade-In flow (single customer surrenders 2 of their teams for 1 empty
  // team). Available only in Team mode. Walkthrough states:
  //   tradeInMode = true: select up to 2 cells owned by the same customer
  //   tradeInConfirm: confirm modal preview, opened after the 2nd selection
  //   tradeInTargetMode = true: admin picks the empty target by clicking a
  //     green + button overlaid on each eligible cell
  const [tradeInMode, setTradeInMode] = useState(false);
  const [tradeInSelection, setTradeInSelection] = useState([]);
  const [tradeInConfirm, setTradeInConfirm] = useState(null);
  const [tradeInTargetMode, setTradeInTargetMode] = useState(false);
  const [executingTradeIn, setExecutingTradeIn] = useState(false);
  const [fitToScreen, setFitToScreen] = useState(true);
  const [rowHeight, setRowHeight] = useState(null);
  const [activeNotification, setActiveNotification] = useState(null);
  const [notifHistory, setNotifHistory] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [showPostNotifModal, setShowPostNotifModal] = useState(false);
  const [postNotifDraft, setPostNotifDraft] = useState('');
  // Quick-add shortcut for Product Additions. Opening the panel focuses the
  // search box, so a whole add is: click, type, Enter. `additions` mirrors the
  // break's is_free rows so a repeat add is a single +1 click.
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addHighlight, setAddHighlight] = useState(0);
  const [additions, setAdditions] = useState([]);
  const [addBusy, setAddBusy] = useState(false);
  const [addFlash, setAddFlash] = useState('');
  const [addError, setAddError] = useState('');
  const addPanelRef = useRef(null);
  const addInputRef = useRef(null);
  const addBtnRef = useRef(null);
  const [addAnchor, setAddAnchor] = useState(null);
  const addSearchSeq = useRef(0);
  const notifPanelRef = useRef(null);
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const theadRef = useRef(null);
  // Row highlight is driven from mouse moves here, not CSS :hover. A :hover
  // set on a spot row sticks to that row whenever the browser stops getting
  // mouse events over the sheet — the pointer leaves the window edge-on, the
  // window loses focus (cmd-tab, a screenshot tool, a second monitor), or the
  // rows shift under a parked pointer — so a spot stayed shaded grey with the
  // mouse nowhere near it. The rule here is strict: a row is highlighted only
  // while a mouse move has put the pointer on it, and anything that could have
  // moved the pointer or the rows since clears it.
  const [hoverRow, setHoverRow] = useState(null);
  const isMulti = category === 'Multi-Sport';
  const loaded = useRef(false);
  const saveTimer = useRef(null);
  const wsRef = useRef(null);
  const remoteUpdate = useRef(false);
  const lastSaveId = useRef(0);
  const pendingSave = useRef(false);
  // sort_order indices the user has touched since the last successful save.
  // The auto-save sends only these rows (server UPSERTs them, untouched rows
  // are left alone), and the WS handler refuses to overwrite them with remote
  // state. Together this prevents a stale full-payload save from clobbering a
  // change the tab missed via WS.
  const dirtySpots = useRef(new Set());

  // Check if this break is already streaming on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/auction/live-break');
        if (data.breakId === breakId) setIsStreaming(true);
      } catch {}
    })();
  }, [breakId]);

  const toggleStreaming = async () => {
    try {
      if (isStreaming) {
        await api.post('/api/auction/live-break', { breakId: null });
        setIsStreaming(false);
      } else {
        await api.post('/api/auction/live-break', { breakId });
        setIsStreaming(true);
      }
    } catch (err) {
      console.error('Toggle streaming error:', err);
    }
  };

  // "Start Next Break" hands off to the break sitting directly above this one
  // in the breaks table: the parent swaps this sheet to that break and streams
  // it to the Auction Grid in the same click.
  //
  // Auto-save is debounced 500ms and its timer is cleared when this sheet
  // unmounts, so a hand-off fired right after a keystroke would drop that edit.
  // Wait for the queued/in-flight save to settle first (capped, so a failing
  // save can't strand the button).
  const waitForSaveToSettle = async () => {
    for (let i = 0; i < 20 && (dirtySpots.current.size > 0 || pendingSave.current); i++) {
      await new Promise(r => setTimeout(r, 100));
    }
  };

  // Confirmed from the modal, which names the break first — this switches the
  // live Auction Grid, so it never fires straight off the button click.
  const startNextBreak = async () => {
    if (!nextBreak || startingNext || typeof onStartNextBreak !== 'function') return;
    setStartingNext(true);
    try {
      await waitForSaveToSettle();
      await onStartNextBreak(nextBreak);
      setConfirmNextBreak(false);
    } catch (err) {
      console.error('Start next break error:', err);
    } finally {
      setStartingNext(false);
    }
  };

  const toggleHighBidOverlay = async () => {
    const next = !showHighBidOverlay;
    setShowHighBidOverlay(next);
    try {
      await api.post(`/api/breaks/${breakId}/toggle-high-bid-overlay`, { enabled: next });
    } catch (err) {
      setShowHighBidOverlay(!next);
      console.error('Toggle high-bid overlay error:', err);
    }
  };

  const toggleTradeCheck = (idx) => {
    setTradeSelection(prev => {
      if (prev.includes(idx)) return prev.filter(i => i !== idx);
      return [...prev, idx];
    });
  };

  // Group selected spots by current customer. Returns
  // { ok: false, error } or { ok: true, groups: [{display, sortOrders}, ...] }.
  const validateTradeSelection = () => {
    if (tradeSelection.length < 2) return { ok: false, error: 'Pick at least 2 spots.' };
    const byCustomer = new Map();
    for (const so of tradeSelection) {
      const s = spots[so];
      const cust = (s?.customer || '').trim();
      if (!cust) return { ok: false, error: `Spot ${so + 1} has no customer assigned.` };
      const key = cust.toLowerCase();
      if (!byCustomer.has(key)) byCustomer.set(key, { display: cust, sortOrders: [] });
      byCustomer.get(key).sortOrders.push(so);
    }
    if (byCustomer.size !== 2) {
      return { ok: false, error: `Need spots from exactly 2 customers (found ${byCustomer.size}).` };
    }
    return { ok: true, groups: [...byCustomer.values()] };
  };

  // Apply Trade now BUILDS the confirmation preview and opens the modal —
  // executeTrade() (called from the modal's Confirm button) is what actually
  // hits the trade endpoint. Cancel leaves tradeMode + tradeSelection intact
  // so the user can adjust their selection.
  const applyTrade = () => {
    const v = validateTradeSelection();
    if (!v.ok) { alert(v.error); return; }

    // Build per-customer preview from local state. Freebies travel with the
    // customer — so each group's `movingFreebies` are the non-empty freebies
    // on the spots they're giving up, in sort_order order.
    const teamLabel = (sortOrder) => {
      const row = spotRows[sortOrder];
      if (!row) return `Spot ${sortOrder + 1}`;
      return isDivision ? row.division
        : isMulti ? [row.nfl, row.nba, row.mlb].filter(Boolean).join(' / ')
        : (row.nfl || row.nba || row.mlb || `Spot ${sortOrder + 1}`);
    };
    const allCustomerSet = new Set(v.groups.map(g => g.display));
    const groups = v.groups.map(g => {
      const sortedSelf = [...g.sortOrders].sort((a, b) => a - b);
      const givingUp = sortedSelf.map(teamLabel);
      // Receiving = the OTHER customers' selected sort_orders.
      const otherSortOrders = v.groups
        .filter(other => other.display !== g.display)
        .flatMap(other => other.sortOrders)
        .sort((a, b) => a - b);
      const receiving = otherSortOrders.map(teamLabel);
      const movingFreebies = sortedSelf
        .map(so => (spots[so]?.freebie || '').trim())
        .filter(Boolean);
      return { customer: g.display, givingUp, receiving, movingFreebies };
    });

    setTradeConfirm({
      groups,
      request: tradeSelection.length === 2
        ? { kind: 'two-spot', spotA: tradeSelection[0], spotB: tradeSelection[1] }
        : { kind: 'multi', spotOrders: [...tradeSelection] },
      // Suppress noise for the corner case where one customer wasn't in the
      // typed set but somehow appeared — keep allCustomerSet for future use.
      allCustomers: [...allCustomerSet],
    });
  };

  const executeTrade = async () => {
    if (!tradeConfirm) return;
    setExecutingTrade(true);
    try {
      const { request } = tradeConfirm;
      let data;
      if (request.kind === 'two-spot') {
        ({ data } = await api.post(`/api/breaks/${breakId}/trade`, {
          spotA: request.spotA,
          spotB: request.spotB,
        }));
      } else {
        ({ data } = await api.post(`/api/breaks/${breakId}/multi-trade`, {
          spotOrders: request.spotOrders,
        }));
      }
      // Rebuild local spots from server response
      const savedByOrder = {};
      for (const s of data.spots) savedByOrder[s.sort_order] = s;
      loaded.current = false;
      dirtySpots.current = new Set();
      setSpots(spotRows.map((_, i) => ({
        customer: savedByOrder[i]?.customer || '',
        price: savedByOrder[i]?.price ?? '',
        hit: savedByOrder[i]?.hit || '',
        freebie: savedByOrder[i]?.freebie || ''
      })));
      if (data.high_bid_spot_id != null) {
        const hbSpot = data.spots.find(s => s.id === data.high_bid_spot_id);
        setHighBidSortOrder(hbSpot ? hbSpot.sort_order : null);
      } else {
        setHighBidSortOrder(null);
      }
      setHighBid(data.high_bid ?? null);
      setTimeout(() => { loaded.current = true; }, 100);
      setTradeSelection([]);
      setTradeMode(false);
      setTradeConfirm(null);
    } catch (err) {
      console.error('Trade error:', err);
      alert(err.response?.data?.error || 'Trade failed');
    }
    setExecutingTrade(false);
  };

  const cancelTradeConfirm = () => {
    if (executingTrade) return;
    setTradeConfirm(null);
  };

  const cancelTrade = () => {
    setTradeSelection([]);
    setTradeMode(false);
  };

  // ---------- Trade-In flow ----------
  // Note: `spots[i]` corresponds to `spotRows[i]` (see the load effect) and
  // the DB row with sort_order === i. Selection indices ARE the sort_order.
  // First-selected customer (lowercased + trimmed) — locks subsequent picks.
  const tradeInLockedCustomer = (() => {
    const first = tradeInSelection[0];
    if (first == null) return null;
    const s = spots[first];
    return (s?.customer || '').trim().toLowerCase() || null;
  })();

  const toggleTradeInCheck = (idx) => {
    if (!tradeInMode || tradeInTargetMode) return;
    const spot = spots[idx];
    if (!spot) return;
    const cust = (spot.customer || '').trim().toLowerCase();
    if (!cust) return; // empty cells can't be sources
    setTradeInSelection(prev => {
      if (prev.includes(idx)) return prev.filter(i => i !== idx);
      // Lock to first-selected customer.
      if (prev.length === 1) {
        const firstSpot = spots[prev[0]];
        const lockedCust = (firstSpot?.customer || '').trim().toLowerCase();
        if (cust !== lockedCust) return prev;
      }
      if (prev.length >= 2) return prev;
      const next = [...prev, idx];
      // Auto-open the confirm modal once we have two valid sources.
      if (next.length === 2) {
        const a = spots[next[0]];
        const b = spots[next[1]];
        const rowA = spotRows[next[0]];
        const rowB = spotRows[next[1]];
        const teamA = rowA?.nfl || rowA?.nba || rowA?.mlb || '';
        const teamB = rowB?.nfl || rowB?.nba || rowB?.mlb || '';
        const priceA = Number(a?.price) || 0;
        const priceB = Number(b?.price) || 0;
        const freebies = [a?.freebie, b?.freebie].map(x => (x || '').trim()).filter(Boolean);
        setTradeInConfirm({
          customer: a?.customer || '',
          sourceA: { sort_order: next[0], team: teamA, price: priceA },
          sourceB: { sort_order: next[1], team: teamB, price: priceB },
          sum: priceA + priceB,
          movingFreebies: freebies,
        });
      }
      return next;
    });
  };

  const cancelTradeIn = () => {
    if (executingTradeIn) return;
    setTradeInSelection([]);
    setTradeInConfirm(null);
    setTradeInTargetMode(false);
    setTradeInMode(false);
  };

  const cancelTradeInConfirm = () => {
    if (executingTradeIn) return;
    setTradeInConfirm(null);
    setTradeInSelection([]);
    // Stay in trade-in mode so the admin can re-select.
  };

  const confirmTradeInProceed = () => {
    if (!tradeInConfirm) return;
    setTradeInConfirm(null);
    setTradeInTargetMode(true);
  };

  const pickTradeInTarget = async (targetIdx) => {
    if (!tradeInTargetMode || executingTradeIn) return;
    const [a, b] = tradeInSelection;
    if (a == null || b == null) return;
    setExecutingTradeIn(true);
    try {
      // The target is an empty spot, and the debounced auto-save only persists
      // rows the user actually edited — so the target (and, on a fast click, a
      // freshly-typed source) may not exist server-side yet. Persist all three
      // by sort_order first, otherwise the trade-in 400s with "spot(s) not found
      // by sort_order".
      const updates = [a, b, targetIdx]
        .map(buildSpotUpdate)
        .filter(u => u && u.team);
      if (updates.length) {
        await api.put(`/api/breaks/${breakId}/spots`, { updates, saveId: Date.now() });
      }
      await api.post(`/api/breaks/${breakId}/trade-in`, {
        source_a_sort_order: a,
        source_b_sort_order: b,
        target_sort_order: targetIdx,
      });
      // Server broadcasts spots via WS; local state will update through the
      // existing handler. Refetch trade-ins for the dropdown.
      try { await fetchTradeIns(); } catch {}
      setTradeInSelection([]);
      setTradeInTargetMode(false);
      setTradeInMode(false);
    } catch (err) {
      console.error('Trade-in error:', err);
      alert(err.response?.data?.error || 'Trade-in failed');
    } finally {
      setExecutingTradeIn(false);
    }
  };

  // Trade-In list state + fetcher (the dropdown below renders these).
  const [tradeIns, setTradeIns] = useState([]);
  const fetchTradeIns = useCallback(async () => {
    if (!breakId) return;
    try {
      const { data } = await api.get(`/api/breaks/${breakId}/trade-ins`);
      setTradeIns(data?.trade_ins || []);
    } catch {}
  }, [breakId]);
  useEffect(() => { fetchTradeIns(); }, [fetchTradeIns]);

  const undoTradeIn = async (tradeInId) => {
    if (!window.confirm('Undo this trade-in?')) return;
    try {
      await api.post(`/api/breaks/${breakId}/trade-ins/${tradeInId}/undo`);
      await fetchTradeIns();
    } catch (err) {
      alert(err.response?.data?.error || 'Undo failed');
    }
  };

  const postNotification = async () => {
    const msg = postNotifDraft.trim();
    if (!msg) return;
    try {
      await api.post(`/api/breaks/${breakId}/notifications`, { message: msg });
      setPostNotifDraft('');
      setShowPostNotifModal(false);
    } catch (err) {
      console.error('Post notification error:', err);
      alert(err.response?.data?.error || 'Failed to post note');
    }
  };

  const spotRows = buildSpotRows(category, spotType);
  const isDivision = spotType === 'Division';
  const totalVisibleRows = isDivision ? spotRows.reduce((sum, r) => sum + (r.multiRows?.length || 1), 0) : spotRows.length;
  const MIN_ROW_HEIGHT = 20;

  const measureAndFit = useCallback(() => {
    if (!fitToScreen) { setRowHeight(null); return; }
    const headerH = headerRef.current?.offsetHeight || 0;
    const statsH = statsRef.current?.offsetHeight || 0;
    const theadH = theadRef.current?.offsetHeight || 0;
    const containerPadding = 20; // bottom padding on table container
    const available = window.innerHeight - headerH - statsH - theadH - containerPadding;
    const ideal = Math.floor(available / totalVisibleRows);
    setRowHeight(ideal >= MIN_ROW_HEIGHT ? ideal : null);
  }, [fitToScreen, totalVisibleRows]);

  useLayoutEffect(() => {
    measureAndFit();
  }, [fitToScreen, showStats, totalVisibleRows, measureAndFit, showCaseBar, caseRequired, caseInfo.cases.length, caseInfo.unmatched.length]);

  useEffect(() => {
    if (!fitToScreen) return;
    const onResize = () => measureAndFit();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fitToScreen, measureAndFit]);

  // A mouse move over a row is the only thing that turns the highlight on, and
  // it names the row the pointer is physically on at that moment.
  const handleSheetMouseMove = (e) => {
    setHoverRow(e.target?.closest?.('tr[data-spot-row]')?.dataset.spotRow ?? null);
  };

  const clearHover = useCallback(() => setHoverRow(null), []);

  // Anything that moves rows out from under the pointer — scrolling the sheet,
  // a fit-to-screen re-measure, the stats bar opening or closing — drops the
  // highlight. It comes back only when the mouse moves again, so no row is
  // ever shaded on the strength of where the pointer used to be.
  useLayoutEffect(() => { clearHover(); }, [rowHeight, showStats, totalVisibleRows, clearHover]);

  // The pointer can leave without a mouseout ever reaching the row it was on.
  useEffect(() => {
    document.addEventListener('mouseleave', clearHover);
    window.addEventListener('blur', clearHover);
    return () => {
      document.removeEventListener('mouseleave', clearHover);
      window.removeEventListener('blur', clearHover);
    };
  }, [clearHover]);

  useEffect(() => {
    if (!showNotifPanel) return;
    function handleClick(e) {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) setShowNotifPanel(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showNotifPanel]);

  useEffect(() => {
    if (!showAddProduct) return;
    function handleClick(e) {
      if (addPanelRef.current && !addPanelRef.current.contains(e.target)) setShowAddProduct(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showAddProduct]);

  // The panel hangs under the button, but the header sits at the right edge of
  // a screen that may be narrower than the panel. Anchoring to the button's
  // rect and clamping to the viewport keeps it fully on screen at any width,
  // which plain right:0 does not.
  const placeAddPanel = useCallback(() => {
    const rect = addBtnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(360, window.innerWidth - 24);
    // Clamped so a button scrolled part-way off the phone action strip still
    // opens the panel on screen. A no-op wherever the button is fully visible.
    const right = Math.max(12, Math.min(window.innerWidth - rect.right, window.innerWidth - width - 12));
    setAddAnchor({ top: rect.bottom + 6, right, width });
  }, []);

  useEffect(() => {
    if (!showAddProduct) return;
    placeAddPanel();
    addInputRef.current?.focus();
    window.addEventListener('resize', placeAddPanel);
    return () => window.removeEventListener('resize', placeAddPanel);
  }, [showAddProduct, placeAddPanel]);

  // The "Added X" confirmation clears itself so the panel doesn't accumulate
  // stale messages while several products go in back to back.
  useEffect(() => {
    if (!addFlash) return;
    const t = setTimeout(() => setAddFlash(''), 2500);
    return () => clearTimeout(t);
  }, [addFlash]);

  // Every write returns the break's full product list. Keep the local mirror
  // and hand it up so the header stats (COGS, margin, score) move with it
  // instead of going stale until the sheet is closed.
  const applyBreakProducts = useCallback((products) => {
    const list = products || [];
    setAdditions(list.filter(p => p.is_free));
    if (onProductsChange) onProductsChange(list);
  }, [onProductsChange]);

  const openAddProduct = async () => {
    // Place it before it mounts: a panel rendered hidden (awaiting placement)
    // cannot take autofocus, and the whole point is that typing starts here.
    placeAddPanel();
    setShowAddProduct(true);
    setAddQuery(''); setAddResults([]); setAddHighlight(0); setAddError(''); setAddFlash('');
    try {
      const { data } = await api.get(`/api/breaks/${breakId}`);
      applyBreakProducts(data.break?.products);
    } catch { /* panel still works; the list just starts empty */ }
  };

  // Sequence-guarded so a slow response for an earlier keystroke can't land
  // on top of the results for what's in the box now.
  const searchAddProducts = async (q) => {
    const seq = ++addSearchSeq.current;
    if (!q.trim()) { setAddResults([]); setAddHighlight(0); return; }
    try {
      const { data } = await api.get(`/api/breaks/search-products?q=${encodeURIComponent(q)}`);
      if (seq !== addSearchSeq.current) return;
      setAddResults(data.products || []);
      setAddHighlight(0);
    } catch {
      if (seq === addSearchSeq.current) setAddResults([]);
    }
  };

  const addProductAddition = async (prod) => {
    if (!prod || addBusy) return;
    setAddBusy(true); setAddError('');
    try {
      const { data } = await api.post(`/api/breaks/${breakId}/products`,
        { product_id: prod.product_id, quantity: 1, is_free: true });
      applyBreakProducts(data.products);
      setAddFlash(`Added ${prod.product_name}`);
      setAddQuery(''); setAddResults([]); setAddHighlight(0);
      addSearchSeq.current++;
      addInputRef.current?.focus();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add product');
    }
    setAddBusy(false);
  };

  // Step an existing addition. Stepping the last one off removes the row, so a
  // mis-click is undone in the same place it was made.
  const stepAddition = async (row, delta) => {
    if (addBusy) return;
    const next = (row.quantity || 0) + delta;
    setAddBusy(true); setAddError('');
    try {
      const { data } = next < 1
        ? await api.delete(`/api/breaks/${breakId}/products/${row.id}`)
        : await api.put(`/api/breaks/${breakId}/products/${row.id}`, { quantity: next });
      applyBreakProducts(data.products);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to update product');
    }
    setAddBusy(false);
  };

  // Derive compact styles when fitToScreen is active
  const isFitting = fitToScreen && rowHeight != null;
  const tightLevel = isFitting ? (rowHeight < 28 ? (rowHeight < 22 ? 3 : 2) : 1) : 0;
  // tightLevel 0 = normal, 1 = reduce header/stats padding, 2 = also reduce row padding, 3 = also reduce font

  const headerPadding = tightLevel >= 1 ? '6px 20px' : '12px 20px';
  const statsPadding = tightLevel >= 1 ? '6px 20px' : '12px 20px';
  const statsFontSize = tightLevel >= 1 ? '15px' : '18px';

  const computedTdPad = tightLevel >= 2 ? '2px 8px' : '6px 8px';
  const computedInputPad = tightLevel >= 2 ? '1px 6px' : '4px 8px';
  const computedFontSize = tightLevel >= 3 ? '12px' : '13px';

  useEffect(() => {
    dirtySpots.current = new Set();
    (async () => {
      try {
        const { data } = await api.get(`/api/breaks/${breakId}/spots`);
        const savedByOrder = {};
        for (const s of data.spots) savedByOrder[s.sort_order] = s;
        setSpots(spotRows.map((_, i) => ({
          customer: savedByOrder[i]?.customer || '',
          price: savedByOrder[i]?.price ?? '',
          hit: savedByOrder[i]?.hit || '',
          freebie: savedByOrder[i]?.freebie || '',
          price_components: savedByOrder[i]?.price_components || null,
        })));
        const hbSpot = data.spots.find(s => s.id === data.high_bid_spot_id);
        setHighBidSortOrder(hbSpot ? hbSpot.sort_order : null);
        setHighBid(data.high_bid ?? null);
      } catch {
        setSpots(spotRows.map(() => ({ customer: '', price: '', hit: '', freebie: '', price_components: null })));
        setHighBidSortOrder(null);
        setHighBid(null);
      }
      setTimeout(() => { loaded.current = true; }, 100);
    })();
    // Notifications history for the bell — independent of spots load
    api.get(`/api/breaks/${breakId}/notifications`)
      .then(({ data }) => setNotifHistory(data.notifications || []))
      .catch(() => {});
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [breakId, category]);

  // WebSocket for real-time sync.
  //
  // Rebuilt around a connect() that owns one socket at a time. The previous
  // version copied handlers onto the replacement socket — `newWs.onopen =
  // ws.onopen` — but that handler was a closure over the OLD socket, so the
  // join was sent on the corpse and the new socket never entered the room.
  // Its onclose closure compared against the original socket too, so after
  // one reconnect no further reconnect could fire. Either way the tab went
  // quiet until someone reloaded.
  useEffect(() => {
    let closed = false;          // the effect has torn down — stop reconnecting
    let socket = null;
    let retryTimer = null;
    let watchdog = null;

    // The server pings every 25s. Hearing nothing for well over that means the
    // socket is dead even if the browser still calls it OPEN, which is what a
    // proxy dropping an idle connection looks like from in here.
    const WATCHDOG_MS = 70_000;
    const armWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        // Closing triggers onclose, which schedules the reconnect.
        try { socket?.close(); } catch { /* already gone */ }
      }, WATCHDOG_MS);
    };

    // Anything that happened while the socket was down was never delivered, so
    // a reconnect has to re-read the board rather than assume it is current.
    const resync = async () => {
      try {
        const { data } = await api.get(`/api/breaks/${breakId}/spots`);
        const savedByOrder = {};
        for (const s of data.spots) savedByOrder[s.sort_order] = s;
        remoteUpdate.current = true;
        setSpots(prev => prev.map((cur, i) => {
          // In-flight local edits still win — they have not been saved yet.
          if (dirtySpots.current.has(i)) return cur;
          const remote = savedByOrder[i];
          if (!remote) return cur;
          return {
            customer: remote.customer || '',
            price: remote.price ?? '',
            hit: remote.hit || '',
            freebie: remote.freebie || '',
            price_components: remote.price_components || null,
          };
        }));
        const hbSpot = data.spots.find(s => s.id === data.high_bid_spot_id);
        setHighBidSortOrder(hbSpot ? hbSpot.sort_order : null);
        setHighBid(data.high_bid ?? null);
      } catch { /* the socket is back, the next broadcast will carry us */ }
      try {
        const { data } = await api.get(`/api/breaks/${breakId}`);
        if (data?.break) setTimingPatch(data.break);
      } catch { /* clock catches up on the next save */ }
    };

    function connect(isReconnect) {
      if (closed) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws/breaks`);
      socket = ws;
      wsRef.current = ws;

      ws.onopen = () => {
        // Bound to THIS socket, not to whichever one happened to be first.
        ws.send(JSON.stringify({ type: 'join', breakId }));
        armWatchdog();
        if (isReconnect) resync();
      };

      ws.onclose = () => {
        clearTimeout(watchdog);
        if (closed || socket !== ws) return;
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => connect(true), 2000);
      };

      ws.onerror = () => { try { ws.close(); } catch { /* onclose handles it */ } };

      ws.onmessage = handleMessage;
    }

    const handleMessage = (event) => {
      // Every message, whatever it is, proves the socket is alive.
      armWatchdog();
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'ping') {
          try { socket?.send(JSON.stringify({ type: 'pong' })); } catch { /* next round will drop it */ }
          return;
        }
        if (msg.type === 'spots_update' && String(msg.breakId) === String(breakId) && msg.spots) {
          // Timing is read before the echo check on purpose: these stamps are
          // the server's answer, not a copy of what we typed, and they are the
          // thing that moves the clock off Fill when the board fills.
          if (msg.timing) setTimingPatch(msg.timing);
          // Skip echoes of our own saves.
          if (msg.saveId && msg.saveId === lastSaveId.current) return;
          // Do NOT skip when pendingSave is true. Previously we dropped the
          // whole message, leaving the tab with stale state — the next save
          // would then overwrite the change we'd refused to absorb. Instead,
          // merge spot-by-spot, leaving any sort_order in dirtySpots alone
          // (those are the user's in-flight edits and would otherwise lose
          // the keystroke).
          const savedByOrder = {};
          for (const s of msg.spots) savedByOrder[s.sort_order] = s;
          remoteUpdate.current = true;
          setSpots(prev => prev.map((cur, i) => {
            if (dirtySpots.current.has(i)) return cur;
            const remote = savedByOrder[i];
            if (!remote) return cur;
            return {
              customer: remote.customer || '',
              price: remote.price ?? '',
              hit: remote.hit || '',
              freebie: remote.freebie || '',
              price_components: remote.price_components || null,
            };
          }));
          // Pick up the server-computed high-bid spot id so the High Bid
          // customer label in non-editing tabs updates without a refresh.
          if (Object.prototype.hasOwnProperty.call(msg, 'high_bid_spot_id')) {
            const hbSpot = msg.spots.find(s => s.id === msg.high_bid_spot_id);
            setHighBidSortOrder(hbSpot ? hbSpot.sort_order : null);
            setHighBid(msg.high_bid ?? null);
          }
        } else if (msg.type === 'cases_changed' && String(msg.breakId) === String(breakId)) {
          // Someone linked or removed a case: re-read, so the prompt clears
          // (or comes back) in every open tab, not just theirs.
          loadCaseInfo();
        } else if (msg.type === 'spot_revealed' && String(msg.breakId) === String(breakId)) {
          setCaseInfo(prev => prev.reveals.includes(msg.sort_order)
            ? prev
            : { ...prev, reveals: [...prev.reveals, msg.sort_order] });
        } else if (msg.type === 'break_notification' && String(msg.breakId) === String(breakId) && msg.notification) {
          setNotifHistory(prev => [msg.notification, ...prev]);
          // Skip the overlay echo for the sender themselves
          if (msg.notification.posted_by_user_id !== user?.id) {
            setActiveNotification(msg.notification);
          }
        }
      } catch (e) { /* ignore */ }
    };

    connect(false);

    // A backgrounded tab gets its timers throttled and its socket dropped, so
    // check the connection the moment it comes back rather than waiting out
    // the watchdog in front of the user.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!socket || socket.readyState > 1) { clearTimeout(retryTimer); connect(true); }
      else armWatchdog();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(retryTimer);
      clearTimeout(watchdog);
      wsRef.current = null;
      try { socket?.close(); } catch { /* already closed */ }
    };
  }, [breakId]);

  const updateSpot = (idx, field, value) => {
    if (lockedByCompletion) return;
    dirtySpots.current.add(idx);
    setSpots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  // Build the server payload for a single spot row. Shared by the debounced
  // auto-save and by flows that must persist specific rows on demand (e.g. a
  // trade-in, whose empty target spot may never have been auto-saved).
  const buildSpotUpdate = (i) => {
    const row = spotRows[i];
    if (!row) return null;
    return {
      sort_order: i,
      team: isDivision ? row.division : isMulti ? [row.nfl, row.nba, row.mlb].filter(Boolean).join('|') : (row.nfl || row.nba || row.mlb),
      sport: isDivision ? 'Multi' : isMulti ? 'Multi' : (category === 'Football' ? 'NFL' : category === 'Basketball' ? 'NBA' : 'MLB'),
      customer: spots[i]?.customer || '',
      price: spots[i]?.price === '' || spots[i]?.price == null ? null : Math.round(Number(spots[i].price)),
      hit: spots[i]?.hit || '',
      freebie: spots[i]?.freebie || ''
    };
  };

  // Reveal one spot. A hit comes back as the Card(s) text and is written into
  // this tab straight away — even over an unsaved edit to the same row, or
  // the next auto-save would send the old empty Card(s) back. A skunk writes
  // nothing, just a brief toast.
  const revealSpot = async (idx) => {
    if (!revealUnlocked) { setToast(`🔒 ${REVEAL_LOCKED_MSG}`); return; }
    if (revealing != null || !canReveal) return;
    const u = buildSpotUpdate(idx);
    if (!u) return;
    setRevealing(idx);
    try {
      const { data } = await api.post(`/api/breaks/${breakId}/spots/${idx}/reveal`, { team: u.team, sport: u.sport });
      setCaseInfo(prev => ({ ...prev, reveals: data.reveals || [...prev.reveals, idx] }));
      if (data.skunk) {
        setToast(`Skunk · ${u.team.split('|').join(' / ')}`);
      } else {
        remoteUpdate.current = true;
        setSpots(prev => prev.map((s, i) => i === idx ? { ...s, hit: data.hit || '' } : s));
      }
      if (data.timing) setTimingPatch(data.timing);
    } catch (err) {
      alert(err.response?.data?.error || 'Reveal failed');
    } finally {
      setRevealing(null);
    }
  };

  // Reveal All: every spot still under its Reveal button at once. The hits
  // come back as Card(s) text and are written into this tab straight away
  // (only on those spots, so an unsaved edit elsewhere isn't overwritten);
  // a toast sums up what came out.
  const [revealingAll, setRevealingAll] = useState(false);
  const revealAll = async () => {
    if (!revealUnlocked) { setToast(`🔒 ${REVEAL_LOCKED_MSG}`); return; }
    if (revealingAll || revealing != null || !canReveal) return;
    const pendingIdx = new Set(spotRows.map((_, i) => i).filter(i => isRevealPending(i)));
    const board = [...pendingIdx].map(i => buildSpotUpdate(i)).filter(Boolean)
      .map(u => ({ sort_order: u.sort_order, team: u.team, sport: u.sport }));
    setRevealingAll(true);
    try {
      const { data } = await api.post(`/api/breaks/${breakId}/reveal-all`, { spots: board });
      setCaseInfo(prev => ({ ...prev, reveals: data.reveals || prev.reveals }));
      const hitBySortOrder = new Map((data.spots || []).map(r => [r.sort_order, r.hit || '']));
      remoteUpdate.current = true;
      setSpots(prev => prev.map((s, i) => pendingIdx.has(i) && hitBySortOrder.has(i) ? { ...s, hit: hitBySortOrder.get(i) } : s));
      if (data.timing) setTimingPatch(data.timing);
      if (data.hits + data.skunks > 0) {
        setToast(`${data.hits} hit${data.hits === 1 ? '' : 's'} · ${data.skunks} skunk${data.skunks === 1 ? '' : 's'}`);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Reveal All failed');
    } finally {
      setRevealingAll(false);
    }
  };
  // Reveal All until every spot is showing, then Hide All.
  const allRevealed = spotRows.length > 0 && spotRows.every((_, i) => !isRevealPending(i));

  // The Card(s) cell: until the spot is revealed, the usual text box stays
  // in place — blank and read-only, so the column keeps its normal width —
  // with a small Reveal button laid over its left end. Before the board fills
  // the button is locked: greyed, with the reason on hover, and a click
  // explains it rather than doing nothing (so it isn't `disabled`, which
  // would swallow the click). `compact` is the phone card's larger tap size.
  const cardsCell = (idx, input, compact = false) => {
    if (!isRevealPending(idx)) return input;
    const placeholder = React.cloneElement(input, {
      value: '', readOnly: true, tabIndex: -1, onChange: undefined, placeholder: '', 'aria-hidden': true,
    });
    const locked = !revealUnlocked;
    const busy = revealing === idx;
    const btnStyle = {
      position: 'absolute', left: 3, top: '50%', transform: 'translateY(-50%)',
      padding: compact ? '4px 12px' : '1px 8px', fontSize: compact ? 13 : 11, lineHeight: 1.4,
      borderRadius: 4, fontWeight: 700, whiteSpace: 'nowrap',
      ...(locked
        ? { background: '#edf2f7', color: '#718096', border: '1px dashed #a0aec0', cursor: 'not-allowed' }
        : { background: '#3182ce', color: '#fff', border: '1px solid #3182ce', cursor: canReveal ? 'pointer' : 'default',
            opacity: !canReveal || (revealing != null && !busy) ? 0.55 : 1 }),
    };
    return (
      <div style={{ position: 'relative' }}>
        {placeholder}
        {locked ? (
          <button type="button" onClick={() => revealSpot(idx)} title={REVEAL_LOCKED_MSG} aria-disabled="true"
            aria-label={`Reveal — ${REVEAL_LOCKED_MSG.toLowerCase()}`} style={btnStyle}>
            &#128274; Reveal
          </button>
        ) : (
          <button type="button" onClick={() => revealSpot(idx)} disabled={!canReveal || revealing != null} style={btnStyle}>
            {busy ? 'Revealing…' : 'Reveal'}
          </button>
        )}
      </div>
    );
  };

  // Auto-save with 500ms debounce. Sends ONLY the rows the user has touched
  // since the last successful save (`dirtySpots`). Untouched rows are not in
  // the payload, so a stale local view of another spot can no longer wipe a
  // change made elsewhere — the server UPSERTs by (break_id, team, sport) and
  // leaves the rest of the table alone.
  useEffect(() => {
    if (!loaded.current || spots.length === 0) return;
    if (remoteUpdate.current) { remoteUpdate.current = false; return; }
    if (lockedByCompletion) return;
    if (dirtySpots.current.size === 0) return;
    pendingSave.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const saveId = Date.now();
      lastSaveId.current = saveId;
      // Snapshot + clear the dirty set so new edits during the in-flight save
      // are tracked for the NEXT save instead of being cleared when this one
      // returns. On failure we merge the snapshot back in.
      const snapshot = new Set(dirtySpots.current);
      dirtySpots.current = new Set();
      setSaveStatus('Saving...');
      try {
        const updates = [];
        for (const i of snapshot) {
          const u = buildSpotUpdate(i);
          if (u) updates.push(u);
        }
        const { data } = await api.put(`/api/breaks/${breakId}/spots`, { updates, saveId });
        // Server walks the trade lineage when computing the high-bid spot, so
        // we can't replicate it from local state alone. Pull the fresh answer
        // off the save response so the High Bid customer label catches up.
        if (data && Object.prototype.hasOwnProperty.call(data, 'high_bid_spot_id')) {
          const hbSpot = (data.spots || []).find(s => s.id === data.high_bid_spot_id);
          setHighBidSortOrder(hbSpot ? hbSpot.sort_order : null);
          setHighBid(data.high_bid ?? null);
        }
        // Server may have auto-promoted the break (planned → live, live → pending).
        if (data?.status && data.status !== breakStatus && typeof onStatusChange === 'function') {
          onStatusChange(data.status);
        }
        // This save may have moved the clock: filling the first spot starts the
        // break, filling the last ends the fill, and the first card after that
        // starts the rip. Take the stamps straight off the response so the
        // clock updates in place instead of waiting for the break to reopen.
        if (data?.timing) setTimingPatch(data.timing);
        setSaveStatus('Saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch (err) {
        console.error('Save spots error:', err);
        setSaveStatus('Save failed');
        // Re-queue so a future edit triggers a retry covering these rows.
        for (const i of snapshot) dirtySpots.current.add(i);
      }
      pendingSave.current = false;
    }, 500);
  }, [spots]);

  const totalSpots = spotRows.length;
  const teamsSold = spots.filter(s => s.customer?.trim()).length;
  const teamsRemaining = totalSpots - teamsSold;
  const targetPPS = totalSpots > 0 && targetPrice > 0 ? targetPrice / totalSpots : 0;
  const filledPrices = spots.filter(s => s.price !== '' && s.price != null).map(s => parseFloat(s.price) || 0);
  const actualPPS = filledPrices.length > 0 ? filledPrices.reduce((a, b) => a + b, 0) / filledPrices.length : 0;
  const actualPrice = filledPrices.reduce((a, b) => a + b, 0);

  const thStyle = { padding: '10px 8px', borderBottom: '2px solid var(--border)', textAlign: 'left', fontWeight: 700, fontSize: computedFontSize };
  const tdStyle = { padding: computedTdPad, borderBottom: '1px solid #eee', fontSize: computedFontSize };
  const inputStyle = { width: '100%', padding: computedInputPad, border: '1px solid var(--border)', borderRadius: '4px', fontSize: computedFontSize };

  // Phone layout for the spots: one card per spot instead of a seven-column
  // spreadsheet that has to be scrolled sideways. Same fields, same handlers
  // and the same trade / trade-in rules as the table rows below. Inputs are
  // 16px so iOS doesn't zoom the page on focus.
  const renderMobileSpots = () => {
    const mInput = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 16, minHeight: 40, background: 'white' };
    const leagueTag = { display: 'inline-block', minWidth: 30, fontSize: 10, fontWeight: 700, color: 'var(--text-light)', letterSpacing: 0.3 };
    const checkLabel = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0, padding: '2px 0' };
    const parseComponents = (spot) => {
      if (!spot?.price_components) return null;
      try {
        const arr = typeof spot.price_components === 'string' ? JSON.parse(spot.price_components) : spot.price_components;
        return Array.isArray(arr) && arr.length > 0 ? arr : null;
      } catch { return null; }
    };

    return spotRows.map((row, idx) => {
      const spot = spots[idx];
      const hasHit = hitShown(idx);
      const hasCustomer = !!spot?.customer?.trim();
      const rowCust = (spot?.customer || '').trim().toLowerCase();
      const isTradeInSourceCandidate = !isDivision && tradeInMode && !tradeInTargetMode && rowCust !== '' && (!tradeInLockedCustomer || rowCust === tradeInLockedCustomer);
      const tradeInGreyed = !isDivision && tradeInMode && !tradeInTargetMode && !isTradeInSourceCandidate && !tradeInSelection.includes(idx);
      const isEmptyTarget = !hasCustomer && (spot?.price == null || spot?.price === '');
      const isFallBackTarget = tradeInSelection.includes(idx);
      const targetPickable = !isDivision && tradeInTargetMode && (isEmptyTarget || isFallBackTarget);
      const components = isDivision ? null : parseComponents(spot);
      const componentsTitle = components
        ? components.map(c => `${c.team}: $${Math.round(Number(c.price) || 0)}`).join(' / ')
        : undefined;
      const info = customerIconFor(spot?.customer);
      const isHighBid = highBidSortOrder === idx;

      let teams;
      if (isDivision) {
        const leagues = [['NFL', 'nfl'], ['NBA', 'nba'], ['MLB', 'mlb']];
        teams = (
          <>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{row.division}</div>
            {leagues.map(([tag, key]) => {
              const names = row.multiRows.map(mr => mr[key]).filter(Boolean);
              if (names.length === 0) return null;
              return (
                <div key={key} style={{ fontSize: 12, lineHeight: 1.45, display: 'flex', gap: 4 }}>
                  <span style={leagueTag}>{tag}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>{names.join(' · ')}</span>
                </div>
              );
            })}
          </>
        );
      } else if (isMulti) {
        teams = [['NFL', row.nfl], ['NBA', row.nba], ['MLB', row.mlb]].filter(([, t]) => t).map(([tag, t]) => (
          <div key={tag} style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={leagueTag}>{tag}</span>
            <span style={{ flex: 1, minWidth: 0 }}>{t}</span>
          </div>
        ));
      } else {
        teams = <div style={{ fontSize: 15, fontWeight: 700 }}>{row.nfl || row.nba || row.mlb}</div>;
      }

      return (
        <div key={idx} data-spot-card={idx} style={{
          background: hasHit ? '#e8f5e9' : hasCustomer ? '#e3f2fd' : 'white',
          border: '1px solid var(--border)',
          borderLeft: `4px solid ${hasHit ? '#38a169' : hasCustomer ? '#3182ce' : 'var(--border)'}`,
          borderRadius: 10, padding: 10, marginBottom: 8,
          opacity: tradeInGreyed || (tradeInTargetMode && !targetPickable) ? 0.4 : 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>{teams}</div>
            {isHighBid && hasCustomer && (
              <span className="badge badge-warning" style={{ flexShrink: 0 }}>High Bid</span>
            )}
            {hasHit && <span className="badge badge-success" style={{ flexShrink: 0 }}>&#10003; Hit</span>}
            {tradeMode && (
              <label style={checkLabel}>
                <input type="checkbox" checked={tradeSelection.includes(idx)}
                  onChange={() => toggleTradeCheck(idx)} style={{ margin: 0, width: 20, height: 20 }} />
                Trade
              </label>
            )}
            {tradeInMode && !tradeInTargetMode && isTradeInSourceCandidate && (
              <label style={checkLabel}>
                <input type="checkbox" checked={tradeInSelection.includes(idx)}
                  onChange={() => toggleTradeInCheck(idx)} style={{ margin: 0, width: 20, height: 20 }} />
                Trade In
              </label>
            )}
          </div>
          {targetPickable ? (
            <button
              onClick={() => pickTradeInTarget(idx)}
              disabled={executingTradeIn}
              style={{
                width: '100%', padding: '10px 8px', borderRadius: 6,
                background: '#16a34a', color: 'white', border: 'none',
                fontWeight: 700, fontSize: 15, cursor: 'pointer',
                opacity: executingTradeIn ? 0.6 : 1,
              }}
            >{isFallBackTarget ? '＋ Keep this team' : '＋ Award this team'}</button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <SpotCustomerIcon state={info.state} buyer={info.buyer}
                canOpen={canOpenCustomerSummary} onOpen={setSummaryBuyer} />
              <input type="text" value={spot?.customer || ''} placeholder="Customer"
                aria-label="Customer"
                onChange={e => updateSpot(idx, 'customer', e.target.value)}
                readOnly={lockedByCompletion || tradeInMode}
                autoCapitalize="none" autoCorrect="off" spellCheck={false}
                style={isHighBid
                  ? { ...mInput, flex: 1, minWidth: 0, border: '2px solid var(--warning)' }
                  : { ...mInput, flex: 1, minWidth: 0 }} />
              <input type="text" inputMode="numeric" placeholder="$" aria-label="Price"
                value={spot?.price !== '' && spot?.price != null ? `$${Math.round(Number(spot.price))}` : ''}
                onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); updateSpot(idx, 'price', v === '' ? '' : v); }}
                readOnly={lockedByCompletion || tradeInMode}
                style={{ ...mInput, width: 84, flex: '0 0 84px', textAlign: 'right' }} />
              {components && (
                <span title={componentsTitle} onClick={() => alert(componentsTitle)}
                  style={{ color: 'var(--warning, #d69e2e)', fontWeight: 700, cursor: 'help', padding: '0 2px' }}>*</span>
              )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
            {cardsCell(idx, (
              <input type="text" value={spot?.hit || ''} placeholder="Card(s)" aria-label="Card(s)"
                onChange={e => updateSpot(idx, 'hit', e.target.value)}
                readOnly={lockedByCompletion}
                style={mInput} />
            ), true)}
            <input type="text" value={spot?.freebie || ''} placeholder="Freebie(s)" aria-label="Freebie(s)"
              onChange={e => updateSpot(idx, 'freebie', e.target.value)}
              readOnly={lockedByCompletion}
              style={mInput} />
          </div>
        </div>
      );
    });
  };

  // The case controls: linked case chips (case number + card count only), the
  // no-case warning, Link / Add Case, Reveal All / Hide All, and any cards that can't land
  // on a spot. Desktop shows them compactly at the end of the header actions
  // (warnings shortened, full text on hover); phones in their own header line.
  const renderCaseControls = (compact) => {
    const unmatchedTitle = caseInfo.unmatched.map(c => `${c.name}${c.team ? ` (${c.team})` : ''}`).join('\n');
    const unmatchedCount = caseInfo.unmatched.length;
    return (
      <>
        {caseRequired && (compact ? (
          <span title="A case must be linked to this break."
            style={{ padding: '2px 8px', borderRadius: 12, background: '#fffbeb', border: '1px solid #f59e0b', color: '#92400e', fontWeight: 600, whiteSpace: 'nowrap' }}>
            &#9888; No case linked
          </span>
        ) : (
          <span style={{ color: '#92400e', fontWeight: 600 }}>&#9888; A case must be linked to this break.</span>
        ))}
        {unmatchedCount > 0 && (
          <span style={{ color: 'var(--warning, #d69e2e)', whiteSpace: compact ? 'nowrap' : undefined }}
            title={compact ? `${unmatchedCount} card${unmatchedCount === 1 ? '' : 's'} won't match any spot on this break:\n${unmatchedTitle}` : unmatchedTitle}>
            &#9888; {compact
              ? `${unmatchedCount} unmatched`
              : `${unmatchedCount} card${unmatchedCount === 1 ? '' : 's'} won't match any spot on this break`}
          </span>
        )}
        {canManageCases && (
          <button className={`btn btn-sm ${caseRequired ? 'btn-primary' : 'btn-secondary'}`} onClick={openCasePicker} disabled={caseBusy}
            style={{ whiteSpace: 'nowrap' }}>
            {caseInfo.cases.length > 0 ? '+ Add Case' : 'Link Case'}
          </button>
        )}
        {revealMode && canReveal && (allRevealed ? (
          <HoverTip text="Re-hide all hits so they're covered with Reveal." width={220}>
            <button className="btn btn-sm btn-secondary" onClick={hideHits} disabled={hidingHits}
              style={{ whiteSpace: 'nowrap' }}>
              {hidingHits ? 'Hiding…' : 'Hide All'}
            </button>
          </HoverTip>
        ) : !revealUnlocked ? (
          // Locked like the spots' own Reveal buttons: a click explains why.
          <button className="btn btn-sm btn-secondary" onClick={revealAll} title={REVEAL_LOCKED_MSG} aria-disabled="true"
            style={{ whiteSpace: 'nowrap', opacity: 0.6, cursor: 'not-allowed' }}>
            &#128274; Reveal All
          </button>
        ) : (
          <HoverTip text="Reveal every spot still under its Reveal button." width={220}>
            <button className="btn btn-sm btn-primary" onClick={revealAll} disabled={revealingAll || revealing != null}
              style={{ whiteSpace: 'nowrap' }}>
              {revealingAll ? 'Revealing…' : 'Reveal All'}
            </button>
          </HoverTip>
        ))}
        {/* The linked case(s) last, right before the close button. Unlinking
            lives in the Add Case picker. */}
        {caseInfo.cases.map(c => (
          <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 12, background: '#ede9fe', color: '#5b21b6', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {c.case_number} &middot; {c.card_count} card{c.card_count === 1 ? '' : 's'}
          </span>
        ))}
      </>
    );
  };

  return (
    <div className="spot-sheet" style={{ position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      {/* On a phone the header stacks: title row (with close) on top, then a
          single swipeable strip of actions instead of a wall of wrapped buttons. */}
      <div ref={headerRef} style={isMobile
        ? { padding: '8px 12px', borderBottom: '1px solid var(--border)', background: 'white', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }
        : { padding: headerPadding, borderBottom: '1px solid var(--border)', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div style={isMobile ? { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 } : { display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          {editingTitle ? (
            <div style={isMobile ? { display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 } : { display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ margin: 0, ...(isMobile ? { fontSize: 16 } : {}) }}>#{breakNumber}</h3>
              <input type="text" value={editTitleValue} onChange={e => setEditTitleValue(e.target.value)}
                style={{ fontSize: '16px', fontWeight: 600, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: '4px', ...(isMobile ? { flex: 1, minWidth: 0 } : {}) }}
                autoFocus onKeyDown={e => {
                  if (e.key === 'Enter') { api.put(`/api/breaks/${breakId}`, { name: editTitleValue }).then(() => { if (onNameChange) onNameChange(editTitleValue); setEditingTitle(false); }); }
                  if (e.key === 'Escape') setEditingTitle(false);
                }} />
              <button className="btn btn-primary btn-sm" onClick={() => { api.put(`/api/breaks/${breakId}`, { name: editTitleValue }).then(() => { if (onNameChange) onNameChange(editTitleValue); setEditingTitle(false); }); }}>Save</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingTitle(false)}>Cancel</button>
            </div>
          ) : isMobile ? (
            <h3 style={{ margin: 0, fontSize: 16, lineHeight: 1.25, display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>#{breakNumber} {breakName}</span>
              {!lockedByCompletion && (
                <button onClick={() => { setEditTitleValue(breakName); setEditingTitle(true); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--text-light)', padding: '4px', flexShrink: 0 }}
                  title="Edit break name">&#9998;</button>
              )}
            </h3>
          ) : (
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              #{breakNumber} {breakName} - Break Spots
              {!lockedByCompletion && (
                <button onClick={() => { setEditTitleValue(breakName); setEditingTitle(true); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--text-light)', padding: '2px' }}
                  title="Edit break name">&#9998;</button>
              )}
            </h3>
          )}
          <span className="badge badge-info" style={isMobile ? { flexShrink: 0, whiteSpace: 'nowrap' } : undefined}>{category}</span>
          {isMobile && (
            <button onClick={onClose} aria-label="Close"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '26px', lineHeight: 1, padding: '4px 6px', color: 'var(--text-secondary)', flexShrink: 0, minWidth: 40, minHeight: 40 }}>&times;</button>
          )}
        </div>
        <div className={isMobile ? 'spot-sheet-actions' : undefined} style={isMobile
          ? { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto', margin: '0 -12px', padding: '0 12px 2px', WebkitOverflowScrolling: 'touch' }
          : { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {!isMobile && saveStatus && <span style={{ fontSize: '12px', color: saveStatus === 'Save failed' ? 'var(--danger)' : 'var(--text-light)' }}>{saveStatus}</span>}
          {/* End Break. Only once the grid has filled — before that there is no
              rip to end, and the button would just be a way to mis-stamp the
              timeline. The clock itself lives in the stats row by the score. */}
          {breakIsRunning && (
            <HoverTip
              text="Stamps Rip Ends / Break Ends for this break, right now. Press it once the recap is done — it's what makes rip time and dead time measurable."
              width={300}
            >
              <button className="btn btn-sm" onClick={() => endBreak('button')} disabled={endingBreak}
                style={{ background: '#dd6b20', borderColor: '#dd6b20', color: '#fff', opacity: endingBreak ? 0.6 : 1 }}>
                {endingBreak ? 'Ending…' : 'End Break'}
              </button>
            </HoverTip>
          )}
          {/* Jumps to the break above this one in the breaks table and streams
              it to the Auction Grid — one click between back-to-back breaks. */}
          <HoverTip
            text={nextBreak
              ? `Ends this break, then opens Break Spots for #${nextBreak.break_number || '--'} ${nextBreak.name || ''} and streams it to the Team Grid. Asks to confirm first.`
              : 'No planned or live break sits above this one.'}
            width={300}
          >
            <button
              className="btn btn-sm"
              onClick={() => setConfirmNextBreak(true)}
              disabled={!nextBreak || startingNext}
              style={{
                background: '#805ad5', borderColor: '#805ad5', color: '#fff',
                opacity: (!nextBreak || startingNext) ? 0.5 : 1,
                cursor: (!nextBreak || startingNext) ? 'not-allowed' : 'pointer',
              }}
            >
              {startingNext ? 'Starting…' : (isMobile ? 'Next Break' : 'Start Next Break')}
            </button>
          </HoverTip>
          <button
            className={`btn btn-sm ${isStreaming ? 'btn-danger' : ''}`}
            onClick={toggleStreaming}
            style={isStreaming ? { background: '#e53e3e', borderColor: '#e53e3e', color: '#fff' } : { background: '#38a169', borderColor: '#38a169', color: '#fff' }}
          >
            {isMobile
              ? (isStreaming ? 'Stop Stream' : 'Stream to Grid')
              : (isStreaming ? 'Stop Streaming' : 'Stream to Team Grid')}
          </button>
          <button
            className={`btn btn-sm ${showHighBidOverlay ? 'btn-primary' : 'btn-secondary'}`}
            onClick={toggleHighBidOverlay}
          >
            {isMobile
              ? (showHighBidOverlay ? 'High Bid: On' : 'High Bid: Off')
              : (showHighBidOverlay ? 'Hide High Bid' : 'Show High Bid')}
          </button>
          {tradeMode ? (
            <>
              <button className="btn btn-sm" onClick={applyTrade} disabled={tradeSelection.length < 2}
                style={{ background: '#38a169', borderColor: '#38a169', color: '#fff', opacity: tradeSelection.length < 2 ? 0.5 : 1 }}>
                Apply Trade{tradeSelection.length > 2 ? ` (${tradeSelection.length} spots)` : ''}
              </button>
              <button className="btn btn-sm" onClick={cancelTrade}
                style={{ background: '#e53e3e', borderColor: '#e53e3e', color: '#fff' }}>
                Cancel Trade
              </button>
            </>
          ) : !lockedByCompletion ? (
            <HoverTip text="Apply trades between two customers" width={220}>
              <button className="btn btn-sm btn-secondary" onClick={() => setTradeMode(true)}>Trade</button>
            </HoverTip>
          ) : null}
          {tradeInMode ? (
            <button className="btn btn-sm" onClick={cancelTradeIn}
              style={{ background: '#e53e3e', borderColor: '#e53e3e', color: '#fff' }}>
              {tradeInTargetMode ? 'Cancel Pick Target' : 'Cancel Trade In'}
            </button>
          ) : (!lockedByCompletion && spotType === 'Team' && !tradeMode) ? (
            <HoverTip text="Apply a trade in for a customer who is trading in two teams to receive one new random team. Can only be used when using the cards to randomize teams." width={320}>
              <button className="btn btn-sm btn-secondary" onClick={() => setTradeInMode(true)}>Trade In</button>
            </HoverTip>
          ) : null}
          {canPostNotifications && !lockedByCompletion && (
            <button className="btn btn-sm btn-secondary" onClick={() => setShowPostNotifModal(true)}>
              Post Note
            </button>
          )}
          {canManageProducts && (
            <div ref={addPanelRef} style={{ position: 'relative' }}>
              <button ref={addBtnRef} className={`btn btn-sm ${showAddProduct ? 'btn-primary' : 'btn-secondary'}`}
                title="Add a product to this break's Product Additions"
                onClick={() => (showAddProduct ? setShowAddProduct(false) : openAddProduct())}>
                + Add Product
              </button>
              {showAddProduct && (
                <div style={{ position: 'fixed', top: addAnchor?.top ?? 0, right: addAnchor?.right ?? 12,
                  width: addAnchor?.width ?? 360, visibility: addAnchor ? 'visible' : 'hidden', background: 'white',
                  border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-lg)', zIndex: 1600, padding: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Add to Product Additions</div>
                  <input
                    ref={addInputRef}
                    autoFocus
                    type="text"
                    value={addQuery}
                    placeholder="Search products, then press Enter…"
                    onChange={e => { setAddQuery(e.target.value); searchAddProducts(e.target.value); }}
                    onKeyDown={e => {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setAddHighlight(i => Math.min(i + 1, addResults.length - 1)); }
                      else if (e.key === 'ArrowUp') { e.preventDefault(); setAddHighlight(i => Math.max(i - 1, 0)); }
                      else if (e.key === 'Enter') { e.preventDefault(); addProductAddition(addResults[addHighlight]); }
                      else if (e.key === 'Escape') { e.preventDefault(); setShowAddProduct(false); }
                    }}
                    style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6 }}
                  />
                  {addResults.length > 0 && (
                    <div style={{ marginTop: 6, maxHeight: 190, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                      {addResults.map((p, i) => (
                        <div key={p.product_id}
                          onMouseEnter={() => setAddHighlight(i)}
                          onClick={() => addProductAddition(p)}
                          style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 12,
                            borderBottom: i === addResults.length - 1 ? 'none' : '1px solid var(--border)',
                            background: i === addHighlight ? 'var(--bg)' : 'white' }}>
                          <div style={{ fontWeight: 500 }}>{p.product_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
                            {p.sku} &middot; COGS: {p.cogs != null ? formatCurrency(p.cogs) : '--'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {addQuery.trim() && addResults.length === 0 && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-light)' }}>No matching products.</div>
                  )}
                  {addFlash && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--success, #22c55e)', fontWeight: 600 }}>&#10003; {addFlash}</div>}
                  {addError && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)' }}>{addError}</div>}

                  <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      On this break
                    </div>
                    {additions.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--text-light)' }}>Nothing added yet.</div>
                    ) : (
                      <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                        {additions.map(row => (
                          <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 12 }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getItemName(row)}</span>
                            <span style={{ color: 'var(--text-light)', minWidth: 24, textAlign: 'right' }}>&times;{row.quantity}</span>
                            <button className="btn btn-secondary btn-sm" disabled={addBusy} title="Remove one"
                              onClick={() => stepAddition(row, -1)} style={{ padding: '0 7px', lineHeight: '20px' }}>&minus;</button>
                            <button className="btn btn-secondary btn-sm" disabled={addBusy} title="Add another"
                              onClick={() => stepAddition(row, 1)} style={{ padding: '0 7px', lineHeight: '20px' }}>+</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* The case — after Post Note and Add Product, beside the close
              button. One unit with the ×, so on a narrower screen they wrap
              to the next line together instead of stranding the ×. */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
              {showCaseBar && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 8, borderLeft: '1px solid var(--border)', fontSize: 13 }}>
                  {renderCaseControls(true)}
                </div>
              )}
              <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', lineHeight: 1, padding: '4px 8px', color: 'var(--text-secondary)' }}>&times;</button>
            </div>
          )}
        </div>
        {/* Phones get the case on its own header line — their action row
            scrolls sideways and would hide the warning. Desktop has it at the
            end of the actions. */}
        {isMobile && showCaseBar && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13,
            padding: caseRequired ? '8px 10px' : 0, borderRadius: 8,
            background: caseRequired ? '#fffbeb' : 'transparent',
            border: caseRequired ? '1px solid #f59e0b' : 'none',
          }}>
            <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Case:</span>
            {renderCaseControls(false)}
          </div>
        )}
      </div>

      {/* Locked banner — visible to everyone when a non-admin opens a completed break */}
      {lockedByCompletion && (
        <div style={{ padding: '8px 20px', background: '#fffbeb', borderBottom: '1px solid #f59e0b', color: '#92400e', fontSize: 13 }}>
          This break is completed — read-only. Only an admin can edit.
        </div>
      )}
      {/* Phone stats toggle: the headline numbers on one line, tap for the rest. */}
      {isMobile && (() => {
        const isFinalized = breakStatus === 'completed' || breakStatus === 'pending';
        const live = computeBreakScore({ totalSpots, soldPrices: filledPrices, cogs: breakCogs });
        const scoreVal = isFinalized && finalScore != null ? finalScore : (live?.score ?? null);
        const clockLive = liveTiming?.break_started_at && !(liveTiming.break_ended_at || breakIsFinished);
        const item = { display: 'flex', alignItems: 'baseline', gap: 4, whiteSpace: 'nowrap' };
        const lbl = { fontSize: 11, color: 'var(--text-light)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 };
        return (
          <button type="button" onClick={() => setMobileStatsOpen(v => !v)} aria-expanded={mobileStatsOpen}
            style={{
              display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '10px 12px',
              background: 'white', border: 'none', borderBottom: '1px solid var(--border)',
              cursor: 'pointer', flexShrink: 0, textAlign: 'left', color: 'var(--text)', minHeight: 44,
            }}>
            <span style={item}><span style={lbl}>Sold</span><strong style={{ fontSize: 15 }}>{teamsSold}/{totalSpots}</strong></span>
            <span style={item}>
              <span style={lbl}>Score</span>
              <strong style={{ fontSize: 15, color: scoreVal != null ? scoreColor(scoreVal) : 'var(--text-light)' }}>{scoreVal ?? '--'}</strong>
            </span>
            {liveTiming?.break_started_at && (
              <span style={item}>
                <span style={lbl}>{clockLive ? 'Clock' : 'Total'}</span>
                <strong style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums', color: clockLive ? '#dd6b20' : undefined }}>
                  {runningElapsed == null ? '--' : formatClock(runningElapsed)}
                </strong>
              </span>
            )}
            {saveStatus && (
              <span style={{ marginLeft: 'auto', fontSize: 11, whiteSpace: 'nowrap', color: saveStatus === 'Save failed' ? 'var(--danger)' : 'var(--text-light)' }}>{saveStatus}</span>
            )}
            <span style={{ marginLeft: saveStatus ? 0 : 'auto', fontSize: 12, color: 'var(--accent, #3182ce)', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {mobileStatsOpen ? 'Hide ▲' : 'Stats ▼'}
            </span>
          </button>
        );
      })()}
      {/* Stats */}
      {/* Desktop always renders this row: the caret on its left opens and
          closes the stats, and collapsed it is just the caret. */}
      {(statsVisible || !isMobile) && (
        <div ref={statsRef} className={isMobile ? 'spot-stats-mobile' : undefined} style={isMobile
          ? { padding: '12px', borderBottom: '1px solid var(--border)', background: 'white', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px', flexShrink: 0, maxHeight: '55vh', overflowY: 'auto' }
          : { padding: statsVisible ? statsPadding : '6px 20px', borderBottom: '1px solid var(--border)', background: 'white', display: 'flex', gap: '32px', flexWrap: 'wrap', flexShrink: 0, alignItems: statsVisible ? undefined : 'center' }}>
          {!isMobile && (
            <button type="button" onClick={() => setShowStats(v => !v)} aria-expanded={showStats}
              aria-label={showStats ? 'Hide stats' : 'Show stats'} title={showStats ? 'Hide stats' : 'Show stats'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', marginRight: -22,
                alignSelf: statsVisible ? 'flex-start' : 'center', fontSize: 14, lineHeight: '18px',
                color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: showStats ? 'rotate(90deg)' : 'none' }}>&#9654;</span>
              {!showStats && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-light)' }}>Stats</span>}
            </button>
          )}
          {statsVisible && (<>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', fontWeight: 600 }}>Spots Sold</div>
            <div style={{ fontSize: statsFontSize, fontWeight: 700 }}>{teamsSold}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{isDivision ? 'Divisions' : 'Teams'} Remaining: {teamsRemaining}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', fontWeight: 600 }}>Target PPS</div>
            <div style={{ fontSize: statsFontSize, fontWeight: 700 }}>{targetPPS > 0 ? formatCurrency(targetPPS) : '--'}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Actual PPS: {actualPPS > 0 ? formatCurrency(actualPPS) : '--'}</div>
          </div>
          {canSeeFinancials && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', fontWeight: 600 }}>Target Price</div>
              <div style={{ fontSize: statsFontSize, fontWeight: 700 }}>{formatCurrency(targetPrice)}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Actual Price: {formatCurrency(actualPrice)}</div>
            </div>
          )}
          {canSeeFinancials && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', fontWeight: 600 }}>{breakStatus === 'live' ? 'Projected Margin' : 'Margin'}</div>
              {(() => {
                const revenue = actualPPS * totalSpots;
                const margin = revenue > 0 ? ((revenue - breakCogs) / revenue) * 100 : null;
                return <>
                  <div style={{ fontSize: statsFontSize, fontWeight: 700, color: margin != null ? (margin >= 0 ? 'var(--success, #22c55e)' : 'var(--danger, #e53e3e)') : undefined }}>
                    {margin != null ? `${margin.toFixed(1)}%` : '--'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>COGS: {formatCurrency(breakCogs)}</div>
                </>;
              })()}
            </div>
          )}
          {/* High Bid — the same amount and customer the overlay shows */}
          {(() => {
            const highestPrice = highBid?.amount ?? null;
            const highBidCustomer = highBid?.customer || null;
            return (
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)', fontWeight: 600 }}>High Bid</div>
                <div style={{ fontSize: statsFontSize, fontWeight: 700 }}>
                  {highestPrice != null && highestPrice > 0 ? formatCurrency(highestPrice) : '--'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                  {highBidCustomer || '--'}
                </div>
              </div>
            );
          })()}
          {(() => {
            // Break enters "finalized" once it goes to pending (sign-off in
            // progress) or completed — final_score is persisted at that point.
            const isFinalized = breakStatus === 'completed' || breakStatus === 'pending';
            const live = computeBreakScore({
              totalSpots,
              soldPrices: filledPrices,
              cogs: breakCogs,
            });
            const scoreVal = isFinalized && finalScore != null ? finalScore : (live?.score ?? null);
            const label = scoreVal == null ? 'Score' : (isFinalized ? 'Final Score' : 'Projected Score');
            const scoreFontSize = `calc(${statsFontSize} * 1.7)`;
            // Spectrum bar: gradient + tick labels with the avg-price needed
            // to reach scores of 50 and 100. Wrapping with the ScoreCell on
            // the left keeps the hover-for-breakdown affordance.
            const priceFor50  = avgPriceForScore(50,  totalSpots, breakCogs);
            const priceFor100 = avgPriceForScore(100, totalSpots, breakCogs);
            const SPECTRUM = 'linear-gradient(to right, #dc2626 0%, #ea580c 20%, #ca8a04 40%, #16a34a 60%, #15803d 80%, #15803d 100%)';
            return (
              <div className="score-spectrum" style={{ minWidth: 360 }}>
                <div style={{ fontSize: '12px', color: 'var(--text-light)', fontWeight: 600, marginBottom: 6 }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 50 }}>
                    <ScoreCell
                      score={scoreVal}
                      label={label}
                      breakdown={live}
                      fontSize={scoreFontSize}
                      showBreakdown={canSeeFinancials}
                    />
                  </div>
                  <div className="score-spectrum-bar" style={{ width: 300, flex: '0 0 300px' }}>
                    <div style={{ position: 'relative', height: 7, borderRadius: 4, background: SPECTRUM }}>
                      {scoreVal != null && (
                        <div title={String(scoreVal)} style={{
                          position: 'absolute',
                          left: `${Math.max(0, Math.min(100, scoreVal))}%`,
                          top: -2,
                          width: 2, height: 11, background: '#111',
                          border: '1px solid #fff', borderRadius: 1,
                          transform: 'translateX(-50%)',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                        }} />
                      )}
                    </div>
                    <div style={{ position: 'relative', height: 30, marginTop: 4 }}>
                      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', fontSize: 10, lineHeight: 1.2 }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-secondary, #555)' }}>50</div>
                        {priceFor50 != null && (
                          <div style={{ color: 'var(--text-light)', whiteSpace: 'nowrap' }}>${Math.ceil(priceFor50).toLocaleString()}/spot</div>
                        )}
                      </div>
                      <div style={{ position: 'absolute', left: '100%', transform: 'translateX(-100%)', textAlign: 'right', fontSize: 10, lineHeight: 1.2 }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-secondary, #555)' }}>100</div>
                        {priceFor100 != null && (
                          <div style={{ color: 'var(--text-light)', whiteSpace: 'nowrap' }}>≥ ${Math.ceil(priceFor100).toLocaleString()}/spot</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Break clock. Sits beside the score so the breaker's eye is already
              on it. The headline counts from the first spot selling and stops
              the moment End Break is pressed, at which point it IS the break
              total. Each segment appears as soon as its start stamp exists and
              ticks live until its own end stamp lands. */}
          {liveTiming?.break_started_at && (() => {
            const fill = elapsedBetween(liveTiming.break_started_at, liveTiming.fill_ended_at);
            const fillToRip = elapsedBetween(liveTiming.fill_ended_at, panelRipStart);
            const rip = elapsedBetween(panelRipStart, liveTiming.break_ended_at);
            const ended = !!liveTiming.break_ended_at;
            const segs = [
              { label: 'Fill', value: fill, live: clockRunning && !liveTiming.fill_ended_at, info: 'First spot sold to last spot sold.' },
              { label: 'Fill → Rip', value: fillToRip, live: clockRunning && !!liveTiming.fill_ended_at && !panelRipStart,
                info: 'Last spot sold to the first card marked in Card(s). Dead time — sold out, nothing opened yet.' },
              { label: 'Rip', value: rip, live: clockRunning && !!panelRipStart,
                info: 'First card marked to End Break, recap included.' },
            ];
            // Two tiles, not one. The clock is a headline figure and belongs in
            // the stats row on the same footing as Spots Sold or High Bid; the
            // segment breakdown is a list and reads as its own column beside it.
            // Stacking them made the clock look like a heading for the list, and
            // the segment rows stretched to the tile's width, throwing their
            // labels and values to opposite edges.
            return (
              <React.Fragment>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)', fontWeight: 600 }}>
                    {ended || breakIsFinished ? 'Break Total' : 'Break Running'}
                    <InfoTip
                      text={'Counts from the first spot selling to End Break being pressed. | Once the break '
                        + 'is ended this is the break total. | Per-break, not per-LIVE.'}
                      label="Break clock" />
                  </div>
                  <div style={{
                    fontSize: statsFontSize, fontWeight: 700,
                    color: ended || breakIsFinished ? undefined : '#dd6b20',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {runningElapsed == null ? '--' : formatClock(runningElapsed)}
                  </div>
                </div>
                {/* Two auto-width grid tracks keep each value next to its own
                    label. A flex row with space-between stretched to the tile
                    and pushed them to opposite edges. */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'auto auto',
                  columnGap: 14, alignContent: 'start', fontSize: '13px', lineHeight: 1.6,
                }}>
                  {segs.map(s => (
                    <React.Fragment key={s.label}>
                      <span style={{ color: 'var(--text-light)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {s.label}<InfoTip text={s.info} label={s.label} />
                      </span>
                      <span style={{
                        textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: s.value == null
                          ? 'var(--text-light)'
                          : s.live ? '#dd6b20' : 'var(--text)',
                      }}>
                        {s.value == null ? '--' : formatClock(s.value)}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </React.Fragment>
            );
          })()}
          </>)}
        </div>
      )}

      {/* Spreadsheet */}
      <div
        style={isMobile
          ? { flex: 1, overflow: 'auto', padding: '10px 12px 88px', WebkitOverflowScrolling: 'touch' }
          : { flex: 1, overflow: 'auto', padding: '0 20px 20px' }}
        onMouseMove={handleSheetMouseMove}
        onMouseLeave={clearHover}
        onScroll={clearHover}
        onTouchStart={clearHover}
      >
        {isMobile ? renderMobileSpots() : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead ref={theadRef}>
            <tr style={{ position: 'sticky', top: 0, background: 'white', zIndex: 2 }}>
              <th style={{ ...thStyle, width: '220px' }}>Customer</th>
              <th style={{ ...thStyle, width: '70px' }}>Price</th>
              {tradeMode && <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>Trade</th>}
              {tradeInMode && !tradeInTargetMode && <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>Trade In</th>}
              {isDivision ? (
                <>
                  <th style={thStyle}>Division</th>
                  <th style={thStyle}>NFL</th>
                  <th style={thStyle}>NBA</th>
                  <th style={thStyle}>MLB</th>
                </>
              ) : isMulti ? (
                <>
                  <th style={thStyle}>NFL</th>
                  <th style={thStyle}>NBA</th>
                  <th style={thStyle}>MLB</th>
                </>
              ) : (
                <th style={thStyle}>
                  {category === 'Football' ? 'NFL' : category === 'Basketball' ? 'NBA' : 'MLB'}
                </th>
              )}
              <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>Hit</th>
              <th style={thStyle}>Card(s)</th>
              <th style={thStyle}>Freebie(s)</th>
            </tr>
          </thead>
          <tbody>
            {isDivision ? spotRows.map((row, idx) => {
              const rowSpan = row.multiRows.length;
              const bgColor = hitShown(idx) ? '#e8f5e9' : spots[idx]?.customer?.trim() ? '#e3f2fd' : 'white';
              const divBorderStyle = { borderTop: idx > 0 ? '2px solid var(--border)' : undefined };
              return row.multiRows.map((mr, subIdx) => (
                <tr key={`${idx}-${subIdx}`} data-spot-row={`${idx}-${subIdx}`}
                  className={'spot-row' + (hoverRow === `${idx}-${subIdx}` ? ' spot-row-hover' : '')}
                  style={{ background: bgColor, ...(subIdx === 0 ? divBorderStyle : {}) }}>
                  {subIdx === 0 && (
                    <>
                      <td style={tdStyle} rowSpan={rowSpan}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {(() => {
                            const info = customerIconFor(spots[idx]?.customer);
                            return <SpotCustomerIcon state={info.state} buyer={info.buyer}
                              canOpen={canOpenCustomerSummary} onOpen={setSummaryBuyer} />;
                          })()}
                          <input type="text" value={spots[idx]?.customer || ''} onChange={e => updateSpot(idx, 'customer', e.target.value)}
                            readOnly={lockedByCompletion}
                            title={highBidSortOrder === idx ? 'High bid' : undefined}
                            style={highBidSortOrder === idx
                              ? { ...inputStyle, flex: 1, minWidth: 0, border: '2px solid var(--warning)', borderRadius: 4 }
                              : { ...inputStyle, flex: 1, minWidth: 0 }} />
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }} rowSpan={rowSpan}>
                        <input type="text" inputMode="numeric"
                          value={spots[idx]?.price !== '' && spots[idx]?.price != null ? `$${Math.round(Number(spots[idx].price))}` : ''}
                          onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); updateSpot(idx, 'price', v === '' ? '' : v); }}
                          readOnly={lockedByCompletion}
                          style={{ ...inputStyle, textAlign: 'right' }} />
                      </td>
                      {tradeMode && (
                        <td style={{ ...tdStyle, textAlign: 'center' }} rowSpan={rowSpan}>
                          <input type="checkbox" checked={tradeSelection.includes(idx)}
                            onChange={() => toggleTradeCheck(idx)} style={{ margin: 0 }} />
                        </td>
                      )}
                      <td style={{ ...tdStyle, fontWeight: 700 }} rowSpan={rowSpan}>{row.division}</td>
                    </>
                  )}
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{mr.nfl}</td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{mr.nba}</td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{mr.mlb}</td>
                  {subIdx === 0 && (
                    <>
                      <td style={{ ...tdStyle, textAlign: 'center' }} rowSpan={rowSpan}>
                        <input type="checkbox" checked={hitShown(idx)} readOnly style={{ pointerEvents: 'none', margin: 0 }} />
                      </td>
                      <td style={tdStyle} rowSpan={rowSpan}>
                        {cardsCell(idx, (
                          <input type="text" value={spots[idx]?.hit || ''} onChange={e => updateSpot(idx, 'hit', e.target.value)}
                            readOnly={lockedByCompletion}
                            style={inputStyle} placeholder="" />
                        ))}
                      </td>
                      <td style={tdStyle} rowSpan={rowSpan}>
                        <input type="text" value={spots[idx]?.freebie || ''} onChange={e => updateSpot(idx, 'freebie', e.target.value)}
                          readOnly={lockedByCompletion}
                          style={inputStyle} placeholder="" />
                      </td>
                    </>
                  )}
                </tr>
              ));
            }) : spotRows.map((row, idx) => {
              const spot = spots[idx];
              const rowCust = (spot?.customer || '').trim().toLowerCase();
              const isTradeInSourceCandidate = tradeInMode && !tradeInTargetMode && rowCust !== '' && (!tradeInLockedCustomer || rowCust === tradeInLockedCustomer);
              const tradeInGreyed = tradeInMode && !tradeInTargetMode && !isTradeInSourceCandidate && !tradeInSelection.includes(idx);
              const isEmptyTarget = (!spot?.customer || !(spot?.customer || '').trim()) && (spot?.price == null || spot?.price === '');
              // A customer who draws one of their own surrendered cards back
              // keeps that team, so the two spots being traded in are valid
              // targets too — they're cleared and rewritten as one combined row.
              const isFallBackTarget = tradeInSelection.includes(idx);
              const targetPickable = tradeInTargetMode && (isEmptyTarget || isFallBackTarget);
              const components = (() => {
                if (!spot?.price_components) return null;
                try {
                  const arr = typeof spot.price_components === 'string' ? JSON.parse(spot.price_components) : spot.price_components;
                  return Array.isArray(arr) && arr.length > 0 ? arr : null;
                } catch { return null; }
              })();
              const componentsTitle = components
                ? components.map(c => `${c.team}: $${Math.round(Number(c.price) || 0)}`).join(' / ')
                : undefined;
              return (
              <tr key={idx} data-spot-row={String(idx)}
                className={'spot-row' + (hoverRow === String(idx) ? ' spot-row-hover' : '')}
                style={{
                background: hitShown(idx) ? '#e8f5e9' : spot?.customer?.trim() ? '#e3f2fd' : 'white',
                opacity: tradeInGreyed || (tradeInTargetMode && !targetPickable) ? 0.4 : 1,
              }}>
                <td style={tdStyle}>
                  {targetPickable ? (
                    <button
                      onClick={() => pickTradeInTarget(idx)}
                      disabled={executingTradeIn}
                      style={{
                        width: '100%', padding: '6px 8px', borderRadius: 6,
                        background: '#16a34a', color: 'white', border: 'none',
                        fontWeight: 800, fontSize: 18, cursor: 'pointer',
                        lineHeight: 1, opacity: executingTradeIn ? 0.6 : 1,
                      }}
                      title={isFallBackTarget
                        ? 'Keep this team — the customer falls back to it'
                        : 'Award this team to the trade-in'}
                    >＋</button>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {(() => {
                        const info = customerIconFor(spot?.customer);
                        return <SpotCustomerIcon state={info.state} buyer={info.buyer}
                          canOpen={canOpenCustomerSummary} onOpen={setSummaryBuyer} />;
                      })()}
                      <input type="text" value={spot?.customer || ''} onChange={e => updateSpot(idx, 'customer', e.target.value)}
                        readOnly={lockedByCompletion || tradeInMode}
                        title={highBidSortOrder === idx ? 'High bid' : undefined}
                        style={highBidSortOrder === idx
                          ? { ...inputStyle, flex: 1, minWidth: 0, border: '2px solid var(--warning)', borderRadius: 4 }
                          : { ...inputStyle, flex: 1, minWidth: 0 }} />
                    </div>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    <input type="text" inputMode="numeric"
                      value={spot?.price !== '' && spot?.price != null ? `$${Math.round(Number(spot.price))}` : ''}
                      onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); updateSpot(idx, 'price', v === '' ? '' : v); }}
                      readOnly={lockedByCompletion || tradeInMode}
                      style={{ ...inputStyle, textAlign: 'right' }} />
                    {components && (
                      <span title={componentsTitle}
                        style={{ color: 'var(--warning, #d69e2e)', fontWeight: 700, cursor: 'help' }}>*</span>
                    )}
                  </div>
                </td>
                {tradeMode && (
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input type="checkbox" checked={tradeSelection.includes(idx)}
                      onChange={() => toggleTradeCheck(idx)} style={{ margin: 0 }} />
                  </td>
                )}
                {tradeInMode && !tradeInTargetMode && (
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {isTradeInSourceCandidate && (
                      <input type="checkbox" checked={tradeInSelection.includes(idx)}
                        onChange={() => toggleTradeInCheck(idx)} style={{ margin: 0 }} />
                    )}
                  </td>
                )}
                {isMulti ? (
                  <>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{row.nfl}</td>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{row.nba}</td>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{row.mlb}</td>
                  </>
                ) : (
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{row.nfl || row.nba || row.mlb}</td>
                )}
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <input type="checkbox" checked={hitShown(idx)} readOnly style={{ pointerEvents: 'none', margin: 0 }} />
                </td>
                <td style={tdStyle}>
                  {cardsCell(idx, (
                    <input type="text" value={spots[idx]?.hit || ''} onChange={e => updateSpot(idx, 'hit', e.target.value)}
                      readOnly={lockedByCompletion}
                      style={inputStyle} placeholder="" />
                  ))}
                </td>
                <td style={tdStyle}>
                  <input type="text" value={spots[idx]?.freebie || ''} onChange={e => updateSpot(idx, 'freebie', e.target.value)}
                    readOnly={lockedByCompletion}
                    style={inputStyle} placeholder="" />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>

      {/* No-case prompt — opens with the sheet while the break has no case.
          Dismissable; the case bar keeps the warning afterwards. */}
      {caseRequired && canManageCases && !caseOverlayDismissed && !casePickerOpen && (
        <div onClick={() => setCaseOverlayDismissed(true)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', textAlign: 'center' }}>
            <div style={{ fontSize: 30, lineHeight: 1, marginBottom: 8 }}>&#9888;</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>No case linked</div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 18 }}>
              A case must be linked to this break. Link the Airtable case being ripped so its hits can be revealed on each spot.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={() => setCaseOverlayDismissed(true)}>Not now</button>
              <button className="btn btn-primary" onClick={() => { setCaseOverlayDismissed(true); openCasePicker(); }}>Link Case</button>
            </div>
          </div>
        </div>
      )}

      {/* Link Case picker — LIVE cases not yet Ripped, newest first. */}
      {casePickerOpen && (
        <div onClick={() => !caseBusy && setCasePickerOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 10, padding: 20, maxWidth: 420, width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 12px' }}>{caseInfo.cases.length > 0 ? 'Cases' : 'Link a case'}</h3>
            {/* Linked to this break — Unlink is the fix for picking the wrong
                one, before or after spots have been revealed from it. */}
            {caseInfo.cases.length > 0 && (() => {
              return (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>On this break</div>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
                    {caseInfo.cases.map((c, i) => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px',
                        borderTop: i === 0 ? 'none' : '1px solid var(--border)', fontSize: 14 }}>
                        <span>
                          <span style={{ fontWeight: 600, color: '#5b21b6' }}>{c.case_number}</span>
                          <span style={{ color: 'var(--text-light)', fontSize: 13 }}> &middot; {c.card_count} card{c.card_count === 1 ? '' : 's'}</span>
                        </span>
                        <button className="btn btn-sm btn-secondary" onClick={() => unlinkCase(c)} disabled={caseBusy}
                          title={`Remove ${c.case_number} from this break`}
                          style={{ color: 'var(--danger, #e53e3e)' }}>
                          Unlink
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.4, margin: '12px 0 6px' }}>Add another</div>
                </div>
              );
            })()}
            <input type="text" autoFocus value={caseSearch} placeholder="Search case number…"
              onChange={e => setCaseSearch(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 10 }} />
            {caseError && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>{caseError}</div>}
            <div style={{ overflowY: 'auto', flex: 1, border: '1px solid var(--border)', borderRadius: 6 }}>
              {linkableCases == null ? (
                <div style={{ padding: 12, fontSize: 13, color: 'var(--text-light)' }}>Loading cases…</div>
              ) : (() => {
                const q = caseSearch.trim().toLowerCase();
                const linkedHere = new Set(caseInfo.cases.map(c => c.case_number));
                const rows = linkableCases.filter(c => !linkedHere.has(c.case_number) && (!q || c.case_number.toLowerCase().includes(q)));
                if (rows.length === 0) return <div style={{ padding: 12, fontSize: 13, color: 'var(--text-light)' }}>No LIVE cases to link.</div>;
                return rows.map(c => {
                  const elsewhere = c.linked_break_number != null;
                  return (
                    <button key={c.id} type="button" disabled={caseBusy || elsewhere} onClick={() => linkCase(c.id)}
                      style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px',
                        border: 'none', borderBottom: '1px solid var(--border)', background: 'white', textAlign: 'left', fontSize: 14,
                        cursor: elsewhere ? 'not-allowed' : 'pointer', opacity: elsewhere ? 0.5 : 1 }}>
                      <span style={{ fontWeight: 600 }}>{c.case_number}</span>
                      <span style={{ color: 'var(--text-light)', fontSize: 13 }}>
                        {elsewhere ? `On Break #${c.linked_break_number}` : `${c.card_count} card${c.card_count === 1 ? '' : 's'}`}
                      </span>
                    </button>
                  );
                });
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setCasePickerOpen(false)} disabled={caseBusy}>
                {caseBusy ? 'Linking…' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast — a skunk on reveal (nothing is written), or a locked Reveal. */}
      {toast && (
        <div style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 2200,
          background: '#1f2937', color: '#fff', padding: '8px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)', pointerEvents: 'none', whiteSpace: 'nowrap', maxWidth: '90vw', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {toast}
        </div>
      )}

      {/* Live notification overlay — click anywhere to dismiss */}
      {activeNotification && (
        <div
          onClick={() => setActiveNotification(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}>
          <div className="note-modal" style={{
            background: 'white', borderRadius: 12, padding: '32px 40px',
            maxWidth: 560, width: '90%', boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 6 }}>
              Note from {activeNotification.posted_by_name}
            </div>
            <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
              {activeNotification.message}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 16 }}>
              Click anywhere to dismiss
            </div>
          </div>
        </div>
      )}

      {/* Bottom-right notification icon + history panel */}
      {notifHistory.length > 0 && (
        <div ref={notifPanelRef} style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1500 }}>
          <button
            onClick={() => setShowNotifPanel(v => !v)}
            title="Break notifications"
            style={{
              position: 'relative', width: 52, height: 52, borderRadius: '50%',
              background: '#1a1a2e', border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <span style={{
              position: 'absolute', top: -4, right: -4, minWidth: 20, height: 20,
              padding: '0 6px', borderRadius: 10, background: '#e53e3e', color: 'white',
              fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center',
              justifyContent: 'center',
            }}>{notifHistory.length}</span>
          </button>
          {showNotifPanel && (
            <div style={{
              position: 'absolute', bottom: 64, right: 0, width: 320, maxHeight: 400,
              overflowY: 'auto', background: 'white', border: '1px solid var(--border)',
              borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 12,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-light)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Notifications for this break
              </div>
              {notifHistory.map(n => (
                <div key={n.id} style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
                    {n.posted_by_name} · {formatTimeAgo(n.created_at)}
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginTop: 2 }}>{n.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Admin/Partner post-note modal */}
      {showPostNotifModal && (
        <div
          onClick={() => setShowPostNotifModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 10, padding: 20, maxWidth: 480, width: '90%',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Post Note</div>
            <textarea
              value={postNotifDraft}
              onChange={e => setPostNotifDraft(e.target.value)}
              maxLength={1000}
              autoFocus
              placeholder="Type a quick note to anyone on this break's spots page…"
              style={{ width: '100%', minHeight: 120, padding: 10, border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowPostNotifModal(false); setPostNotifDraft(''); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={!postNotifDraft.trim()} onClick={postNotification}>Post</button>
            </div>
          </div>
        </div>
      )}

      {/* Start Next Break confirmation — names the break before anything moves */}
      {confirmNextBreak && nextBreak && (
        <div
          onClick={() => { if (!startingNext) setConfirmNextBreak(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 2200, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 10, padding: 20, maxWidth: 420, width: '90%',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Start Next Break</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
              Open Break Spots for this break and stream it to the Team Grid?
            </div>
            <div style={{ padding: '12px 14px', background: 'var(--bg)', borderRadius: 6 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                #{nextBreak.break_number || '--'} {nextBreak.name || '(unnamed)'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>
                {nextBreak.category}{nextBreak.date ? ` · ${formatDate(nextBreak.date)}` : ''}
              </div>
            </div>
            {/* The next break's case — shown when linked, picked here when not. */}
            {nextCaseInfo == null ? (
              <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-light)' }}>Checking case…</div>
            ) : nextCaseInfo.enabled && !nextNeedsCase ? (
              <div style={{ marginTop: 12, fontSize: 13 }}>
                <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Case: </span>
                {nextCaseInfo.cases.map(c => `${c.case_number} · ${c.card_count} card${c.card_count === 1 ? '' : 's'}`).join(', ')}
              </div>
            ) : nextNeedsCase ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Pick the case for this break</div>
                <input type="text" value={caseSearch} placeholder="Search case number…"
                  onChange={e => setCaseSearch(e.target.value)}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 6 }} />
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                  {linkableCases == null ? (
                    <div style={{ padding: 10, fontSize: 13, color: 'var(--text-light)' }}>Loading cases…</div>
                  ) : (() => {
                    const q = caseSearch.trim().toLowerCase();
                    const rows = linkableCases.filter(c => !q || c.case_number.toLowerCase().includes(q));
                    if (rows.length === 0) return <div style={{ padding: 10, fontSize: 13, color: 'var(--text-light)' }}>No LIVE cases to link.</div>;
                    return rows.map(c => {
                      const elsewhere = c.linked_break_number != null;
                      const picked = nextCaseChoice === c.id;
                      return (
                        <button key={c.id} type="button" disabled={elsewhere || caseBusy} onClick={() => setNextCaseChoice(c.id)}
                          style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px',
                            border: 'none', borderBottom: '1px solid var(--border)', textAlign: 'left', fontSize: 14,
                            background: picked ? '#ede9fe' : 'white', cursor: elsewhere ? 'not-allowed' : 'pointer', opacity: elsewhere ? 0.5 : 1 }}>
                          <span style={{ fontWeight: 600, color: picked ? '#5b21b6' : undefined }}>{picked ? '✓ ' : ''}{c.case_number}</span>
                          <span style={{ color: 'var(--text-light)', fontSize: 13 }}>
                            {elsewhere ? `On Break #${c.linked_break_number}` : `${c.card_count} card${c.card_count === 1 ? '' : 's'}`}
                          </span>
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            ) : null}
            {caseError && confirmNextBreak && (
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--danger)' }}>{caseError}</div>
            )}
            {/* Moving on ends the break you're moving on from — there is no
                world where the next break starts while this one is still
                running. Stated here rather than offered, so the confirmation
                covers both actions. The stamp is still this break's own end,
                never the next break's start, so the gap between them stays
                measurable. */}
            {breakIsRunning && (
              <div style={{
                marginTop: 12, padding: '10px 12px', borderRadius: 6,
                background: 'rgba(221,107,32,0.1)', border: '1px solid rgba(221,107,32,0.35)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#9c4221' }}>
                  This also ends #{breakNumber || '--'}
                  {runningElapsed != null ? ` — running ${formatClock(runningElapsed)}` : ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Its rip time is recorded as of now, and the gap until the next break's
                  first spot sells becomes its between-breaks dead time.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" disabled={startingNext}
                onClick={() => setConfirmNextBreak(false)}>Cancel</button>
              {(() => {
                // Link Case & Start Break is the preferred path. Start Without
                // Case is the grey escape hatch beside Cancel: it starts the
                // break with Card(s) typed by hand, as before cases existed.
                const busy = startingNext || caseBusy || nextCaseInfo == null;
                const blocked = busy || (nextNeedsCase && !nextCaseChoice);
                const start = async (linkFirst) => {
                  if (linkFirst) {
                    setCaseBusy(true);
                    setCaseError('');
                    try {
                      await api.post(`/api/breaks/${nextBreak.id}/cases`, { case_id: nextCaseChoice });
                    } catch (err) {
                      setCaseError(err.response?.data?.error || 'Could not link that case.');
                      return;
                    } finally {
                      setCaseBusy(false);
                    }
                  }
                  if (breakIsRunning) await endBreak('handoff');
                  startNextBreak();
                };
                return (
                  <>
                    {nextNeedsCase && (
                      <button className="btn btn-secondary btn-sm" disabled={busy}
                        onClick={() => start(false)}
                        title="Start this break with no case linked. Card(s) is typed by hand.">
                        Start Without Case
                      </button>
                    )}
                    <button className="btn btn-sm" disabled={blocked}
                      onClick={() => start(nextNeedsCase)}
                      style={{ background: '#805ad5', borderColor: '#805ad5', color: '#fff', opacity: blocked ? 0.6 : 1 }}>
                      {startingNext ? 'Starting…' : caseBusy ? 'Linking case…' : nextNeedsCase ? 'Link Case & Start Break' : 'Start Break'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Trade modal — opened by applyTrade(), commits via executeTrade() */}
      {tradeConfirm && (
        <div
          onClick={cancelTradeConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 2200, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 10, padding: 20, maxWidth: 480, width: '90%',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Confirm Trade</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
              Are you sure you want to confirm this trade?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tradeConfirm.groups.map((g, i) => (
                <div key={i} style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 6, fontSize: 13 }}>
                  <div>
                    <strong>{g.customer}:</strong>{' '}
                    {g.givingUp.join(', ') || '(nothing)'}
                    {' '}<span style={{ color: 'var(--text-light)' }}>→</span>{' '}
                    {g.receiving.join(', ') || '(nothing)'}
                  </div>
                  {g.movingFreebies.length > 0 && (
                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
                      Freebies moving: {g.movingFreebies.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" disabled={executingTrade} onClick={cancelTradeConfirm}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={executingTrade} onClick={executeTrade}>
                {executingTrade ? 'Confirming…' : 'Confirm Trade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Trade-In modal — opens after 2 same-customer sources are picked. */}
      {tradeInConfirm && (
        <div
          onClick={cancelTradeInConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 2200, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 10, padding: 20, maxWidth: 480, width: '90%',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Confirm Trade-In</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
              <strong>{tradeInConfirm.customer}</strong> is trading{' '}
              <strong>{tradeInConfirm.sourceA.team}</strong> and{' '}
              <strong>{tradeInConfirm.sourceB.team}</strong> for a new random team.
            </div>
            <div style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 6, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{tradeInConfirm.sourceA.team}</span>
                <span>${Math.round(tradeInConfirm.sourceA.price)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{tradeInConfirm.sourceB.team}</span>
                <span>${Math.round(tradeInConfirm.sourceB.price)}</span>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>New team</span>
                <span>${Math.round(tradeInConfirm.sum)}</span>
              </div>
              {tradeInConfirm.movingFreebies.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                  Freebies moving: {tradeInConfirm.movingFreebies.join(', ')}
                </div>
              )}
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                After confirming, click the green ＋ on the team {tradeInConfirm.customer} should get — any
                empty team, or one of the two above if they draw their own card back.
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" disabled={executingTradeIn} onClick={cancelTradeInConfirm}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={executingTradeIn} onClick={confirmTradeInProceed}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer summary — the same panel the Top Customers grid opens,
          reached from the black info icon beside a returning break buyer.
          Editing notes / username inside it is admin+partner only, the same
          set as canSeeFinancials. A username edit re-links (or unlinks) the
          handle this roster is keyed on, so re-pull it. */}
      {summaryBuyer && (
        <CustomerDetailModal
          customer={summaryBuyer}
          canEdit={canSeeFinancials}
          onClose={() => setSummaryBuyer(null)}
          onUsernameUpdated={() => refreshLifetimeBreakBuyers()}
        />
      )}
    </div>
  );
}

function ProductSearchDropdown({ value, onChange, onSelect, results }) {
  return (
    <div style={{ position: 'relative' }}>
      <input type="text" placeholder="Search products to add..." value={value} onChange={onChange} />
      {results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: 'var(--shadow-lg)', maxHeight: '200px', overflowY: 'auto', zIndex: 50 }}>
          {results.map(p => (
            <div key={p.product_id} onClick={() => onSelect(p)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
              <div style={{ fontWeight: 500 }}>
                {p.product_name}
                {p.category === 'Custom' && <span className="badge badge-warning" style={{ marginLeft: '6px', fontSize: '10px' }}>Custom</span>}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                {p.sku} &middot; Price: {formatCurrency(p.price)} &middot; COGS: {p.cogs != null ? formatCurrency(p.cogs) : '--'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OtherProductForm({ form, setForm, onAdd, onCancel }) {
  return (
    <div style={{ padding: '12px', background: 'var(--bg)', borderRadius: '6px', marginTop: '8px' }}>
      <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px' }}>Add Custom Product</div>
      <div className="form-row">
        <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Product name" /></div>
        <div className="form-group"><label>Qty</label><input type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} /></div>
      </div>
      <div className="form-row">
        <div className="form-group"><label>Price</label><input type="number" step="0.01" min="0" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="0.00" /></div>
        <div className="form-group"><label>COGS</label><input type="number" step="0.01" min="0" value={form.cogs} onChange={e => setForm({ ...form, cogs: e.target.value })} placeholder="0.00" /></div>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" className="btn btn-primary btn-sm" onClick={onAdd}>Add</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function Breaks() {
  const { user, hasPermission } = useAuth();
  const { breakId: urlBreakId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const urlShowSpots = location.pathname.endsWith('/spots');
  const [breaks, setBreaks] = useState([]);
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', date: todayET(), scheduled_time: '', host_user_id: '', category: '', spot_type: 'Team' });
  const [formProducts, setFormProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [detailProductSearch, setDetailProductSearch] = useState('');
  const [detailProductResults, setDetailProductResults] = useState([]);
  const [showOtherForm, setShowOtherForm] = useState(false);
  const [otherForm, setOtherForm] = useState({ name: '', price: '', cogs: '', quantity: '1' });
  const [showDetailOtherForm, setShowDetailOtherForm] = useState(false);
  const [detailOtherForm, setDetailOtherForm] = useState({ name: '', price: '', cogs: '', quantity: '1' });
  const [templates, setTemplates] = useState([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const editTemplateFormRef = useRef(null);
  const [templateForm, setTemplateForm] = useState({ name: '' });
  const [templateProducts, setTemplateProducts] = useState([]);
  const [templateProductSearch, setTemplateProductSearch] = useState('');
  const [templateProductResults, setTemplateProductResults] = useState([]);
  const [showTemplateOther, setShowTemplateOther] = useState(false);
  const [templateOtherForm, setTemplateOtherForm] = useState({ name: '', price: '', cogs: '', quantity: '1' });
  const [showSpots, setShowSpots] = useState(false);
  const [users, setUsers] = useState([]);
  const [freeProductSearch, setFreeProductSearch] = useState('');
  const [freeProductResults, setFreeProductResults] = useState([]);
  const [showFreeOtherForm, setShowFreeOtherForm] = useState(false);
  const [freeOtherForm, setFreeOtherForm] = useState({ name: '', price: '', cogs: '', quantity: '1' });
  const [sortCol, setSortCol] = useState('break_number');
  const [sortDir, setSortDir] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  // Date range powered by the shared DateRangeFilter component (same
  // interaction the Financials page uses). Default 'lifetime' so the
  // Breaks list defaults to showing every break — matches the pre-swap
  // behavior of empty date inputs. includeFuture is required here: breaks
  // are scheduled ahead of time, and the plain 'lifetime' range ends at
  // today, which would hide every upcoming break.
  const initialBreaksDateRange = getDatePreset('lifetime', { includeFuture: true });
  const [filterDateFrom, setFilterDateFrom] = useState(initialBreaksDateRange.startDate);
  const [filterDateTo, setFilterDateTo] = useState(initialBreaksDateRange.endDate);
  const [filterHost, setFilterHost] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  // Text search across break_spots.hit + break_spots.freebie. Combines
  // with filterCustomer (AND).
  const [filterHitsQuery, setFilterHitsQuery] = useState('');
  // Server-fetched hits/freebies detail card (populated when filterCustomer OR filterHitsQuery is set).
  const [customerHitsFreebies, setCustomerHitsFreebies] = useState(null);
  const [showBreakFilters, setShowBreakFilters] = useState(false);
  // Edit Order mode swaps the table for a drag-to-reorder list of planned breaks.
  const [editingOrder, setEditingOrder] = useState(false);
  // Collapses the main breaks table so the Hits & Freebies card below it is
  // reachable without a long scroll. Only honored while that card is on
  // screen (see breaksTableCollapsed below) — the table can never be left
  // hidden with nothing else to look at.
  const [collapseBreaksTable, setCollapseBreaksTable] = useState(false);
  const [showSkunkExport, setShowSkunkExport] = useState(false);
  const [skunkDate, setSkunkDate] = useState(new Date().toISOString().split('T')[0]);
  // Export scope: 'all' = every break on skunkDate (original behavior),
  // 'select' = only the break ids checked in skunkBreakIds.
  const [skunkScope, setSkunkScope] = useState('all');
  const [skunkBreakIds, setSkunkBreakIds] = useState([]);
  // Step 2 of the export: everything fetched for the chosen scope, frozen at
  // submit time so later edits to the scope controls can't change the PDF.
  // null = the Freebies step isn't open.
  const [exportDraft, setExportDraft] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  // Typed freebies, keyed by the customer name from the break-customers list.
  // Prefilled from saved freebies when the step opens, and written back on
  // Submit, so the export sheet and the stored record never drift apart.
  const [freebieNotes, setFreebieNotes] = useState({});
  // Standalone Freebies panel, opened from the page header. Same per-customer
  // table as the export's Freebies step, but usable on its own so freebies can
  // be entered before the breaks exist and corrected after the PDF went out.
  const [showFreebiePanel, setShowFreebiePanel] = useState(false);
  const [freebiePanelDate, setFreebiePanelDate] = useState(new Date().toISOString().split('T')[0]);
  const [freebiePanelRows, setFreebiePanelRows] = useState([]);
  // customer name -> edited text. Only keys present here are sent on Save, so
  // an untouched row is never rewritten (and never steals `entered_by`).
  const [freebiePanelDrafts, setFreebiePanelDrafts] = useState({});
  // Freebie COGS recorded for the panel's date. Shown next to Add COGS so the
  // giveaway cost is visible on the surface that lists the giveaways.
  const [freebieCogsTotal, setFreebieCogsTotal] = useState(null);
  const [freebiePanelLoading, setFreebiePanelLoading] = useState(false);
  const [freebiePanelSaving, setFreebiePanelSaving] = useState(false);
  const [freebieNewCustomer, setFreebieNewCustomer] = useState('');
  // Add COGS panel, opened from the Freebies step. Products recorded here are
  // day-level giveaway cost — they attach to no break, and land on the day
  // being exported (Financials shows them as "Freebie COGS").
  const [showCogsPanel, setShowCogsPanel] = useState(false);
  const [cogsDate, setCogsDate] = useState('');
  const [cogsEntries, setCogsEntries] = useState([]);
  const [cogsBusy, setCogsBusy] = useState(false);
  const [cogsSearch, setCogsSearch] = useState('');
  const [cogsResults, setCogsResults] = useState([]);
  const [showCogsOtherForm, setShowCogsOtherForm] = useState(false);
  const [cogsOtherForm, setCogsOtherForm] = useState({ name: '', price: '', cogs: '', quantity: '1' });
  const [detailSpots, setDetailSpots] = useState([]);
  const [detailHighBidSpotId, setDetailHighBidSpotId] = useState(null);
  const [detailTrades, setDetailTrades] = useState([]);
  const [detailTradeIns, setDetailTradeIns] = useState([]);
  const [showHitsSummary, setShowHitsSummary] = useState(false);
  const [showHittersSkunks, setShowHittersSkunks] = useState(false);
  const [showTrades, setShowTrades] = useState(false);
  const [showTradeIns, setShowTradeIns] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // null | 'hits' | 'stickies'
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [detailNotifDraft, setDetailNotifDraft] = useState('');
  const [postingDetailNotif, setPostingDetailNotif] = useState(false);
  const canManage = hasPermission('manage_breaks');
  // Selected break is completed and the current user is not an admin → lock its
  // detail-panel editors (Date / Time / Host / Category / Spot / Products,
  // notes/Posts, delete). Top-of-page actions like New Break / Templates use
  // canManage directly and are unaffected.
  const detailLockedByCompletion = selected?.status === 'completed' && user?.role !== 'admin';
  const canEditSelected = canManage && !detailLockedByCompletion;
  const canSeeFinancials = !!user && ['admin', 'partner'].includes(user.role);
  // Info icons on both Freebies tables. They open on different days and often
  // one at a time, so each keeps its own roster and neither fetches while its
  // surface is closed. A multi-date break selection uses the EARLIEST day it
  // covers, so the flag reads as "first-timer within this export".
  const exportFreebieRoster = useBreakBuyerRoster({
    beforeDate: exportDraft?.scopeDates?.[0] || null,
    enabled: !!exportDraft,
  });
  const panelFreebieRoster = useBreakBuyerRoster({
    beforeDate: freebiePanelDate || null,
    enabled: showFreebiePanel,
  });
  // Buyer row whose customer summary is open over a Freebies surface, or null.
  const [freebieSummaryBuyer, setFreebieSummaryBuyer] = useState(null);
  // "Show only first time buyers" on each Freebies table, and which customers'
  // per-break dropdowns are open (keyed by customer name, one set per table).
  const [exportFirstTimeOnly, setExportFirstTimeOnly] = useState(false);
  const [panelFirstTimeOnly, setPanelFirstTimeOnly] = useState(false);
  const [exportExpanded, setExportExpanded] = useState(() => new Set());
  const [panelExpanded, setPanelExpanded] = useState(() => new Set());
  function toggleExpanded(setter, customer) {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(customer)) next.delete(customer); else next.add(customer);
      return next;
    });
  }

  const fetchBreaks = useCallback(async () => {
    try {
      const { data } = await api.get('/api/breaks');
      setBreaks(data.breaks);
    } catch (err) { console.error('Breaks fetch error:', err); }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/api/breaks/templates');
      setTemplates(data.templates);
    } catch (err) { console.error('Templates fetch error:', err); }
  }, []);

  useEffect(() => {
    fetchBreaks(); fetchTemplates();
    api.get('/api/users').then(({ data }) => setUsers(data.users)).catch(() => {});
  }, [fetchBreaks, fetchTemplates]);

  // Populate the Customer autocomplete when the filter date range changes.
  useEffect(() => {
    const params = {};
    if (filterDateFrom) params.startDate = filterDateFrom;
    if (filterDateTo) params.endDate = filterDateTo;
    api.get('/api/breaks/customers', { params })
      .then(({ data }) => setCustomerOptions(data?.customers || []))
      .catch(() => setCustomerOptions([]));
  }, [filterDateFrom, filterDateTo]);

  // Fetch the Hits & Freebies detail card whenever a customer is picked
  // OR a hits/freebies search is typed. Debounced 200ms so typing doesn't
  // spam the network.
  useEffect(() => {
    const cust = filterCustomer.trim();
    const q = filterHitsQuery.trim();
    if (!cust && !q) { setCustomerHitsFreebies(null); return; }
    const handle = setTimeout(() => {
      const params = {};
      if (cust) params.customer = cust;
      if (q) params.q = q;
      if (filterDateFrom) params.startDate = filterDateFrom;
      if (filterDateTo) params.endDate = filterDateTo;
      api.get('/api/breaks/customer-hits-freebies', { params })
        .then(({ data }) => setCustomerHitsFreebies(data || null))
        .catch(() => setCustomerHitsFreebies(null));
    }, 200);
    return () => clearTimeout(handle);
  }, [filterCustomer, filterHitsQuery, filterDateFrom, filterDateTo]);

  // Restore the selected break from the URL on load/refresh. `:breakId` is a
  // break_number (that's what every /breaks/... link carries), never a row id,
  // so the lookup is pinned with `by=number` — the numbers overlap, and the
  // unpinned lookup would resolve some of them to a different break entirely.
  // Everything downstream keys off the resolved row's id.
  useEffect(() => {
    if (urlBreakId && !selected) {
      api.get(`/api/breaks/${urlBreakId}`, { params: { by: 'number' } }).then(({ data }) => {
        setSelected(data.break);
        setSelectedProducts(data.break.products || []);
        if (urlShowSpots && data.break.category) setShowSpots(true);
        api.get(`/api/breaks/${data.break.id}/spots`).then(({ data: s }) => { setDetailSpots(s.spots || []); setDetailHighBidSpotId(s.high_bid_spot_id ?? null); }).catch(() => {});
        api.get(`/api/breaks/${data.break.id}/trades`).then(({ data: t }) => setDetailTrades(t.trades || [])).catch(() => {});
        api.get(`/api/breaks/${data.break.id}/trade-ins`).then(({ data: ti }) => setDetailTradeIns(ti.trade_ins || [])).catch(() => {});
      }).catch(() => {});
    }
  }, [urlBreakId]);

  function closeDetail() {
    setSelected(null);
    navigate('/breaks', { replace: true });
  }

  async function selectBreak(brk) {
    setSelected(brk);
    navigate(`/breaks/${brk.break_number}`, { replace: true });
    try {
      const { data } = await api.get(`/api/breaks/${brk.id}`);
      setSelected(data.break);
      setSelectedProducts(data.break.products || []);
    } catch { setSelectedProducts([]); }
    try {
      const spotsRes = await api.get(`/api/breaks/${brk.id}/spots`);
      setDetailSpots(spotsRes.data.spots || []);
      setDetailHighBidSpotId(spotsRes.data.high_bid_spot_id ?? null);
    } catch { setDetailSpots([]); setDetailHighBidSpotId(null); }
    try {
      const tradesRes = await api.get(`/api/breaks/${brk.id}/trades`);
      setDetailTrades(tradesRes.data.trades || []);
    } catch { setDetailTrades([]); }
    try {
      const tiRes = await api.get(`/api/breaks/${brk.id}/trade-ins`);
      setDetailTradeIns(tiRes.data.trade_ins || []);
    } catch { setDetailTradeIns([]); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    try {
      const products = formProducts.map(p => {
        if (p.custom) return { custom: true, custom_name: p.custom_name, custom_price: p.custom_price, custom_cogs: p.custom_cogs, quantity: p.quantity };
        return { product_id: p.product_id, quantity: p.quantity };
      });
      await api.post('/api/breaks', { ...form, category: form.category || null, spot_type: form.spot_type || 'Team', products });
      setShowForm(false);
      setForm({ name: '', date: todayET(), scheduled_time: '', host_user_id: '', category: '', spot_type: 'Team' });
      setFormProducts([]);
      fetchBreaks();
    } catch (err) {
      console.error('Create break error:', err);
      alert(err.response?.data?.error || 'Failed to create break');
    }
  }

  async function searchProducts(query, target) {
    if (query.length < 1) {
      if (target === 'form') setProductResults([]);
      else if (target === 'detail') setDetailProductResults([]);
      else if (target === 'template') setTemplateProductResults([]);
      else if (target === 'free') setFreeProductResults([]);
      else if (target === 'freebie-cogs') setCogsResults([]);
      return;
    }
    try {
      const { data } = await api.get(`/api/breaks/search-products?q=${encodeURIComponent(query)}`);
      if (target === 'form') setProductResults(data.products);
      else if (target === 'detail') setDetailProductResults(data.products);
      else if (target === 'template') setTemplateProductResults(data.products);
      else if (target === 'free') setFreeProductResults(data.products);
      else if (target === 'freebie-cogs') setCogsResults(data.products);
    } catch {}
  }

  function addFormProduct(prod) {
    if (formProducts.find(p => p.product_id === prod.product_id && !p.custom)) return;
    setFormProducts([...formProducts, { ...prod, quantity: 1 }]);
    setProductSearch(''); setProductResults([]);
  }

  function addFormOther() {
    if (!otherForm.name) return;
    setFormProducts([...formProducts, {
      custom: true, custom_name: otherForm.name, custom_price: parseFloat(otherForm.price) || 0,
      custom_cogs: parseFloat(otherForm.cogs) || 0, quantity: parseInt(otherForm.quantity) || 1,
      product_name: otherForm.name, price: parseFloat(otherForm.price) || 0, cogs: parseFloat(otherForm.cogs) || 0,
      _tempId: Date.now()
    }]);
    setOtherForm({ name: '', price: '', cogs: '', quantity: '1' });
    setShowOtherForm(false);
  }

  function updateFormProductQty(idx, qty) {
    setFormProducts(formProducts.map((p, i) => i === idx ? { ...p, quantity: qty } : p));
  }

  function removeFormProduct(idx) { setFormProducts(formProducts.filter((_, i) => i !== idx)); }

  function loadTemplate(template) {
    // A template saved before a product was switched off in Admin -> Break
    // Products still holds that row, as does one whose catalog product has
    // since been deleted outright. Leave both behind rather than letting a
    // stale template smuggle a break spot — or a dangling id the server will
    // reject — into a new break's contents.
    const stale = p => p.product_id && (p.available_for_breaks === 0 || !p.product_name);
    const dropped = template.products.filter(stale);
    const products = template.products.filter(p => !stale(p)).map(p => {
      if (p.custom_name && !p.product_id) {
        return { custom: true, custom_name: p.custom_name, custom_price: p.custom_price || 0, custom_cogs: p.custom_cogs || 0,
          quantity: p.quantity, product_name: p.custom_name, price: p.custom_price || 0, cogs: p.custom_cogs || 0, _tempId: Date.now() + Math.random() };
      }
      return { ...p, quantity: p.quantity };
    });
    setFormProducts(products);
    if (template.name) {
      setForm(prev => ({ ...prev, name: prev.name || template.name }));
    }
    setShowTemplatePicker(false);
    if (dropped.length > 0) {
      alert(`Left out of this break — no longer available to add:\n${dropped.map(p => `• ${p.product_name || p.product_id}`).join('\n')}`);
    }
  }

  async function addDetailProduct(prod) {
    if (selectedProducts.find(p => p.product_id === prod.product_id)) return;
    try {
      const { data } = await api.post(`/api/breaks/${selected.id}/products`, { product_id: prod.product_id, quantity: 1 });
      setSelectedProducts(data.products);
      setDetailProductSearch(''); setDetailProductResults([]);
      fetchBreaks();
    } catch (err) {
      console.error('Add break product error:', err);
      alert(err.response?.data?.error || 'Failed to add product');
    }
  }

  async function addDetailOther() {
    if (!detailOtherForm.name) return;
    const { data } = await api.post(`/api/breaks/${selected.id}/products`, {
      custom_name: detailOtherForm.name, custom_price: parseFloat(detailOtherForm.price) || 0,
      custom_cogs: parseFloat(detailOtherForm.cogs) || 0, quantity: parseInt(detailOtherForm.quantity) || 1
    });
    setSelectedProducts(data.products);
    setDetailOtherForm({ name: '', price: '', cogs: '', quantity: '1' });
    setShowDetailOtherForm(false);
    fetchBreaks();
  }

  async function addDetailFreeProduct(prod) {
    try {
      const { data } = await api.post(`/api/breaks/${selected.id}/products`, { product_id: prod.product_id, quantity: 1, is_free: true });
      setSelectedProducts(data.products);
      setFreeProductSearch(''); setFreeProductResults([]);
      fetchBreaks();
    } catch (err) {
      console.error('Add free product error:', err);
      alert(err.response?.data?.error || 'Failed to add product');
    }
  }

  async function addDetailFreeOther() {
    if (!freeOtherForm.name) return;
    const { data } = await api.post(`/api/breaks/${selected.id}/products`, {
      custom_name: freeOtherForm.name, custom_price: parseFloat(freeOtherForm.price) || 0,
      custom_cogs: parseFloat(freeOtherForm.cogs) || 0, quantity: parseInt(freeOtherForm.quantity) || 1,
      is_free: true
    });
    setSelectedProducts(data.products);
    setFreeOtherForm({ name: '', price: '', cogs: '', quantity: '1' });
    setShowFreeOtherForm(false);
    fetchBreaks();
  }

  async function updateDetailProductQty(bpId, qty) {
    const { data } = await api.put(`/api/breaks/${selected.id}/products/${bpId}`, { quantity: qty });
    setSelectedProducts(data.products); fetchBreaks();
  }

  async function removeDetailProduct(bpId) {
    if (!window.confirm('Remove this product from the break?')) return;
    const { data } = await api.delete(`/api/breaks/${selected.id}/products/${bpId}`);
    setSelectedProducts(data.products); fetchBreaks();
  }

  // Ends a running break as of now. Backdating a stamp is the timeline
  // editor's job, so this no longer carries a time of its own.
  async function endSelectedBreak(at, source) {
    try {
      const { data } = await api.post(`/api/breaks/${selected.id}/end`, { at, source });
      setSelected(prev => ({ ...prev, ...data.break }));
      fetchBreaks();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to end break');
    }
  }

  async function updateBreakStatus(breakId, status) {
    try {
      await api.put(`/api/breaks/${breakId}`, { status });
      setSelected(prev => ({ ...prev, status }));
      fetchBreaks();
      // The server stamps the fill-time clock on this transition, so read the
      // fresh stamps back rather than leaving the panel showing the old span
      // (or 'Still filling') until the break is re-opened.
      try {
        const { data } = await api.get(`/api/breaks/${breakId}`);
        setSelected(prev => (prev && prev.id === breakId ? { ...prev, ...data.break } : prev));
      } catch { /* the status change itself already landed */ }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    }
  }

  async function updateBreakCategory(breakId, category) {
    await api.put(`/api/breaks/${breakId}`, { category });
    setSelected(prev => ({ ...prev, category }));
    fetchBreaks();
  }

  // Confirm Hits waits on every spot being revealed when a case is linked: an
  // unrevealed hit isn't in Card(s) yet, so it would be confirmed without it.
  // The modal lists those spots (sort_orders; null while loading) and reveals
  // them right there, one at a time or all at once.
  const [confirmUnrevealed, setConfirmUnrevealed] = useState(null);
  const [confirmRevealing, setConfirmRevealing] = useState(null); // sort_order | 'all' | null
  const [confirmRevealNote, setConfirmRevealNote] = useState('');
  useEffect(() => {
    setConfirmRevealNote('');
    if (confirmModal !== 'hits' || !selected) { setConfirmUnrevealed(null); return; }
    let cancelled = false;
    setConfirmUnrevealed(null);
    api.get(`/api/breaks/${selected.id}/cases`)
      .then(({ data }) => { if (!cancelled) setConfirmUnrevealed(data.cases?.length ? (data.unrevealed || []) : []); })
      .catch(() => { if (!cancelled) setConfirmUnrevealed([]); });
    return () => { cancelled = true; };
  }, [confirmModal, selected?.id]);

  async function confirmRevealSpot(spot) {
    if (!selected || confirmRevealing != null) return;
    setConfirmRevealing(spot.sort_order);
    try {
      const { data } = await api.post(`/api/breaks/${selected.id}/spots/${spot.sort_order}/reveal`, { team: spot.team, sport: spot.sport });
      if (data.spots) setDetailSpots(data.spots);
      setConfirmUnrevealed(prev => (prev || []).filter(o => o !== spot.sort_order));
      const team = (spot.team || '').replace(/\s*\|\s*/g, ' / ');
      setConfirmRevealNote(data.skunk ? `${team}: skunk` : `${team}: ${data.hit}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Reveal failed');
    } finally {
      setConfirmRevealing(null);
    }
  }

  async function confirmRevealAll() {
    if (!selected || confirmRevealing != null) return;
    setConfirmRevealing('all');
    try {
      const { data } = await api.post(`/api/breaks/${selected.id}/reveal-all`);
      if (data.spots) setDetailSpots(data.spots);
      setConfirmUnrevealed(data.unrevealed || []);
      setConfirmRevealNote(`${data.hits} hit${data.hits === 1 ? '' : 's'} · ${data.skunks} skunk${data.skunks === 1 ? '' : 's'}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Reveal All failed');
    } finally {
      setConfirmRevealing(null);
    }
  }

  async function submitConfirm(side) {
    if (!selected || confirmSubmitting) return;
    setConfirmSubmitting(true);
    try {
      const { data } = await api.post(`/api/breaks/${selected.id}/confirm-${side}`);
      const patch = side === 'hits'
        ? { hits_confirmed_by: data.hits_confirmed_by, hits_confirmed_at: data.hits_confirmed_at, hits_confirmed_by_name: data.confirmer_name }
        : { stickies_confirmed_by: data.stickies_confirmed_by, stickies_confirmed_at: data.stickies_confirmed_at, stickies_confirmed_by_name: data.confirmer_name };
      if (data.status) patch.status = data.status;
      setSelected(prev => ({ ...prev, ...patch }));
      setConfirmModal(null);
      fetchBreaks();
    } catch (err) {
      // Revealed state changed since the modal opened (another tab): show
      // the spots it's waiting on rather than just an alert.
      if (err.response?.data?.code === 'unrevealed') { setConfirmUnrevealed(err.response.data.unrevealed || []); return; }
      alert(err.response?.data?.error || 'Failed to record confirmation');
    } finally {
      setConfirmSubmitting(false);
    }
  }

  function rejectAndEdit() {
    setConfirmModal(null);
    if (selected) {
      setShowSpots(true);
      navigate(`/breaks/${selected.break_number}/spots`, { replace: true });
    }
  }

  async function updateBreakSpotType(breakId, spot_type) {
    const updates = { spot_type };
    if (spot_type === 'Division') updates.category = 'Multi-Sport';
    await api.put(`/api/breaks/${breakId}`, updates);
    setSelected(prev => ({ ...prev, ...updates }));
    fetchBreaks();
  }

  async function handleDelete(breakId) {
    if (!window.confirm('Delete this break?')) return;
    await api.delete(`/api/breaks/${breakId}`); closeDetail(); fetchBreaks();
  }

  async function postDetailNotification() {
    const msg = detailNotifDraft.trim();
    if (!msg || !selected) return;
    setPostingDetailNotif(true);
    try {
      await api.post(`/api/breaks/${selected.id}/notifications`, { message: msg });
      setDetailNotifDraft('');
    } catch (err) {
      console.error('Post detail notification error:', err);
      alert(err.response?.data?.error || 'Failed to post note');
    } finally {
      setPostingDetailNotif(false);
    }
  }

  // Template CRUD
  function startNewTemplate() {
    setEditingTemplate('new');
    setTemplateForm({ name: '' });
    setTemplateProducts([]);
  }

  function startEditTemplate(t) {
    setEditingTemplate(t.id);
    setTemplateForm({ name: t.name || '' });
    setTemplateProducts((t.products || []).map(p => ({ ...p })));
    // Scroll the edit form into view — the templates list can be long enough
    // that the form would otherwise appear above the user's scroll position
    // and the click feels like a no-op.
    setTimeout(() => {
      editTemplateFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  async function saveTemplate() {
    if (!templateForm.name) return;
    const products = templateProducts.map(p => {
      if (p.custom_name && !p.product_id) return { custom_name: p.custom_name, custom_price: p.custom_price || 0, custom_cogs: p.custom_cogs || 0, quantity: p.quantity };
      return { product_id: p.product_id, quantity: p.quantity };
    });
    if (editingTemplate === 'new') {
      await api.post('/api/breaks/templates', { name: templateForm.name, products });
    } else {
      await api.put(`/api/breaks/templates/${editingTemplate}`, { name: templateForm.name, products });
    }
    setEditingTemplate(null);
    fetchTemplates();
  }

  async function deleteTemplate(id) {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/api/breaks/templates/${id}`);
    fetchTemplates();
  }

  function addTemplateProduct(prod) {
    if (templateProducts.find(p => p.product_id === prod.product_id && !p.custom_name)) return;
    setTemplateProducts([...templateProducts, { ...prod, quantity: 1 }]);
    setTemplateProductSearch(''); setTemplateProductResults([]);
  }

  function addTemplateOther() {
    if (!templateOtherForm.name) return;
    setTemplateProducts([...templateProducts, {
      custom_name: templateOtherForm.name, custom_price: parseFloat(templateOtherForm.price) || 0,
      custom_cogs: parseFloat(templateOtherForm.cogs) || 0, quantity: parseInt(templateOtherForm.quantity) || 1,
      product_name: templateOtherForm.name, price: parseFloat(templateOtherForm.price) || 0, cogs: parseFloat(templateOtherForm.cogs) || 0
    }]);
    setTemplateOtherForm({ name: '', price: '', cogs: '', quantity: '1' });
    setShowTemplateOther(false);
  }

  const formTotalPrice = formProducts.reduce((sum, p) => sum + (p.custom ? (p.custom_price || 0) : (p.price || 0)) * p.quantity, 0);
  const formTotalCogs = formProducts.reduce((sum, p) => sum + (p.custom ? (p.custom_cogs || 0) : (p.cogs || 0)) * p.quantity, 0);

  // Sort & filter
  const COLUMNS = [
    { key: 'break_number', label: 'Break #', align: 'left' },
    { key: 'name', label: 'Name', align: 'left' },
    { key: 'date', label: 'Date', align: 'left' },
    { key: 'scheduled_time', label: 'Time', align: 'left' },
    { key: 'fill_seconds', label: 'Fill Time', align: 'right', info: FILL_TIME_INFO },
    { key: 'break_total_seconds', label: 'Break Total', align: 'right', info: BREAK_TOTAL_INFO },
    { key: 'host_name', label: 'Host', align: 'left' },
    { key: 'score', label: 'Score', align: 'center' },
    ...(canSeeFinancials ? [
      { key: 'avg_spot_price', label: 'Avg Spot Price', align: 'right' },
      { key: 'actual_price', label: 'Actual Price', align: 'right' },
      { key: 'net_profit',   label: 'Net Profit',   align: 'right' },
      { key: 'margin',       label: 'Gross Margin', align: 'right' },
    ] : []),
    { key: 'status', label: 'Status', align: 'left' },
  ];

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  // Planned breaks just got renumbered. Refresh the list, and if the open
  // detail panel is one of them, keep its number and URL in step.
  function handleOrderSaved(numbers) {
    setEditingOrder(false);
    fetchBreaks();
    const moved = selected && numbers.find(n => n.id === selected.id);
    if (moved && moved.break_number !== selected.break_number) {
      setSelected(prev => ({ ...prev, break_number: moved.break_number }));
      navigate(`/breaks/${moved.break_number}${showSpots ? '/spots' : ''}`, { replace: true });
    }
  }

  const hasActiveFilters = filterStatus || filterCategory || filterDateFrom || filterDateTo || filterHost || filterCustomer || filterHitsQuery;
  function clearFilters() {
    setFilterStatus(''); setFilterCategory(''); setFilterHost('');
    setFilterCustomer(''); setFilterHitsQuery('');
    const lifetime = getDatePreset('lifetime', { includeFuture: true });
    setFilterDateFrom(lifetime.startDate);
    setFilterDateTo(lifetime.endDate);
  }

  // Row checkboxes only exist while an export is actually being set up.
  const skunkSelectMode = showSkunkExport && skunkScope === 'select';

  // Ticked breaks, ordered the way the server orders the PDF. Derived from the
  // full `breaks` list rather than the visible rows, so a break stays selected
  // if the table's own filters later hide it.
  const selectedSkunkBreaks = breaks
    .filter(b => skunkBreakIds.includes(b.id))
    .sort((a, b) =>
      String(a.date || '').localeCompare(String(b.date || '')) ||
      String(a.scheduled_time || '').localeCompare(String(b.scheduled_time || '')) ||
      (a.break_number || 0) - (b.break_number || 0)
    );

  function toggleSkunkBreak(id) {
    setSkunkBreakIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  // Step 1 of the export — the scope panel's Submit button. Pulls everything
  // the PDF needs for the chosen scope, snapshots every label derived from the
  // scope controls, and opens the Freebies step. Nothing downloads yet.
  async function submitExportScope() {
    if (exportLoading) return;
    try {
      setExportLoading(true);
      // 'select' scope exports exactly the ticked breaks and ignores the date
      // (a selection may span dates); 'all' keeps the original whole-day export.
      const useSelection = skunkScope === 'select';
      if (useSelection && selectedSkunkBreaks.length === 0) {
        alert('Tick at least one break in the table to export.');
        return;
      }
      const scopeQuery = useSelection
        ? `breakIds=${selectedSkunkBreaks.map(b => b.id).join(',')}`
        : `date=${skunkDate}`;
      const [skunksRes, tradesRes, customersRes] = await Promise.all([
        api.get(`/api/breaks/skunks?${scopeQuery}`),
        api.get(`/api/breaks/trades-by-date?${scopeQuery}`),
        api.get(`/api/breaks/break-customers?${scopeQuery}`),
      ]);
      // Server returns only customers with no hit and no spot freebie anywhere
      // in scope. Day-level freebies are filtered out at PDF time, below.
      const skunkEntries = (skunksRes.data.skunks || []).map(s => ({
        name: s.name,
        display: s.name,
      }));
      const dayBreaks = tradesRes.data.breaks || [];
      const customers = customersRes.data.customers || [];
      if (!skunkEntries.length && !dayBreaks.length && !customers.length) {
        alert(useSelection ? 'Nothing to export for the selected breaks.' : 'Nothing to export for this date.');
        return;
      }

      const [y, m, d] = skunkDate.split('-');
      const dateLabel = `${parseInt(m)}/${parseInt(d)}/${y}`;
      // A selection can span dates, so it gets no date in the heading — the
      // break numbers underneath are what identify the sheet instead. Capped
      // at 8 numbers so the 4"-wide page keeps a readable subtitle.
      const titleText = useSelection ? 'Skunks — Selected Breaks' : `Skunks — ${dateLabel}`;
      const tradesTitleText = useSelection ? 'Trades — Selected Breaks' : `Trades — ${dateLabel}`;
      const freebiesTitleText = useSelection ? 'Freebies — Selected Breaks' : `Freebies — ${dateLabel}`;
      const firstTimeTitleText = useSelection ? 'First Time Break Buyers — Selected Breaks' : `First Time Break Buyers — ${dateLabel}`;
      const scopeNums = selectedSkunkBreaks.map(b => `#${b.break_number || '--'}`);
      const scopeText = !useSelection ? ''
        : scopeNums.length <= 8
          ? `Breaks ${scopeNums.join(', ')}`
          : `Breaks ${scopeNums.slice(0, 8).join(', ')} +${scopeNums.length - 8} more`;
      // Multi-date selections print each break's date on its trades header,
      // since the page heading no longer carries one.
      const showPerBreakDates = useSelection &&
        new Set(dayBreaks.map(b => b.date)).size > 1;

      // Date exports keep the original date-stamped name. Selections are named
      // by break number instead, so repeated exports don't overwrite each other.
      const nums = selectedSkunkBreaks.map(b => b.break_number || b.id);
      const filename = !useSelection
        ? `skunks-trades-${String(parseInt(m)).padStart(2,'0')}-${String(parseInt(d)).padStart(2,'0')}-${y}.pdf`
        : nums.length <= 4
          ? `skunks-trades-breaks-${nums.join('-')}.pdf`
          : `skunks-trades-${nums.length}-breaks.pdf`;

      // Dates the export covers — the Add COGS panel books giveaway cost
      // against one of these, never against today.
      const scopeDates = useSelection
        ? [...new Set(selectedSkunkBreaks.map(b => b.date).filter(Boolean))].sort()
        : [skunkDate];

      // Freebies are keyed by date, so a multi-date selection books them
      // against the last date in scope — the same rule Add COGS uses.
      const freebieDate = scopeDates[scopeDates.length - 1] || skunkDate;

      // Prefill from what's already saved for that date, so freebies entered
      // ahead of time aren't retyped — or silently dropped — on export. The
      // freebies endpoint returns the same roster shape plus any customer who
      // has a saved freebie but no spots in scope, so it replaces the roster
      // outright when it comes back populated.
      let freebieCustomers = customers;
      const savedNotes = {};
      try {
        const saved = await loadFreebies(freebieDate, useSelection ? selectedSkunkBreaks.map(b => b.id) : null);
        if (Array.isArray(saved.customers) && saved.customers.length > 0) freebieCustomers = saved.customers;
        for (const row of saved.customers || []) {
          if ((row.freebie || '').trim()) savedNotes[row.customer] = row.freebie;
        }
      } catch (err) {
        // A read failure shouldn't block the export — fall back to the plain
        // roster and an empty step, which is the old behavior.
        console.error('Load saved freebies error:', err);
      }

      setFreebieNotes(savedNotes);
      setExportDraft({
        useSelection, skunkEntries, dayBreaks, customers: freebieCustomers,
        titleText, tradesTitleText, freebiesTitleText, firstTimeTitleText, scopeText,
        showPerBreakDates, filename, scopeDates, freebieDate,
      });
      setShowSkunkExport(false);
    } catch (err) {
      console.error('Export skunks & trades error:', err);
      alert('Failed to load export data');
    } finally {
      setExportLoading(false);
    }
  }

  // ---- Freebies (day-level, per customer) ---------------------------------
  // Saved freebies for a date merged with that date's break-customer roster.
  // `breakIds` narrows the ROSTER only — a freebie is always keyed by date.
  async function loadFreebies(date, breakIds = null) {
    const params = new URLSearchParams({ date });
    if (breakIds && breakIds.length) params.set('breakIds', breakIds.join(','));
    const { data } = await api.get(`/api/breaks/freebies?${params.toString()}`);
    return data;
  }

  async function refreshFreebiePanel(date) {
    setFreebiePanelLoading(true);
    try {
      const data = await loadFreebies(date);
      setFreebiePanelRows(data.customers || []);
      setFreebiePanelDrafts({});
    } catch (err) {
      console.error('Load freebies error:', err);
      alert('Failed to load freebies for this date');
      setFreebiePanelRows([]);
    } finally {
      setFreebiePanelLoading(false);
    }
    refreshFreebieCogsTotal(date);
  }

  // Read-only companion to the Add COGS panel — a failure here just hides the
  // figure rather than blocking the freebies list it sits under.
  async function refreshFreebieCogsTotal(date) {
    try {
      const { data } = await api.get(`/api/breaks/freebie-cogs?date=${date}`);
      setFreebieCogsTotal(data.total || 0);
    } catch (err) {
      console.error('Load freebie COGS total error:', err);
      setFreebieCogsTotal(null);
    }
  }

  async function openFreebiePanel() {
    const date = skunkDate || new Date().toISOString().split('T')[0];
    setFreebiePanelDate(date);
    setShowFreebiePanel(true);
    setShowSkunkExport(false);
    setFreebieNewCustomer('');
    await refreshFreebiePanel(date);
  }

  // A blank value is meaningful here — it clears a saved freebie — so edits are
  // tracked in their own map and compared against the stored text on Save.
  function editFreebieDraft(customer, value) {
    setFreebiePanelDrafts(prev => ({ ...prev, [customer]: value }));
  }

  function dropFreebieDraft(customer) {
    setFreebiePanelDrafts(prev => {
      const next = { ...prev };
      delete next[customer];
      return next;
    });
  }

  // Add someone with no spots in scope — a freebie sent outside any break, or
  // typed before the break was built. The row is local until Save.
  function addFreebieCustomer() {
    const name = freebieNewCustomer.trim();
    if (!name) return;
    if (freebiePanelRows.some(r => r.customer.toLowerCase() === name.toLowerCase())) {
      alert(`${name} is already in the list below.`);
      setFreebieNewCustomer('');
      return;
    }
    setFreebiePanelRows(prev => [...prev, {
      customer: name, spend: 0, breaks: 0, spots: 0, hits: 0, hit_rate_pct: 0,
      freebie: '', freebie_id: null, updated_at: null, entered_by_name: null,
      in_scope: false,
    }]);
    setFreebiePanelDrafts(prev => ({ ...prev, [name]: '' }));
    setFreebieNewCustomer('');
  }

  const freebiePanelChanges = freebiePanelRows
    .filter(r => Object.prototype.hasOwnProperty.call(freebiePanelDrafts, r.customer))
    .map(r => ({ customer: r.customer, freebie: freebiePanelDrafts[r.customer] ?? '' }))
    .filter(it => {
      const row = freebiePanelRows.find(r => r.customer === it.customer);
      return (it.freebie || '').trim() !== (row?.freebie || '').trim();
    });

  // Rows each Freebies table actually draws. The first-time filter is display
  // only: hidden rows keep their typed freebies, and Save / Submit still send
  // every row.
  const panelVisibleRows = panelFirstTimeOnly
    ? freebiePanelRows.filter(r => isFreebieFirstTimer(panelFreebieRoster, r.customer, !!r.in_scope))
    : freebiePanelRows;
  const exportVisibleCustomers = !exportDraft ? []
    : exportFirstTimeOnly
      ? exportDraft.customers.filter(c => isFreebieFirstTimer(exportFreebieRoster, c.customer, c.in_scope !== false))
      : exportDraft.customers;

  async function saveFreebiePanel() {
    if (freebiePanelSaving) return;
    if (freebiePanelChanges.length === 0) { alert('No freebie changes to save.'); return; }
    setFreebiePanelSaving(true);
    try {
      const { data } = await api.post('/api/breaks/freebies', {
        date: freebiePanelDate, items: freebiePanelChanges,
      });
      setFreebiePanelRows(data.customers || []);
      setFreebiePanelDrafts({});
    } catch (err) {
      console.error('Save freebies error:', err);
      alert(err.response?.data?.error || 'Failed to save freebies');
    } finally {
      setFreebiePanelSaving(false);
    }
  }

  async function removeFreebieRow(row) {
    // Never saved (a locally added customer) → just drop the row.
    if (!row.freebie_id) {
      setFreebiePanelRows(prev => prev.filter(r => r.customer !== row.customer));
      dropFreebieDraft(row.customer);
      return;
    }
    if (!window.confirm(`Remove the freebie saved for ${row.customer} on ${freebiePanelDate}?`)) return;
    try {
      const { data } = await api.delete(`/api/breaks/freebies/${row.freebie_id}`);
      setFreebiePanelRows(data.customers || []);
      dropFreebieDraft(row.customer);
    } catch (err) {
      console.error('Remove freebie error:', err);
      alert(err.response?.data?.error || 'Failed to remove freebie');
    }
  }

  // ---- Add COGS (day-level freebie cost) ----------------------------------
  // Opened from either Freebies surface — the export's Freebies step or the
  // standalone panel. Everything recorded here is booked against that surface's
  // day, not the current one, and not against any break.
  function cogsScopeDates() {
    if (exportDraft) return exportDraft.scopeDates?.length ? exportDraft.scopeDates : [skunkDate];
    if (showFreebiePanel) return [freebiePanelDate];
    return [skunkDate];
  }

  // Closing re-reads the standalone panel's total so a just-added product shows
  // up behind the modal instead of going stale.
  function closeCogsPanel() {
    setShowCogsPanel(false);
    if (!exportDraft && showFreebiePanel) refreshFreebieCogsTotal(freebiePanelDate);
  }

  async function openCogsPanel() {
    const dates = cogsScopeDates();
    const date = dates[dates.length - 1];
    setCogsDate(date);
    setShowCogsPanel(true);
    setCogsSearch(''); setCogsResults([]); setShowCogsOtherForm(false);
    await loadCogsEntries(date);
  }

  async function loadCogsEntries(date) {
    try {
      const { data } = await api.get(`/api/breaks/freebie-cogs?date=${date}`);
      setCogsEntries(data.entries || []);
    } catch (err) {
      console.error('Load freebie COGS error:', err);
      setCogsEntries([]);
    }
  }

  async function addCogsItem(item) {
    if (cogsBusy) return;
    setCogsBusy(true);
    try {
      const { data } = await api.post('/api/breaks/freebie-cogs', { date: cogsDate, items: [item] });
      setCogsEntries(data.entries || []);
    } catch (err) {
      console.error('Add freebie COGS error:', err);
      alert(err.response?.data?.error || 'Failed to add freebie COGS');
    }
    setCogsBusy(false);
  }

  // Adds a single unit — the quantity is then edited in the row's Qty box,
  // the same way products are added to a break.
  async function addCogsProduct(prod) {
    await addCogsItem({ product_id: prod.product_id, quantity: 1 });
    setCogsSearch(''); setCogsResults([]);
  }

  async function addCogsOther() {
    if (!cogsOtherForm.name.trim()) return;
    await addCogsItem({
      custom_name: cogsOtherForm.name,
      custom_price: parseFloat(cogsOtherForm.price) || 0,
      custom_cogs: parseFloat(cogsOtherForm.cogs) || 0,
      quantity: parseInt(cogsOtherForm.quantity, 10) || 1,
    });
    setCogsOtherForm({ name: '', price: '', cogs: '', quantity: '1' });
    setShowCogsOtherForm(false);
  }

  // Optimistic so the Qty box stays responsive while typing; the server
  // response (authoritative for the day's total) replaces it when it lands.
  async function updateCogsEntryQty(id, qty) {
    setCogsEntries(prev => prev.map(e => (e.id === id ? { ...e, quantity: qty } : e)));
    try {
      const { data } = await api.put(`/api/breaks/freebie-cogs/${id}`, { quantity: qty });
      setCogsEntries(data.entries || []);
    } catch (err) {
      console.error('Update freebie COGS error:', err);
      alert(err.response?.data?.error || 'Failed to update quantity');
      loadCogsEntries(cogsDate);
    }
  }

  async function removeCogsEntry(id) {
    if (cogsBusy) return;
    if (!window.confirm('Remove this product from the freebie COGS for this date?')) return;
    setCogsBusy(true);
    try {
      const { data } = await api.delete(`/api/breaks/freebie-cogs/${id}`);
      setCogsEntries(data.entries || []);
    } catch (err) {
      console.error('Remove freebie COGS error:', err);
      alert(err.response?.data?.error || 'Failed to remove entry');
    }
    setCogsBusy(false);
  }

  // Step 2 of the export — Submit at the bottom of the Freebies step. Renders
  // the four-part PDF from the snapshot plus whatever freebies were typed in.
  async function buildExportPdf(draft, notes) {
    try {
      const {
        useSelection, dayBreaks, customers,
        titleText, tradesTitleText, freebiesTitleText, firstTimeTitleText, scopeText,
        showPerBreakDates, filename, scopeDates, freebieDate,
      } = draft;

      // A freebie typed in this step counts as a card too, so anyone given one
      // drops off the skunk page. Matched case-insensitively, like the roster.
      const gotFreebie = new Set(
        Object.entries(notes)
          .filter(([, v]) => v && v.trim())
          .map(([name]) => name.trim().toLowerCase())
      );
      const skunkEntries = draft.skunkEntries.filter(s => !gotFreebie.has(s.name.trim().toLowerCase()));

      // Persist before rendering, so the sheet that goes out and the record in
      // Nucleus always agree. Every roster row is sent — a box left blank
      // clears whatever was saved for that customer, matching what the
      // exporter sees on screen.
      if (canManage && freebieDate && customers.length > 0) {
        try {
          await api.post('/api/breaks/freebies', {
            date: freebieDate,
            items: customers.map(c => ({ customer: c.customer, freebie: notes[c.customer] || '' })),
          });
        } catch (err) {
          console.error('Save freebies error:', err);
          if (!window.confirm('Could not save these freebies to Nucleus. Download the sheet anyway?')) return;
        }
      }

      // First time break buyers for the last page — the same rule as the
      // yellow icon in the Freebies step: a customer with spots in scope and
      // no break spot bought on any day before the first day in scope. The
      // roster is fetched fresh here rather than read off the step's icons,
      // which show nothing until their own fetch lands.
      let firstTimeBuyers = null;
      try {
        const { data } = await api.get('/api/breaks/lifetime-break-buyers', {
          params: { beforeDate: scopeDates?.[0] || freebieDate },
        });
        const returning = new Set((data.buyers || []).map(b => (b.customer || '').trim().toLowerCase()));
        firstTimeBuyers = customers
          .filter(c => c.in_scope !== false && !returning.has((c.customer || '').trim().toLowerCase()))
          .map(c => c.customer)
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      } catch (err) {
        console.error('Load first time break buyers error:', err);
        if (!window.confirm('Could not load first time break buyers. Download the sheet without that page?')) return;
      }

      const pdf = new jsPDF({ unit: 'in', format: [4, 6], orientation: 'portrait' });
      const PAGE_W = 4, PAGE_H = 6;

      // ---------- Page 1: Skunks ----------
      if (skunkEntries.length > 0) {
        const W = 1200, H = 1800;
        const canvas = document.createElement('canvas');
        canvas.width = W; canvas.height = H;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#000000';
        ctx.font = 'bold 48px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(titleText, W / 2, 70);

        if (scopeText) {
          ctx.font = '28px sans-serif';
          ctx.fillText(scopeText, W / 2, 112);
        }

        const cols = skunkEntries.length <= 15 ? 1 : skunkEntries.length <= 30 ? 2 : 3;
        const topMargin = scopeText ? 150 : 110;
        const bottomMargin = 40;
        const availH = H - topMargin - bottomMargin;
        const rows = Math.ceil(skunkEntries.length / cols);

        let fontSize = 36;
        const rowH = availH / rows;
        if (rowH < 44) fontSize = Math.max(18, Math.floor(rowH * 0.75));

        const colW = W / cols;

        ctx.textAlign = 'left';
        ctx.font = `${fontSize}px sans-serif`;
        for (let i = 0; i < skunkEntries.length; i++) {
          const col = Math.floor(i / rows);
          const row = i % rows;
          const x = col * colW + 30;
          const yPos = topMargin + row * rowH + rowH * 0.65;
          ctx.fillStyle = '#000000';
          // Clip names to the column box (with a 10px gutter before the
          // vertical bar) so anything that doesn't fit gets cut right before
          // the divider instead of bleeding into the next column.
          ctx.save();
          ctx.beginPath();
          ctx.rect(col * colW, topMargin + row * rowH, colW - 10, rowH);
          ctx.clip();
          ctx.fillText(skunkEntries[i].display, x, yPos);
          ctx.restore();
          if (row < rows - 1 || col < cols - 1) {
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(col * colW + 10, topMargin + (row + 1) * rowH);
            ctx.lineTo((col + 1) * colW - 10, topMargin + (row + 1) * rowH);
            ctx.stroke();
          }
        }

        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        for (let c = 1; c < cols; c++) {
          ctx.beginPath();
          ctx.moveTo(c * colW, topMargin);
          ctx.lineTo(c * colW, topMargin + rows * rowH);
          ctx.stroke();
        }

        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, PAGE_W, PAGE_H);
      } else {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(18);
        pdf.text(titleText, PAGE_W / 2, 0.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        if (scopeText) pdf.text(scopeText, PAGE_W / 2, 0.85, { align: 'center' });
        pdf.text(
          useSelection ? 'No skunks for the selected breaks.' : 'No skunks for this date.',
          PAGE_W / 2, PAGE_H / 2, { align: 'center' }
        );
      }

      // ---------- Page 2+: Breaks & Trades ----------
      pdf.addPage();

      const MARGIN_X = 0.3;
      const MARGIN_TOP = 0.4;
      const MARGIN_BOTTOM = 0.3;
      const MAX_TEXT_W = PAGE_W - MARGIN_X * 2;
      let yCursor = MARGIN_TOP;

      function ensureSpace(neededIn) {
        if (yCursor + neededIn > PAGE_H - MARGIN_BOTTOM) {
          pdf.addPage();
          yCursor = MARGIN_TOP;
        }
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text(tradesTitleText, PAGE_W / 2, yCursor, { align: 'center' });
      yCursor += 0.22;
      if (scopeText) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        const scopeLines = pdf.splitTextToSize(scopeText, PAGE_W - MARGIN_X * 2);
        pdf.text(scopeLines, PAGE_W / 2, yCursor, { align: 'center' });
        yCursor += 0.16 * scopeLines.length + 0.04;
      }
      yCursor += 0.08;

      if (dayBreaks.length === 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(
          useSelection ? 'No breaks selected.' : 'No breaks for this date.',
          PAGE_W / 2, PAGE_H / 2, { align: 'center' }
        );
      } else {
        const displayTeam = team => (team && team.includes('|')) ? team.split('|')[0] : team;
        const LINE_H = 0.17;
        const HEADER_H = 0.22;
        const SECTION_GAP = 0.1;

        for (const b of dayBreaks) {
          ensureSpace(HEADER_H + LINE_H);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          const datePrefix = showPerBreakDates && b.date ? `${formatDate(b.date)} · ` : '';
          pdf.text(`${datePrefix}#${b.break_number || '--'} — ${b.name || ''}`, MARGIN_X, yCursor);
          yCursor += HEADER_H;

          pdf.setFontSize(9);
          if (!b.trades || b.trades.length === 0) {
            pdf.setFont('helvetica', 'italic');
            pdf.text('No Trades', MARGIN_X + 0.15, yCursor);
            yCursor += LINE_H + SECTION_GAP;
          } else {
            pdf.setFont('helvetica', 'normal');
            // Group multi-leg trades (shared trade_group_id) into one line each.
            const groups = [];
            for (const t of b.trades) {
              if (t.trade_group_id == null) {
                groups.push({ kind: 'single', t });
              } else {
                const last = groups[groups.length - 1];
                if (last && last.kind === 'multi' && last.groupId === t.trade_group_id) {
                  last.legs.push(t);
                } else {
                  groups.push({ kind: 'multi', groupId: t.trade_group_id, legs: [t] });
                }
              }
            }
            for (const g of groups) {
              let line;
              if (g.kind === 'single') {
                const t = g.t;
                line = `${t.customer_a} | ${displayTeam(t.team_a)} <-> ${t.customer_b} | ${displayTeam(t.team_b)}`;
              } else {
                const byGiver = new Map();
                for (const leg of g.legs) {
                  if (!byGiver.has(leg.customer_a)) byGiver.set(leg.customer_a, []);
                  byGiver.get(leg.customer_a).push(displayTeam(leg.team_a));
                }
                const parts = [...byGiver.entries()].map(([giver, teams]) => `${giver} gave ${teams.join(', ')}`);
                line = parts.join(' <-> ');
              }
              const wrapped = pdf.splitTextToSize(line, MAX_TEXT_W - 0.15);
              ensureSpace(LINE_H * wrapped.length);
              pdf.text(wrapped, MARGIN_X + 0.15, yCursor);
              yCursor += LINE_H * wrapped.length;
            }
            yCursor += SECTION_GAP;
          }
        }
      }

      // ---------- Freebies page(s) ----------
      // Only customers the exporter actually typed a freebie for make the
      // sheet, in the same highest-spend-first order they were shown in.
      const freebieRows = customers
        .map(c => ({ customer: c.customer, freebie: (notes[c.customer] || '').trim() }))
        .filter(r => r.freebie);

      pdf.addPage();
      yCursor = MARGIN_TOP;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text(freebiesTitleText, PAGE_W / 2, yCursor, { align: 'center' });
      yCursor += 0.22;
      if (scopeText) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        const scopeLines = pdf.splitTextToSize(scopeText, MAX_TEXT_W);
        pdf.text(scopeLines, PAGE_W / 2, yCursor, { align: 'center' });
        yCursor += 0.16 * scopeLines.length + 0.04;
      }
      yCursor += 0.08;

      if (freebieRows.length === 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text('No freebies recorded.', PAGE_W / 2, PAGE_H / 2, { align: 'center' });
      } else {
        const NAME_W = 1.3, COL_GAP = 0.1;
        const NAME_X = MARGIN_X;
        const FREEBIE_X = MARGIN_X + NAME_W + COL_GAP;
        const FREEBIE_W = PAGE_W - MARGIN_X - FREEBIE_X;
        const ROW_LINE_H = 0.16;

        // Repeated at the top of every freebies page so a spilled table still
        // says which column is which.
        function freebieTableHeader() {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9);
          pdf.text('TikTok Username', NAME_X, yCursor);
          pdf.text('Freebies', FREEBIE_X, yCursor);
          yCursor += 0.07;
          pdf.setDrawColor(0);
          pdf.setLineWidth(0.01);
          pdf.line(MARGIN_X, yCursor, PAGE_W - MARGIN_X, yCursor);
          yCursor += 0.15;
          pdf.setFont('helvetica', 'normal');
        }

        freebieTableHeader();
        for (const row of freebieRows) {
          const nameLines = pdf.splitTextToSize(row.customer, NAME_W);
          const freebieLines = pdf.splitTextToSize(row.freebie, FREEBIE_W);
          const lineCount = Math.max(nameLines.length, freebieLines.length);
          const rowH = ROW_LINE_H * lineCount + 0.06;
          if (yCursor + rowH > PAGE_H - MARGIN_BOTTOM) {
            pdf.addPage();
            yCursor = MARGIN_TOP;
            freebieTableHeader();
          }
          // yCursor is the baseline of the row's FIRST line; wrapped lines sit
          // below it, so the rule goes under the last one — not just above the
          // next row's baseline, where it would strike through that text.
          pdf.text(nameLines, NAME_X, yCursor);
          pdf.text(freebieLines, FREEBIE_X, yCursor);
          const ruleY = yCursor + ROW_LINE_H * (lineCount - 1) + 0.07;
          pdf.setDrawColor(200);
          pdf.setLineWidth(0.005);
          pdf.line(MARGIN_X, ruleY, PAGE_W - MARGIN_X, ruleY);
          yCursor += rowH;
        }
        pdf.setDrawColor(0);
      }

      // ---------- First Time Break Buyers page(s) ----------
      if (firstTimeBuyers) {
        pdf.addPage();
        yCursor = MARGIN_TOP;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        const firstTimeTitleLines = pdf.splitTextToSize(firstTimeTitleText, MAX_TEXT_W);
        pdf.text(firstTimeTitleLines, PAGE_W / 2, yCursor, { align: 'center' });
        yCursor += 0.22 * firstTimeTitleLines.length;
        if (scopeText) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          const scopeLines = pdf.splitTextToSize(scopeText, MAX_TEXT_W);
          pdf.text(scopeLines, PAGE_W / 2, yCursor, { align: 'center' });
          yCursor += 0.16 * scopeLines.length + 0.04;
        }
        yCursor += 0.08;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        if (firstTimeBuyers.length === 0) {
          pdf.text('No first time break buyers.', PAGE_W / 2, PAGE_H / 2, { align: 'center' });
        } else {
          const NAME_LINE_H = 0.2;
          // A first timer who is also on page 1's skunk list gets an asterisk,
          // so the freebie for a first-time skunk isn't missed.
          const skunkKeys = new Set(skunkEntries.map(s => s.name.trim().toLowerCase()));
          for (const name of firstTimeBuyers) {
            const label = skunkKeys.has(name.trim().toLowerCase()) ? `${name}*` : name;
            const nameLines = pdf.splitTextToSize(label, MAX_TEXT_W);
            if (yCursor + NAME_LINE_H * nameLines.length > PAGE_H - MARGIN_BOTTOM) {
              pdf.addPage();
              yCursor = MARGIN_TOP;
            }
            pdf.text(nameLines, MARGIN_X, yCursor);
            yCursor += NAME_LINE_H * nameLines.length;
          }
          yCursor += 0.1;
          if (yCursor > PAGE_H - MARGIN_BOTTOM) {
            pdf.addPage();
            yCursor = MARGIN_TOP;
          }
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(10);
          pdf.text('* = skunk', MARGIN_X, yCursor);
        }
      }

      pdf.save(filename);
      setExportDraft(null);
      setFreebieNotes({});
    } catch (err) {
      console.error('Export skunks & trades error:', err);
      alert('Failed to export skunks & trades');
    }
  }

  async function downloadSurpriseSet(brk) {
    try {
      const { data, headers } = await api.get(`/api/breaks/${brk.id}/surprise-set`, { responseType: 'blob' });
      const filename = `Surprise Set Batch - Break ${brk.break_number}.xlsx`;
      const blob = new Blob([data], { type: headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download surprise set error:', err);
      const msg = err.response?.data instanceof Blob
        ? await err.response.data.text().then(t => { try { return JSON.parse(t).error; } catch { return t; } }).catch(() => null)
        : err.response?.data?.error;
      alert(msg || 'Failed to download surprise set');
    }
  }

  function downloadSpotsCsv() {
    downloadBreakSpotsCsv(selected, detailSpots);
  }

  const uniqueHosts = [...new Set(breaks.map(b => b.host_name).filter(Boolean))].sort();

  const filteredBreaks = breaks.filter(b => {
    if (filterStatus && b.status !== filterStatus) return false;
    if (filterCategory && (b.category || '') !== filterCategory) return false;
    if (filterHost && (b.host_name || '') !== filterHost) return false;
    if (filterDateFrom && b.date < filterDateFrom) return false;
    if (filterDateTo && b.date > filterDateTo) return false;
    if (filterCustomer.trim()) {
      const target = filterCustomer.trim().toLowerCase();
      const custs = String(b.customers || '').split(',').map(c => c.trim().toLowerCase());
      if (!custs.includes(target)) return false;
    }
    return true;
  });

  function getMargin(brk) {
    if (brk.actual_price == null || !brk.actual_price) return null;
    const cogs = brk.actual_cogs != null ? brk.actual_cogs : brk.expected_cogs || 0;
    return ((brk.actual_price - cogs) / brk.actual_price) * 100;
  }

  function getNetProfit(brk) {
    if (brk.actual_price == null || !brk.actual_price) return null;
    const cogs = brk.actual_cogs != null ? brk.actual_cogs : brk.expected_cogs || 0;
    return brk.actual_price - cogs;
  }

  function getAvgSpotPrice(brk) {
    if (!brk.sold_spots_count) return null;
    return brk.sold_spots_sum / brk.sold_spots_count;
  }

  const sortedBreaks = [...filteredBreaks].sort((a, b) => {
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol === 'host_name') { av = a.host_name || ''; bv = b.host_name || ''; }
    if (sortCol === 'margin') { av = getMargin(a) ?? -Infinity; bv = getMargin(b) ?? -Infinity; }
    if (sortCol === 'net_profit') { av = getNetProfit(a) ?? -Infinity; bv = getNetProfit(b) ?? -Infinity; }
    if (sortCol === 'avg_spot_price') { av = getAvgSpotPrice(a) ?? -Infinity; bv = getAvgSpotPrice(b) ?? -Infinity; }
    if (sortCol === 'score') { av = a.score?.score ?? -Infinity; bv = b.score?.score ?? -Infinity; }
    if (sortCol === 'fill_seconds') { av = a.fill_seconds ?? -Infinity; bv = b.fill_seconds ?? -Infinity; }
    if (sortCol === 'break_total_seconds') { av = a.break_total_seconds ?? -Infinity; bv = b.break_total_seconds ?? -Infinity; }
    if (['total_price', 'total_cogs', 'product_count', 'break_number', 'actual_price', 'net_profit', 'margin', 'score', 'avg_spot_price'].includes(sortCol)) {
      av = Number(av) || 0; bv = Number(bv) || 0;
    }
    if (av == null) av = '';
    if (bv == null) bv = '';
    let cmp = 0;
    if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Average fill time across the rows currently in view, so it answers for
  // whatever the filters are showing rather than for all time. Breaks with no
  // trustworthy span (never went live, filled in one pass, or implausibly
  // long) are excluded by the server's own rule and counted separately, so a
  // thin sample is visible rather than silently averaged away.
  const unendedInView = sortedBreaks.filter(b => b.is_running).length;

  // What the Break Spots page's "Start Next Break" button jumps to: the next
  // break still to run, walking up from the open one in the table's natural
  // order (break_number descending). Deliberately computed off the FULL breaks
  // list, not sortedBreaks: whatever is typed into the table's filters or sort
  // headers must not change which break comes next.
  //
  // A break is skippable unless it's still ahead of you: pending and completed
  // breaks are already broken, cancelled ones are never getting broken, and one
  // with no category can't open Break Spots at all. So the walk keeps going up
  // until it finds a planned or live break with a category.
  const startableBreak = b => !!b.category && (b.status === 'planned' || b.status === 'live');
  const breaksByNumber = [...breaks].sort((a, b) => (Number(b.break_number) || 0) - (Number(a.break_number) || 0));
  const nextBreakUp = (() => {
    if (!selected) return null;
    const i = breaksByNumber.findIndex(b => b.id === selected.id);
    if (i < 0) return null;
    for (let j = i - 1; j >= 0; j--) {
      if (startableBreak(breaksByNumber[j])) return breaksByNumber[j];
    }
    return null;
  })();

  // Open Break Spots for that break AND stream it to the Auction Grid. The
  // stream switch goes first so the freshly mounted sheet's live-break check
  // already sees its own break and comes up showing "Stop Streaming".
  async function startNextBreak(next) {
    if (!next || !startableBreak(next)) return;
    try {
      await api.post('/api/auction/live-break', { breakId: next.id });
    } catch (err) {
      console.error('Start next break: stream to Auction Grid failed', err);
      alert('Opened the next break, but streaming it to the Team Grid failed. Use the Stream to Team Grid button to retry.');
    }
    setShowSpots(true);
    setSelected(next);
    setSelectedProducts([]);
    navigate(`/breaks/${next.break_number}/spots`, { replace: true });
    try {
      const { data } = await api.get(`/api/breaks/${next.id}`);
      setSelected(data.break);
      setSelectedProducts(data.break.products || []);
    } catch { setSelectedProducts([]); }
    try {
      const spotsRes = await api.get(`/api/breaks/${next.id}/spots`);
      setDetailSpots(spotsRes.data.spots || []);
      setDetailHighBidSpotId(spotsRes.data.high_bid_spot_id ?? null);
    } catch { setDetailSpots([]); setDetailHighBidSpotId(null); }
    try {
      const tradesRes = await api.get(`/api/breaks/${next.id}/trades`);
      setDetailTrades(tradesRes.data.trades || []);
    } catch { setDetailTrades([]); }
    try {
      const tiRes = await api.get(`/api/breaks/${next.id}/trade-ins`);
      setDetailTradeIns(tiRes.data.trade_ins || []);
    } catch { setDetailTradeIns([]); }
  }

  // Calendar helpers
  const calendarMonth = new Date();
  const firstDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
  const lastDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
  const calendarDays = [];
  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) calendarDays.push(new Date(d).toISOString().split('T')[0]);
  const breaksByDate = {};
  breaks.forEach(b => { if (!breaksByDate[b.date]) breaksByDate[b.date] = []; breaksByDate[b.date].push(b); });

  // The Hits & Freebies card only renders once a customer or hits/freebies
  // search has come back from the server.
  const showHitsFreebies = !!customerHitsFreebies && !!(filterCustomer.trim() || filterHitsQuery.trim());
  const breaksTableCollapsed = showHitsFreebies && collapseBreaksTable;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Breaks</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <span className={`tab ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>List</span>
            <span className={`tab ${view === 'calendar' ? 'active' : ''}`} onClick={() => setView('calendar')}>Calendar</span>
          </div>
          {/* Freebies normally get typed inside the export flow, but they often
              have to be set up beforehand or corrected afterwards, so the same
              table is reachable on its own from here. */}
          <button className="btn btn-secondary" onClick={openFreebiePanel}
            title="View and edit the per-customer freebies for a date">Freebies</button>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button className="btn btn-secondary" onClick={() => setShowSkunkExport(!showSkunkExport)}>Export Skunks &amp; Trades</button>
            {showSkunkExport && (
              <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '260px' }}>
                <div style={{ display: 'flex', gap: '12px', fontSize: '13px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                    <input type="radio" name="skunk-scope" checked={skunkScope === 'all'}
                      onChange={() => setSkunkScope('all')} />
                    All breaks
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                    <input type="radio" name="skunk-scope" checked={skunkScope === 'select'}
                      onChange={() => { setSkunkScope('select'); setView('list'); }} />
                    Specific breaks
                  </label>
                </div>

                {skunkScope === 'all' ? (
                  <input type="date" value={skunkDate} onChange={e => setSkunkDate(e.target.value)}
                    style={{ padding: '6px 8px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px' }} />
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {selectedSkunkBreaks.length === 0
                      ? 'Tick the breaks you want in the table below.'
                      : (
                        <span>
                          <strong style={{ color: 'var(--text)' }}>{selectedSkunkBreaks.length} break{selectedSkunkBreaks.length === 1 ? '' : 's'}</strong> ticked
                          {' '}<span style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => setSkunkBreakIds([])}>clear</span>
                        </span>
                      )}
                  </div>
                )}

                <button className="btn btn-primary btn-sm" onClick={submitExportScope}
                  disabled={exportLoading || (skunkScope === 'select' && selectedSkunkBreaks.length === 0)}>
                  {exportLoading ? 'Loading…' : 'Submit'}
                </button>
              </div>
            )}
          </div>
          {canManage && <button className="btn btn-secondary" onClick={() => setView('templates')}>Break Templates</button>}
          {canManage && <button className="btn btn-primary" onClick={() => setShowForm(true)}>New Break</button>}
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card">
          <form onSubmit={handleCreate}>
            <div className="form-row">
              <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
              <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Time</label><input type="time" value={form.scheduled_time} onChange={e => setForm({ ...form, scheduled_time: e.target.value })} required /></div>
              <div className="form-group">
                <label>Host</label>
                <select value={form.host_user_id} onChange={e => setForm({ ...form, host_user_id: e.target.value })}>
                  <option value="">-- None --</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} disabled={form.spot_type === 'Division'}>
                  <option value="">-- None --</option>
                  {BREAK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Spot</label>
                <select value={form.spot_type} onChange={e => {
                  const st = e.target.value;
                  setForm(f => ({ ...f, spot_type: st, ...(st === 'Division' ? { category: 'Multi-Sport' } : {}) }));
                }}>
                  {SPOT_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label>Products</label>
              <ProductSearchDropdown value={productSearch}
                onChange={e => { setProductSearch(e.target.value); searchProducts(e.target.value, 'form'); }}
                onSelect={addFormProduct} results={productResults} />
              <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowOtherForm(true)}>+ Other</button>
                {templates.length > 0 && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowTemplatePicker(!showTemplatePicker)}>
                    Create From Template
                  </button>
                )}
              </div>
              {showTemplatePicker && (
                <div style={{ marginTop: '8px', border: '1px solid var(--border)', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                  {templates.map(t => (
                    <div key={t.id} onClick={() => loadTemplate(t)}
                      style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '13px' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                      <div style={{ fontWeight: 600 }}>{t.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                        {t.products.map(p => `${p.quantity}x ${getItemName(p)}`).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {showOtherForm && (
                <OtherProductForm form={otherForm} setForm={setOtherForm} onAdd={addFormOther} onCancel={() => setShowOtherForm(false)} />
              )}
              <div style={{ marginTop: '8px' }}>
                <ProductTable products={formProducts} onUpdateQty={updateFormProductQty} onRemove={removeFormProduct} useIndex />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="submit" className="btn btn-primary">Create Break</button>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setFormProducts([]); }}>Cancel</button>
              {formProducts.length > 0 && (
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Total Price: {formatCurrency(formTotalPrice)} &middot; Total COGS: {formatCurrency(formTotalCogs)}
                </span>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Templates view */}
      {view === 'templates' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Break Templates</h3>
            {canManage && !editingTemplate && (
              <button className="btn btn-primary btn-sm" onClick={startNewTemplate}>New Template</button>
            )}
          </div>

          {editingTemplate && (
            <div ref={editTemplateFormRef} style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '16px', scrollMarginTop: 80 }}>
              <div style={{ fontWeight: 600, marginBottom: '12px' }}>{editingTemplate === 'new' ? 'Create Template' : 'Edit Template'}</div>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label>Template Name / Default Break Name</label>
                <input value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="e.g. Friday NBA Mixer (auto-fills new break's Name)" />
              </div>
              <label>Default Products</label>
              <ProductSearchDropdown value={templateProductSearch}
                onChange={e => { setTemplateProductSearch(e.target.value); searchProducts(e.target.value, 'template'); }}
                onSelect={addTemplateProduct} results={templateProductResults} />
              <div style={{ marginTop: '6px' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowTemplateOther(true)}>+ Other</button>
              </div>
              {showTemplateOther && (
                <OtherProductForm form={templateOtherForm} setForm={setTemplateOtherForm} onAdd={addTemplateOther} onCancel={() => setShowTemplateOther(false)} />
              )}
              <div style={{ marginTop: '8px' }}>
                <ProductTable products={templateProducts}
                  onUpdateQty={(idx, qty) => setTemplateProducts(templateProducts.map((p, i) => i === idx ? { ...p, quantity: qty } : p))}
                  onRemove={idx => setTemplateProducts(templateProducts.filter((_, i) => i !== idx))} useIndex />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button className="btn btn-primary btn-sm" onClick={saveTemplate}>Save Template</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingTemplate(null)}>Cancel</button>
              </div>
            </div>
          )}

          {templates.length === 0 && !editingTemplate && (
            <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No templates yet. Create one to quickly set up breaks with common product mixes.</p>
          )}

          {templates.map(t => (
            <div key={t.id} style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontWeight: 600 }}>{t.name}</div>
                {canManage && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => startEditTemplate(t)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteTemplate(t.id)}>Delete</button>
                  </div>
                )}
              </div>
              <ProductTable products={t.products} readOnly />
            </div>
          ))}
        </div>
      )}

      {/* Calendar view */}
      {view === 'calendar' && (
        <div className="card">
          <div className="calendar-grid">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="calendar-header">{d}</div>)}
            {Array(firstDay.getDay()).fill(null).map((_, i) => <div key={`empty-${i}`} className="calendar-day" />)}
            {calendarDays.map(dateStr => (
              <div key={dateStr} className="calendar-day">
                <div className="calendar-day-number">{parseInt(dateStr.split('-')[2])}</div>
                {(breaksByDate[dateStr] || []).map(b => (
                  <div key={b.id} className={`calendar-break ${b.status}`} onClick={() => selectBreak(b)}>{b.name.split(' - ')[0]}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List view */}
      {view === 'list' && (
        <div className="card">
          {editingOrder ? (
            <>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Edit Order</div>
              <BreakOrderEditor breaks={breaks} onCancel={() => setEditingOrder(false)} onSaved={handleOrderSaved} />
            </>
          ) : (
          <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showBreakFilters ? '8px' : '0' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-light)' }}>
              {filteredBreaks.length !== breaks.length ? `${filteredBreaks.length} of ${breaks.length}` : breaks.length} breaks
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              {canManage && (
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingOrder(true)}
                  title="Re-order planned breaks. Breaks that are live, pending, completed or cancelled keep their numbers.">
                  Edit Order
                </button>
              )}
              {hasActiveFilters && <button className="btn btn-secondary btn-sm" onClick={() => { clearFilters(); }}>Clear</button>}
              {showHitsFreebies && (
                <button className="btn btn-secondary btn-sm"
                  onClick={() => setCollapseBreaksTable(!collapseBreaksTable)}
                  title={breaksTableCollapsed ? 'Show the breaks table' : 'Hide the breaks table to jump to Hits & Freebies'}>
                  {breaksTableCollapsed ? 'Show Breaks ▾' : 'Hide Breaks ▴'}
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => setShowBreakFilters(!showBreakFilters)}>
                {showBreakFilters ? 'Hide Filters' : 'Filter'}
              </button>
            </div>
          </div>
          {showBreakFilters && (
            <>
            <div style={{ marginBottom: '8px' }}>
              <DateRangeFilter
                initial="lifetime"
                allowFuture
                onChange={r => {
                  setFilterDateFrom(r.startDate || '');
                  setFilterDateTo(r.endDate || '');
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
              <input type="text" list="breaks-customer-options"
                placeholder="Search Customer" value={filterCustomer}
                onChange={e => setFilterCustomer(e.target.value)}
                title="Filter breaks by customer name — pick from suggestions for exact match"
                style={{ width: '180px', padding: '6px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px', color: filterCustomer ? 'inherit' : 'var(--text-light)' }} />
              <datalist id="breaks-customer-options">
                {customerOptions.map(c => <option key={c} value={c} />)}
              </datalist>
              <input
                type="text"
                placeholder="Search Hits/Freebies"
                value={filterHitsQuery}
                onChange={e => setFilterHitsQuery(e.target.value)}
                title="Substring search across every spot's hit and freebie text (case-insensitive)"
                style={{ width: '200px', padding: '6px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px', color: filterHitsQuery ? 'inherit' : 'var(--text-light)' }}
              />
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ width: 'auto', padding: '6px 8px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px', color: filterStatus ? 'inherit' : 'var(--text-light)' }}>
                <option value="">All Statuses</option>
                {BREAK_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                style={{ width: 'auto', padding: '6px 8px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px', color: filterCategory ? 'inherit' : 'var(--text-light)' }}>
                <option value="">All Categories</option>
                {BREAK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {uniqueHosts.length > 1 && (
                <select value={filterHost} onChange={e => setFilterHost(e.target.value)}
                  style={{ width: 'auto', padding: '6px 8px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px', color: filterHost ? 'inherit' : 'var(--text-light)' }}>
                  <option value="">All Hosts</option>
                  {uniqueHosts.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              )}
            </div>
            </>
          )}
          {/* Unended breaks are the one failure mode that quietly poisons
              three numbers at once — rip, break total, and the gap to the next
              break — so they get a banner rather than a silent '--'. */}
          {unendedInView > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              marginBottom: 10, padding: '8px 12px', borderRadius: 6, fontSize: 13,
              background: 'rgba(221,107,32,0.1)', border: '1px solid rgba(221,107,32,0.35)',
            }}>
              <strong style={{ color: '#9c4221' }}>
                {unendedInView} break{unendedInView === 1 ? '' : 's'} still marked running
              </strong>
              <span style={{ color: 'var(--text-secondary)' }}>
                — End Break was never pressed, so their rip time, break total and dead time
                can't be measured. Open one and set its end time.
              </span>
            </div>
          )}
          {breaksTableCollapsed ? (
            <div
              onClick={() => setCollapseBreaksTable(false)}
              style={{ padding: '10px 12px', border: '1px dashed var(--border)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-light)' }}>
              Breaks table hidden — {sortedBreaks.length} break{sortedBreaks.length === 1 ? '' : 's'}. Click to show.
            </div>
          ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {skunkSelectMode && (
                    <th style={{ width: '32px', textAlign: 'center' }}
                      title="Select all breaks currently shown">
                      <input type="checkbox"
                        checked={sortedBreaks.length > 0 && sortedBreaks.every(b => skunkBreakIds.includes(b.id))}
                        onChange={e => {
                          const visibleIds = sortedBreaks.map(b => b.id);
                          // Only touches the rows in view — ticks made under a
                          // different filter stay put.
                          setSkunkBreakIds(prev => e.target.checked
                            ? [...new Set([...prev, ...visibleIds])]
                            : prev.filter(id => !visibleIds.includes(id)));
                        }} />
                    </th>
                  )}
                  {COLUMNS.map(col => (
                    <th key={col.key} style={{ textAlign: col.align, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      onClick={() => toggleSort(col.key)}>
                      {col.label} {sortCol === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                      {col.info && (
                        // The whole header toggles the sort, so the icon has to
                        // swallow its own click or reading the tip re-sorts.
                        <span onClick={e => e.stopPropagation()} style={{ cursor: 'default' }}>
                          <InfoTip text={col.info} label={col.label} />
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedBreaks.map(b => {
                  const margin = getMargin(b);
                  const netProfit = getNetProfit(b);
                  const avgSpotPrice = getAvgSpotPrice(b);
                  const sc = b.score;
                  return (
                    <tr key={b.id} className="clickable-row" onClick={() => selectBreak(b)}>
                      {skunkSelectMode && (
                        // stopPropagation so ticking a row doesn't also open its
                        // detail panel (the whole row is clickable).
                        <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={skunkBreakIds.includes(b.id)}
                            onChange={() => toggleSkunkBreak(b.id)} />
                        </td>
                      )}
                      <td><strong>{b.break_number || '--'}</strong></td>
                      <td>{b.name}</td>
                      <td>{formatDate(b.date)}</td>
                      <td>{formatTime(b.scheduled_time)}</td>
                      <td style={{ textAlign: 'right' }}
                        title={b.break_started_at && b.fill_ended_at
                          ? `${formatDateTime(b.break_started_at)} → ${formatDateTime(b.fill_ended_at)}`
                          : 'No fill time recorded'}>
                        {formatDuration(b.fill_seconds)}
                      </td>
                      <td style={{ textAlign: 'right' }}
                        title={b.is_running ? 'Still running — End Break has not been pressed' : undefined}>
                        {b.is_running
                          ? <span style={{ color: '#dd6b20', fontWeight: 600 }}>running</span>
                          : formatDuration(b.break_total_seconds)}
                      </td>
                      <td>{b.host_name || '--'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {sc ? (
                          <span title={sc.isFinal ? 'Final Score' : 'Projected Score'}
                            style={{
                              display: 'inline-block', minWidth: '36px', padding: '2px 8px',
                              borderRadius: '10px', fontWeight: 700, fontSize: '12px',
                              color: scoreColor(sc.score), background: scoreBg(sc.score),
                            }}>
                            {(b.status === 'completed' || b.status === 'pending') ? '' : '~'}{sc.score}
                          </span>
                        ) : '--'}
                      </td>
                      {canSeeFinancials && (
                        <>
                          <td style={{ textAlign: 'right' }}>{avgSpotPrice != null ? formatCurrency(avgSpotPrice) : '--'}</td>
                          <td style={{ textAlign: 'right' }}>{b.actual_price != null ? formatCurrency(b.actual_price) : '--'}</td>
                          <td style={{ textAlign: 'right', color: netProfit != null ? (netProfit >= 0 ? 'var(--success, green)' : 'var(--danger, red)') : undefined }}>{netProfit != null ? formatCurrency(netProfit) : '--'}</td>
                          <td style={{ textAlign: 'right', color: margin != null ? (margin >= 0 ? 'var(--success, green)' : 'var(--danger, red)') : undefined }}>{margin != null ? `${margin.toFixed(1)}%` : '--'}</td>
                        </>
                      )}
                      <td><span className={`badge ${statusBadgeClass(b.status)}`}>{b.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          </>
          )}
        </div>
      )}

      {/* Hits & Freebies detail card — appears when a customer OR text search is active */}
      {showHitsFreebies && (() => {
        const cust = customerHitsFreebies.customer;
        const q = customerHitsFreebies.q;
        const header = cust && q ? `Hits & Freebies for ${cust} matching "${q}"`
                     : cust ? `Hits & Freebies for ${cust}`
                     : `Hits & Freebies matching "${q}"`;
        const emptyText = cust && q ? `No hits or freebies for ${cust} matching "${q}" in this date range.`
                        : cust ? `No hits or freebies for ${cust} in this date range.`
                        : `No hits or freebies matching "${q}" in this date range.`;
        // Wrap the matching substring in <mark> when q is set.
        const highlight = (text) => {
          if (!q || !text) return text || '—';
          const idx = String(text).toLowerCase().indexOf(q.toLowerCase());
          if (idx < 0) return text;
          return (<>
            {text.slice(0, idx)}
            <mark style={{ background: '#fde68a', padding: '0 2px', borderRadius: 2 }}>{text.slice(idx, idx + q.length)}</mark>
            {text.slice(idx + q.length)}
          </>);
        };
        return (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>{header}</h3>
            <span style={{ fontSize: 13, color: 'var(--text-light)' }}>
              {customerHitsFreebies.totals.spots} spots · {customerHitsFreebies.totals.hits} hits · {customerHitsFreebies.totals.freebies} freebies
              {canSeeFinancials && ` · ${formatCurrency(customerHitsFreebies.totals.spend)}`}
            </span>
          </div>
          {customerHitsFreebies.rows.length === 0 ? (
            <div style={{ padding: '20px 12px', color: 'var(--text-light)', fontSize: 13 }}>
              {emptyText}
            </div>
          ) : (
            <div className="table-container sticky-col">
              <table>
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>Break #</th>
                    <th>Break Name</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                    <th>Team</th>
                    <th>Customer</th>
                    {canSeeFinancials && <th style={{ textAlign: 'right' }}>Price</th>}
                    <th>Hit</th>
                    <th>Freebie</th>
                  </tr>
                </thead>
                <tbody>
                  {customerHitsFreebies.rows.map((r, i) => {
                    const hasHit = (r.hit || '').trim();
                    return (
                      <tr key={`${r.break_id}-${r.team}-${i}`}
                        className="clickable-row"
                        onClick={() => {
                          const brk = breaks.find(b => b.id === r.break_id);
                          if (brk) selectBreak(brk);
                        }}
                        style={{ background: hasHit ? '#e8f5e9' : undefined }}>
                        <td><strong>{r.break_number || '--'}</strong></td>
                        <td>{r.break_name}</td>
                        <td>{formatDate(r.date)}</td>
                        <td>{String(r.team || '').split('|').map(s => s.trim()).filter(Boolean).join(' | ')}</td>
                        <td>{r.customer || '—'}</td>
                        {canSeeFinancials && <td style={{ textAlign: 'right' }}>{r.price != null ? formatCurrency(r.price) : '--'}</td>}
                        <td>{(r.hit || '').trim() ? highlight(r.hit) : '—'}</td>
                        <td>{(r.freebie || '').trim() ? highlight(r.freebie) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        );
      })()}

      {/* Detail panel */}
      {selected && (() => {
        // Shadow canManage inside the detail panel: once the break is completed,
        // only admins can edit Date/Time/Host/Category/Spot/Status/Products/etc.
        // Top-of-page actions (New Break, Templates) keep their own canManage.
        // components/BreakDetailPanel.jsx is the view-only twin of this panel:
        // anything added or changed here must be mirrored there (read-only).
        const canManage = canEditSelected;
        return (
        <div className="modal-overlay" onClick={() => closeDetail()}>
          <div className="detail-panel" onClick={e => e.stopPropagation()}>
            <div className="detail-panel-header">
              {editingName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3 style={{ margin: 0 }}>#{selected.break_number || '--'}</h3>
                  <input type="text" value={editNameValue} onChange={e => setEditNameValue(e.target.value)}
                    style={{ fontSize: '16px', fontWeight: 600, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: '4px' }}
                    autoFocus onKeyDown={e => { if (e.key === 'Enter') { api.put(`/api/breaks/${selected.id}`, { name: editNameValue }).then(() => { setSelected(prev => ({ ...prev, name: editNameValue })); setEditingName(false); fetchBreaks(); }); } if (e.key === 'Escape') setEditingName(false); }} />
                  <button className="btn btn-primary btn-sm" onClick={() => { api.put(`/api/breaks/${selected.id}`, { name: editNameValue }).then(() => { setSelected(prev => ({ ...prev, name: editNameValue })); setEditingName(false); fetchBreaks(); }); }}>Save</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingName(false)}>Cancel</button>
                </div>
              ) : (
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  #{selected.break_number || '--'} {selected.name}
                  {canManage && (
                    <button onClick={() => { setEditNameValue(selected.name); setEditingName(true); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--text-light)', padding: '2px' }}
                      title="Edit break name">&#9998;</button>
                  )}
                </h3>
              )}
              <button className="detail-panel-close" onClick={() => closeDetail()}>&times;</button>
            </div>
            <div className="break-summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div>
                <label>Date</label>
                <div>
                  {canManage ? (
                    <input type="date" value={selected.date || ''} required onChange={async e => {
                      const val = e.target.value;
                      if (!val) return;
                      const yr = parseInt(val.slice(0, 4), 10);
                      if (!yr || yr < 2000 || yr > 2100) return;
                      setSelected(prev => ({ ...prev, date: val }));
                      try {
                        await api.put(`/api/breaks/${selected.id}`, { date: val });
                        fetchBreaks();
                      } catch (err) {
                        console.error('Update date error:', err);
                      }
                    }} style={{ width: 'auto' }} />
                  ) : (
                    <span>{formatDate(selected.date)}</span>
                  )}
                </div>
              </div>
              <div>
                <label>Time</label>
                <div>
                  {canManage ? (
                    <input type="time" value={selected.scheduled_time || ''} required onChange={async e => {
                      const val = e.target.value;
                      if (!val) return;
                      await api.put(`/api/breaks/${selected.id}`, { scheduled_time: val });
                      setSelected(prev => ({ ...prev, scheduled_time: val }));
                      fetchBreaks();
                    }} style={{ width: 'auto' }} />
                  ) : (
                    <span>{selected.scheduled_time ? formatTime(selected.scheduled_time) : 'Not set'}</span>
                  )}
                </div>
              </div>
              <div>
                <label>Host</label>
                <div>
                  {canManage ? (
                    <select value={selected.host_user_id || ''} onChange={async e => {
                      const val = e.target.value || null;
                      await api.put(`/api/breaks/${selected.id}`, { host_user_id: val });
                      const hostUser = users.find(u => u.id === Number(val));
                      setSelected(prev => ({ ...prev, host_user_id: val, host_name: hostUser?.name || null }));
                      fetchBreaks();
                    }} style={{ width: 'auto' }}>
                      <option value="">-- None --</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  ) : (
                    <span>{selected.host_name || 'Unassigned'}</span>
                  )}
                </div>
              </div>
              <div>
                <label>Status</label>
                <div>
                  {canManage ? (
                    <select value={selected.status} onChange={e => updateBreakStatus(selected.id, e.target.value)} style={{ width: 'auto' }}>
                      {BREAK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <span className={`badge ${statusBadgeClass(selected.status)}`}>{selected.status}</span>
                  )}
                </div>
              </div>
              <div>
                <label>Category</label>
                <div>
                  {canManage ? (
                    <select value={selected.category || ''} onChange={e => updateBreakCategory(selected.id, e.target.value || null)} style={{ width: 'auto' }} disabled={selected.spot_type === 'Division'}>
                      <option value="">-- None --</option>
                      {BREAK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <span>{selected.category || 'None'}</span>
                  )}
                </div>
              </div>
              <div>
                <label>Spot</label>
                <div>
                  {canManage ? (
                    <select value={selected.spot_type || 'Team'} onChange={e => updateBreakSpotType(selected.id, e.target.value)} style={{ width: 'auto' }}>
                      {SPOT_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <span>{selected.spot_type || 'Team'}</span>
                  )}
                </div>
              </div>
              {/* The Airtable case(s) this break rips — linked on the Break Spots page.
                  Same cell in both break panels (Breaks.jsx and BreakDetailPanel.jsx). */}
              <div>
                <label>Case</label>
                <div>
                  <span>
                    {(selected.cases || []).length > 0
                      ? selected.cases.map(c => `${c.case_number} · ${c.card_count} card${c.card_count === 1 ? '' : 's'}`).join(', ')
                      : 'None'}
                  </span>
                </div>
              </div>
              {(() => {
                // Mirror the BreakSpotsSheet (and the view-only panel) exactly:
                //   - totalSpots = category-derived total
                //   - soldPrices = filled prices only (drop nulls/zeros)
                //   - cogs = sum of break_products price-cogs × quantity
                //   - margin = ((avgSold × totalSpots) − breakCogs) / (avgSold × totalSpots) × 100
                const filledPrices = detailSpots
                  .map(s => Number(s.price))
                  .filter(p => p && p > 0);
                const detailActualPrice = filledPrices.reduce((a, b) => a + b, 0);
                const totalSpots = (() => {
                  if ((selected.spot_type || 'Team') === 'Division') return 8;
                  if (selected.category === 'Football') return 32;
                  if (selected.category === 'Basketball') return 30;
                  if (selected.category === 'Baseball') return 30;
                  if (selected.category === 'Multi-Sport') return 33;
                  return detailSpots.length;
                })();
                const breakCogs = selectedProducts.reduce(
                  (s, p) => s + (getItemCogs(p) || 0) * (p.quantity || 0),
                  0
                );
                const avgSold = filledPrices.length > 0 ? detailActualPrice / filledPrices.length : 0;
                const projectedRevenue = avgSold * totalSpots;
                const detailMargin = projectedRevenue > 0
                  ? ((projectedRevenue - breakCogs) / projectedRevenue) * 100
                  : null;
                const isFinalized = selected.status === 'completed' || selected.status === 'pending';
                const lockedScore = isFinalized && selected.final_score != null ? selected.final_score : null;
                const liveScore = computeBreakScore({
                  totalSpots,
                  soldPrices: filledPrices,
                  cogs: breakCogs,
                });
                const scoreVal = lockedScore != null ? lockedScore : (liveScore?.score ?? null);
                const scoreLabel = isFinalized ? 'Final Score' : 'Projected Score';
                return (<>
                  {canSeeFinancials ? (
                    <>
                      <div>
                        <label>Actual Price</label>
                        <div>{detailActualPrice ? formatCurrency(detailActualPrice) : '--'}</div>
                      </div>
                      <div>
                        <label>{selected.status === 'live' ? 'Projected Margin' : 'Margin'}</label>
                        <div style={{ color: detailMargin != null ? (detailMargin >= 0 ? 'var(--success, green)' : 'var(--danger, red)') : undefined }}>
                          {detailMargin != null ? `${detailMargin.toFixed(1)}%` : '--'}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div></div>
                      <div></div>
                    </>
                  )}
                  <div>
                    <label>Spots Sold</label>
                    <div>{filledPrices.length}/{totalSpots || '--'}</div>
                  </div>
                  {/* Second stats row: Spots Sold, Actual PPS, then the score
                      (final or projected) to its right. Break Spots sits on the
                      row below, beside Confirm Hits / Stickies or Download
                      Surprise Set. */}
                  {canSeeFinancials && (
                    <div>
                      <label>Actual PPS</label>
                      <div>{avgSold > 0 ? formatCurrency(avgSold) : '--'}</div>
                    </div>
                  )}
                  <div>
                    <label>{selected.status === 'live' ? 'Projected Score' : (scoreLabel || 'Break Score')}</label>
                    {scoreVal == null ? (
                      <div style={{ fontSize: '12px', color: 'var(--text-light)', lineHeight: 1.3 }}>
                        Score unavailable — enter COGS and at least one spot price to begin scoring
                      </div>
                    ) : (
                      <ScoreCell
                        score={scoreVal}
                        label={scoreLabel}
                        breakdown={liveScore}
                        showBreakdown={canSeeFinancials}
                      />
                    )}
                  </div>
                </>);
              })()}
              {selected.category && (
                <>
                  <div>
                    <label>&nbsp;</label>
                    <div>
                      {selected.status === 'planned' ? (
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => downloadSurpriseSet(selected)}
                          disabled={!SURPRISE_SET_CATEGORIES.includes(selected.category)}>
                          Download Surprise Set
                        </button>
                      ) : (selected.status === 'pending' || selected.hits_confirmed_at) ? (
                        <>
                          <button className="btn btn-secondary btn-sm"
                            onClick={() => setConfirmModal('hits')}
                            disabled={!canManage || !!selected.hits_confirmed_at}
                            style={{ opacity: selected.hits_confirmed_at ? 0.55 : 1 }}>
                            Confirm Hits
                          </button>
                          {selected.hits_confirmed_at && (
                            <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4, lineHeight: 1.3 }}>
                              Confirmed by {selected.hits_confirmed_by_name || 'user'} on {formatDateTime(selected.hits_confirmed_at)}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    {(selected.status === 'pending' || selected.stickies_confirmed_at) && (
                      <>
                        <label>&nbsp;</label>
                        <div>
                          <button className="btn btn-secondary btn-sm"
                            onClick={() => setConfirmModal('stickies')}
                            disabled={!canManage || !!selected.stickies_confirmed_at}
                            style={{ opacity: selected.stickies_confirmed_at ? 0.55 : 1 }}>
                            Confirm Stickies
                          </button>
                          {selected.stickies_confirmed_at && (
                            <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4, lineHeight: 1.3 }}>
                              Confirmed by {selected.stickies_confirmed_by_name || 'user'} on {formatDateTime(selected.stickies_confirmed_at)}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
              {/* Break Spots: column 3 of the action row, in line with Confirm
                  Hits / Stickies or Download Surprise Set. */}
              <div className="stat-pin-col3">
                <label>&nbsp;</label>
                <div>
                  <button className="btn btn-primary btn-sm" onClick={() => { setShowSpots(true); navigate(`/breaks/${selected.break_number}/spots`, { replace: true }); }}>Break Spots</button>
                </div>
              </div>
              {hasPermission('post_break_note') && (
                <div className="break-summary-note" style={{ gridColumn: '1 / -1' }}>
                  <label>Post Note to Break Spots</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input value={detailNotifDraft} onChange={e => setDetailNotifDraft(e.target.value)}
                      placeholder="Type a quick note that pops over the spots page…"
                      maxLength={1000}
                      onKeyDown={e => { if (e.key === 'Enter' && detailNotifDraft.trim()) postDetailNotification(); }}
                      style={{ flex: 1 }} />
                    <button className="btn btn-primary btn-sm"
                      disabled={!detailNotifDraft.trim() || postingDetailNotif}
                      onClick={postDetailNotification}>
                      {postingDetailNotif ? 'Posting…' : 'Post'}
                    </button>
                  </div>
                </div>
              )}
              {/* Timing sits at the foot of the summary: it is a record of how
                  the break ran, not a field anyone sets while running it. */}
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
                <BreakTimeline
                  brk={selected}
                  canEdit={user?.role === 'admin'}
                  onSaved={updated => {
                    setSelected(prev => ({ ...prev, ...updated }));
                    fetchBreaks();
                  }}
                />
                {/* Kept alongside the editor: ending a running break as of now
                    is one click, where the editor means typing a timestamp. */}
                {canManage && selected.is_running && (
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm" style={{ background: '#dd6b20', borderColor: '#dd6b20', color: '#fff' }}
                      onClick={() => endSelectedBreak(undefined, 'button')}>
                      End Break Now
                    </button>
                    <span style={{ fontSize: 11, color: 'var(--text-light)' }}>
                      Stamps Break Ends as of right now.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Price Timeline — spot prices in the order they sold. Live while
                the panel is open, so it grows as the board fills. */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '4px' }}>Price Timeline</h4>
              <BreakPriceTimeline breakId={selected.id} live compact />
            </div>

            {/* Break Products section */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '8px' }}>Break Products</h4>
              <ProductTable
                products={selectedProducts.filter(p => !p.is_free)}
                onUpdateQty={(bpId, qty) => updateDetailProductQty(bpId, qty)}
                onRemove={bpId => removeDetailProduct(bpId)}
                readOnly={!canManage}
              />
              {canManage && (
                <div style={{ marginTop: '8px' }}>
                  <ProductSearchDropdown value={detailProductSearch}
                    onChange={e => { setDetailProductSearch(e.target.value); searchProducts(e.target.value, 'detail'); }}
                    onSelect={addDetailProduct} results={detailProductResults} />
                  <div style={{ marginTop: '6px' }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDetailOtherForm(true)}>+ Other</button>
                  </div>
                  {showDetailOtherForm && (
                    <OtherProductForm form={detailOtherForm} setForm={setDetailOtherForm} onAdd={addDetailOther} onCancel={() => setShowDetailOtherForm(false)} />
                  )}
                </div>
              )}
            </div>

            {/* Product Additions section (is_free rows — counted in Paid Order
                COGS on Financials, same as Break Products) */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '8px' }}>Product Additions</h4>
              <ProductTable
                products={selectedProducts.filter(p => p.is_free)}
                onUpdateQty={(bpId, qty) => updateDetailProductQty(bpId, qty)}
                onRemove={bpId => removeDetailProduct(bpId)}
                readOnly={!canManage}
              />
              {canManage && (
                <div style={{ marginTop: '8px' }}>
                  <ProductSearchDropdown value={freeProductSearch}
                    onChange={e => { setFreeProductSearch(e.target.value); searchProducts(e.target.value, 'free'); }}
                    onSelect={addDetailFreeProduct} results={freeProductResults} />
                  <div style={{ marginTop: '6px' }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowFreeOtherForm(true)}>+ Other</button>
                  </div>
                  {showFreeOtherForm && (
                    <OtherProductForm form={freeOtherForm} setForm={setFreeOtherForm} onAdd={addDetailFreeOther} onCancel={() => setShowFreeOtherForm(false)} />
                  )}
                </div>
              )}
            </div>

            {/* Hits Summary */}
            {detailSpots.length > 0 && (() => {
              const showFreebies = detailSpots.some(s => s.freebie?.trim());
              const hits = detailSpots.filter(s => s.customer?.trim() && (s.hit?.trim() || s.freebie?.trim()));
              if (hits.length === 0) return null;
              return (
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ marginBottom: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => setShowHitsSummary(v => !v)}>
                    <span style={{ display: 'inline-block', transform: showHitsSummary ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '12px' }}>&#9654;</span>
                    Hits Summary
                  </h4>
                  {showHitsSummary && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: '13px', fontWeight: 700 }}>Customer</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: '13px', fontWeight: 700 }}>Team</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: '13px', fontWeight: 700 }}>Hit</th>
                          {showFreebies && <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: '13px', fontWeight: 700 }}>Freebie(s)</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {hits.map((s, i) => (
                          <tr key={i}>
                            <td title={s.id === detailHighBidSpotId ? 'High bid' : undefined} style={{
                              padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: '13px',
                              ...(s.id === detailHighBidSpotId ? { outline: '2px solid var(--warning)', outlineOffset: '-2px', borderRadius: 4 } : {}),
                            }}>{s.customer}</td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: '13px' }}>{(s.team || '').replace(/\s*\|\s*/g, ' | ')}</td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: '13px' }}>{s.hit}</td>
                            {showFreebies && <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: '13px' }}>{s.freebie || ''}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}

            {/* Hitters / Skunks */}
            {detailSpots.length > 0 && (() => {
              const customerSpots = {};
              detailSpots.forEach(s => {
                if (!s.customer?.trim()) return;
                if (!customerSpots[s.customer]) customerSpots[s.customer] = [];
                customerSpots[s.customer].push(s);
              });
              const hitters = Object.keys(customerSpots).filter(c => customerSpots[c].some(s => s.hit?.trim()));
              const skunks = Object.keys(customerSpots).filter(c => !customerSpots[c].some(s => s.hit?.trim()));
              if (hitters.length === 0 && skunks.length === 0) return null;
              return (
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ marginBottom: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => setShowHittersSkunks(v => !v)}>
                    <span style={{ display: 'inline-block', transform: showHittersSkunks ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '12px' }}>&#9654;</span>
                    Hitters / Skunks
                  </h4>
                  {showHittersSkunks && (
                    <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '8px' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--success)', marginBottom: '4px' }}>Hitters</div>
                        {hitters.length > 0 ? hitters.map(c => (
                          <div key={c} style={{ fontSize: '13px', padding: '2px 0' }}>{c} ({customerSpots[c].length})</div>
                        )) : <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>None</div>}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--danger)', marginBottom: '4px' }}>Skunks</div>
                        {skunks.length > 0 ? skunks.map(c => (
                          <div key={c} style={{ fontSize: '13px', padding: '2px 0' }}>{c} ({customerSpots[c].length})</div>
                        )) : <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>None</div>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Team Trades */}
            {detailTrades.length > 0 && (() => {
              // Undo button visibility: only while the break is still in the
              // editable window (not pending / completed) and the user can
              // manage breaks. Server enforces the same status guard.
              const canUndoTrade = canManage
                && selected.status !== 'pending'
                && selected.status !== 'completed';
              const undoTrade = async (tradeId) => {
                if (!window.confirm('Undo this trade?')) return;
                try {
                  await api.post(`/api/breaks/${selected.id}/trades/${tradeId}/undo`);
                  const [tradesRes, spotsRes] = await Promise.all([
                    api.get(`/api/breaks/${selected.id}/trades`),
                    api.get(`/api/breaks/${selected.id}/spots`),
                  ]);
                  setDetailTrades(tradesRes.data.trades || []);
                  setDetailSpots(spotsRes.data.spots || []);
                  setDetailHighBidSpotId(spotsRes.data.high_bid_spot_id ?? null);
                } catch (err) {
                  alert(err.response?.data?.error || 'Undo failed');
                }
              };
              return (
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ marginBottom: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => setShowTrades(v => !v)}>
                  <span style={{ display: 'inline-block', transform: showTrades ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '12px' }}>&#9654;</span>
                  Team Trades
                </h4>
                {showTrades && (
                  <div style={{ marginTop: '8px' }}>
                    {(() => {
                      const displayTeam = team => team && team.includes('|') ? team.split('|')[0] : team;
                      // Multi-leg trades share trade_group_id and arrive contiguously
                      // (same traded_at timestamp). Collapse them into one logical entry.
                      const groups = [];
                      for (const t of detailTrades) {
                        if (t.trade_group_id == null) {
                          groups.push({ kind: 'single', t });
                        } else {
                          const last = groups[groups.length - 1];
                          if (last && last.kind === 'multi' && last.groupId === t.trade_group_id) {
                            last.legs.push(t);
                          } else {
                            groups.push({ kind: 'multi', groupId: t.trade_group_id, legs: [t] });
                          }
                        }
                      }
                      return groups.map((g, idx) => {
                        const undoTradeId = g.kind === 'single' ? g.t.id : g.legs[0].id;
                        const rowStyle = { fontSize: '13px', padding: '2px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 };
                        if (g.kind === 'single') {
                          const t = g.t;
                          return (
                            <div key={t.id} style={rowStyle}>
                              <span>{t.customer_a} | {displayTeam(t.team_a)} ↔ {t.customer_b} | {displayTeam(t.team_b)}</span>
                              {canUndoTrade && (
                                <button className="btn btn-secondary btn-sm" onClick={() => undoTrade(undoTradeId)}>Undo</button>
                              )}
                            </div>
                          );
                        }
                        // Multi-trade: group legs by giver.
                        const byGiver = new Map();
                        for (const leg of g.legs) {
                          if (!byGiver.has(leg.customer_a)) byGiver.set(leg.customer_a, []);
                          byGiver.get(leg.customer_a).push(displayTeam(leg.team_a));
                        }
                        const parts = [...byGiver.entries()].map(([giver, teams]) => `${giver} gave ${teams.join(', ')}`);
                        return (
                          <div key={`g-${g.groupId}-${idx}`} style={rowStyle}>
                            <span>{parts.join('  ↔  ')}</span>
                            {canUndoTrade && (
                              <button className="btn btn-secondary btn-sm" onClick={() => undoTrade(undoTradeId)}>Undo</button>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
              );
            })()}

            {/* Team Trade-Ins — separate from Team Trades. Each row: one
                customer's two surrendered teams → one merged target. */}
            {detailTradeIns.length > 0 && (() => {
              const canUndoTradeIn = canManage
                && selected.status !== 'pending'
                && selected.status !== 'completed';
              const undoTradeIn = async (tradeInId) => {
                if (!window.confirm('Undo this trade-in?')) return;
                try {
                  await api.post(`/api/breaks/${selected.id}/trade-ins/${tradeInId}/undo`);
                  const [tiRes, spotsRes] = await Promise.all([
                    api.get(`/api/breaks/${selected.id}/trade-ins`),
                    api.get(`/api/breaks/${selected.id}/spots`),
                  ]);
                  setDetailTradeIns(tiRes.data.trade_ins || []);
                  setDetailSpots(spotsRes.data.spots || []);
                  setDetailHighBidSpotId(spotsRes.data.high_bid_spot_id ?? null);
                } catch (err) {
                  alert(err.response?.data?.error || 'Undo failed');
                }
              };
              return (
                <div style={{ marginBottom: '12px' }}>
                  <h4 style={{ marginBottom: 0, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => setShowTradeIns(v => !v)}>
                    <span style={{ display: 'inline-block', transform: showTradeIns ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '12px' }}>&#9654;</span>
                    Team Trade-Ins
                  </h4>
                  {showTradeIns && (
                    <div style={{ marginTop: '8px' }}>
                      {detailTradeIns.map(ti => (
                        <div key={ti.id} style={{ fontSize: '13px', padding: '2px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span>
                            {ti.customer} | {ti.source_a_team} (${Math.round(Number(ti.source_a_price) || 0)}) + {ti.source_b_team} (${Math.round(Number(ti.source_b_price) || 0)}) → {ti.target_team} (${Math.round(Number(ti.target_price) || 0)})
                          </span>
                          {canUndoTradeIn && (
                            <button className="btn btn-secondary btn-sm" onClick={() => undoTradeIn(ti.id)}>Undo</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <ThreadPanel entityType="break" entityId={selected.id} />

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '16px', marginBottom: '16px' }}>
              {canManage && (
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(selected.id)}>Delete Break</button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={downloadSpotsCsv}>Download as CSV</button>
            </div>
          </div>
        </div>
        );
      })()}

      {confirmModal && selected && (() => {
        const showFreebies = (detailSpots || []).some(s => s.freebie?.trim());
        const hits = (detailSpots || []).filter(s => s.customer?.trim() && (s.hit?.trim() || s.freebie?.trim()));
        const title = confirmModal === 'hits' ? 'Confirm Hits' : 'Confirm Stickies';
        const checkingReveals = confirmModal === 'hits' && confirmUnrevealed == null;
        const unrevealedOrders = confirmModal === 'hits' ? (confirmUnrevealed || []) : [];
        const unrevealedSpots = unrevealedOrders
          .map(o => (detailSpots || []).find(s => s.sort_order === o))
          .filter(Boolean);
        const blocked = checkingReveals || unrevealedOrders.length > 0;
        return (
          <div className="modal-overlay" onClick={() => !confirmSubmitting && setConfirmModal(null)}>
            <div className="detail-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
              <div className="detail-panel-header">
                <h3 style={{ margin: 0 }}>{title}</h3>
                <button className="detail-panel-close" onClick={() => !confirmSubmitting && setConfirmModal(null)}>&times;</button>
              </div>
              {unrevealedOrders.length > 0 && (
                <div style={{ margin: '8px 0 4px', padding: '10px 12px', borderRadius: 6, background: '#fffbeb', border: '1px solid #f59e0b' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 200, color: '#92400e', fontWeight: 600, fontSize: 13 }}>
                      &#9888; {unrevealedOrders.length} spot{unrevealedOrders.length === 1 ? ' hasn\'t' : 's haven\'t'} been revealed.
                      Their hits aren't recorded until they are, so reveal them before confirming.
                    </div>
                    {canManage && (
                      <button className="btn btn-sm btn-primary" onClick={confirmRevealAll} disabled={confirmRevealing != null}
                        style={{ whiteSpace: 'nowrap' }}>
                        {confirmRevealing === 'all' ? 'Revealing…' : 'Reveal All'}
                      </button>
                    )}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--card-bg, #fff)' }}>
                    <tbody>
                      {unrevealedSpots.map(s => (
                        <tr key={s.sort_order}>
                          <td style={{ padding: '5px 8px', borderBottom: '1px solid #eee', fontSize: 13 }}>{s.customer || ''}</td>
                          <td style={{ padding: '5px 8px', borderBottom: '1px solid #eee', fontSize: 13 }}>{(s.team || '').replace(/\s*\|\s*/g, ' | ')}</td>
                          <td style={{ padding: '5px 8px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                            {canManage && (
                              <button className="btn btn-sm btn-secondary" onClick={() => confirmRevealSpot(s)} disabled={confirmRevealing != null}
                                style={{ whiteSpace: 'nowrap' }}>
                                {confirmRevealing === s.sort_order ? 'Revealing…' : 'Reveal'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {confirmModal === 'hits' && confirmRevealNote && (
                <div style={{ fontSize: 12, color: 'var(--text-light)', margin: '6px 0 0' }}>Revealed — {confirmRevealNote}</div>
              )}
              <div style={{ padding: '4px 0 12px' }}>
                {hits.length === 0 ? (
                  <div style={{ color: 'var(--text-light)', fontSize: 13, padding: '12px 0' }}>No hits recorded for this break.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: 13, fontWeight: 700 }}>Customer</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: 13, fontWeight: 700 }}>Team</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: 13, fontWeight: 700 }}>Hit</th>
                        {showFreebies && <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)', fontSize: 13, fontWeight: 700 }}>Freebie(s)</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {hits.map((s, i) => (
                        <tr key={i}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13 }}>{s.customer}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13 }}>{(s.team || '').replace(/\s*\|\s*/g, ' | ')}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13 }}>{s.hit}</td>
                          {showFreebies && <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13 }}>{s.freebie || ''}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button
                  onClick={() => submitConfirm(confirmModal)}
                  disabled={confirmSubmitting || blocked}
                  title={unrevealedOrders.length > 0 ? 'Reveal every spot first' : undefined}
                  style={{ flex: 1, padding: '10px 16px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: confirmSubmitting || blocked ? 'not-allowed' : 'pointer', opacity: confirmSubmitting || blocked ? 0.6 : 1 }}>
                  {confirmSubmitting ? 'Saving…' : checkingReveals ? 'Checking reveals…' : 'Confirm'}
                </button>
                <button
                  onClick={rejectAndEdit}
                  disabled={confirmSubmitting}
                  style={{ flex: 1, padding: '10px 16px', background: '#c62828', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: confirmSubmitting ? 'not-allowed' : 'pointer', opacity: confirmSubmitting ? 0.6 : 1 }}>
                  Reject &amp; Edit
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Standalone Freebies panel — the same per-customer table the export's
          Freebies step shows, but opened on its own so a date's freebies can be
          set up before the breaks exist and corrected after the sheet went out.
          Edits here are saved to customer_freebies, which is what the export
          step now prefills from. */}
      {showFreebiePanel && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 2300, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <div
            style={{
              background: 'white', borderRadius: 10, padding: 20, width: '90%', maxWidth: 900,
              maxHeight: '88vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Freebies</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Per-customer freebies for one date — the same list the Export Skunks &amp; Trades
                  sheet is built from. {canManage ? 'Edit any row, then Save.' : 'Read-only for your role.'}
                </div>
              </div>
              <button className="detail-panel-close" onClick={() => {
                setShowFreebiePanel(false); setFreebiePanelDrafts({}); setFreebieNewCustomer('');
              }}>&times;</button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              {/* App.css stretches every bare input to 100% — these two are
                  toolbar controls, so they carry their own widths. */}
              <label style={{ display: 'inline-block', marginBottom: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Date</label>
              <input type="date" value={freebiePanelDate}
                onChange={e => { setFreebiePanelDate(e.target.value); refreshFreebiePanel(e.target.value); }}
                style={{ width: 160, padding: '6px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6 }} />
              {canManage && (
                <>
                  <input type="text" value={freebieNewCustomer}
                    onChange={e => setFreebieNewCustomer(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFreebieCustomer(); } }}
                    placeholder="Add a customer not listed…"
                    style={{ width: 240, padding: '6px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6 }} />
                  <button className="btn btn-secondary btn-sm" onClick={addFreebieCustomer}
                    disabled={!freebieNewCustomer.trim()}>Add</button>
                </>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <FirstTimeBuyersToggle checked={panelFirstTimeOnly} onChange={setPanelFirstTimeOnly}
                  roster={panelFreebieRoster} shown={panelVisibleRows.length} total={freebiePanelRows.length} />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', marginTop: 12, border: '1px solid var(--border)', borderRadius: 6 }}>
              {freebiePanelLoading ? (
                <div style={{ color: 'var(--text-light)', fontSize: 13, padding: 16 }}>Loading…</div>
              ) : freebiePanelRows.length === 0 ? (
                <div style={{ color: 'var(--text-light)', fontSize: 13, padding: 16 }}>
                  No break customers and no saved freebies for this date.
                  {canManage && ' Use the box above to add someone.'}
                </div>
              ) : panelVisibleRows.length === 0 ? (
                <div style={{ color: 'var(--text-light)', fontSize: 13, padding: 16 }}>
                  {panelFreebieRoster.ready ? 'No first time break buyers on this date.' : 'Checking first time break buyers…'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {[
                        ['Username', 'left'], ['Spend', 'right'], ['Breaks', 'right'],
                        ['Spots', 'right'], ['Hits', 'right'], ['Hit %', 'right'],
                        ['Freebies', 'left'], ['', 'right'],
                      ].map(([label, align], i) => (
                        <th key={label || `col-${i}`} style={{
                          textAlign: align, padding: '8px', fontSize: 12, fontWeight: 700,
                          borderBottom: '2px solid var(--border)', background: 'var(--bg)',
                          position: 'sticky', top: 0, zIndex: 1,
                        }}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {panelVisibleRows.map(r => {
                      const edited = Object.prototype.hasOwnProperty.call(freebiePanelDrafts, r.customer);
                      const value = edited ? freebiePanelDrafts[r.customer] : (r.freebie || '');
                      const dirty = edited && (value || '').trim() !== (r.freebie || '').trim();
                      const cell = { padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13 };
                      const details = r.break_details || [];
                      const expanded = details.length > 0 && panelExpanded.has(r.customer);
                      return (
                        <React.Fragment key={r.customer}>
                        <tr style={dirty ? { background: 'rgba(255, 214, 102, 0.18)' } : undefined}>
                          <td style={{ ...cell, fontWeight: 600 }}>
                            <FreebieCustomerName customer={r.customer} inScope={!!r.in_scope}
                              roster={panelFreebieRoster} onOpen={setFreebieSummaryBuyer}
                              expanded={expanded}
                              onToggle={details.length > 0 ? () => toggleExpanded(setPanelExpanded, r.customer) : null}>
                              {/* No spots in scope — a freebie sent outside the day's breaks. */}
                              {!r.in_scope && (
                                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-light)' }}
                                  title="No break spots on this date — freebie only">no breaks</span>
                              )}
                            </FreebieCustomerName>
                          </td>
                          <td style={{ ...cell, textAlign: 'right' }}>{canSeeFinancials ? formatCurrency(r.spend) : '—'}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{r.breaks}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{r.spots}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{r.hits}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{r.hit_rate_pct}%</td>
                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                            {canManage ? (
                              <input type="text" value={value}
                                onChange={e => editFreebieDraft(r.customer, e.target.value)}
                                placeholder="Add freebie…"
                                style={{ width: '100%', minWidth: 160, padding: '5px 7px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 5 }} />
                            ) : (
                              <span style={{ fontSize: 13 }}>{(r.freebie || '').trim() || '—'}</span>
                            )}
                            {r.updated_at && (
                              <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>
                                {r.entered_by_name ? `${r.entered_by_name} · ` : ''}{formatTimeAgo(r.updated_at)}
                              </div>
                            )}
                          </td>
                          <td style={{ ...cell, textAlign: 'right' }}>
                            {canManage && (r.freebie_id || !r.in_scope) && (
                              <button className="btn btn-secondary btn-sm" onClick={() => removeFreebieRow(r)}
                                title={r.freebie_id ? 'Delete this saved freebie' : 'Remove this row'}>Remove</button>
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <FreebieBreakDetailsRow details={details} colSpan={8} showSpend={canSeeFinancials} />
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Same day-level giveaway cost the export's Freebies step
                    records — it books to this panel's date, not to any break. */}
                {canManage && (
                  <button className="btn btn-secondary btn-sm" onClick={openCogsPanel}>Add COGS</button>
                )}
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {freebiePanelRows.filter(r => (r.freebie || '').trim()).length} saved
                  {freebieCogsTotal != null && ` · ${formatCurrency(freebieCogsTotal)} freebie COGS`}
                  {freebiePanelChanges.length > 0 && ` · ${freebiePanelChanges.length} unsaved change(s)`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  setShowFreebiePanel(false); setFreebiePanelDrafts({}); setFreebieNewCustomer('');
                }}>Close</button>
                {canManage && (
                  <button className="btn btn-primary btn-sm" onClick={saveFreebiePanel}
                    disabled={freebiePanelSaving || freebiePanelChanges.length === 0}>
                    {freebiePanelSaving ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Freebies step — second half of Export Skunks & Trades. Type a freebie
          next to any customer; only filled-in rows reach the PDF. */}
      {exportDraft && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 2300, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <div
            style={{
              background: 'white', borderRadius: 10, padding: 20, width: '90%', maxWidth: 860,
              maxHeight: '88vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{exportDraft.freebiesTitleText}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {exportDraft.scopeText || 'All breaks on this date'} · top customers by spend.
                  {' '}Only customers you enter a freebie for appear on the Freebies page.
                  {' '}Prefilled from freebies already saved for {exportDraft.freebieDate}; Submit saves
                  {' '}what's here, so clearing a box removes it.
                </div>
              </div>
              <button className="detail-panel-close" onClick={() => { setExportDraft(null); setFreebieNotes({}); }}>&times;</button>
            </div>

            <div style={{ marginTop: 12 }}>
              <FirstTimeBuyersToggle checked={exportFirstTimeOnly} onChange={setExportFirstTimeOnly}
                roster={exportFreebieRoster} shown={exportVisibleCustomers.length} total={exportDraft.customers.length} />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', marginTop: 10, border: '1px solid var(--border)', borderRadius: 6 }}>
              {exportDraft.customers.length === 0 ? (
                <div style={{ color: 'var(--text-light)', fontSize: 13, padding: 16 }}>
                  No break customers in this scope.
                </div>
              ) : exportVisibleCustomers.length === 0 ? (
                <div style={{ color: 'var(--text-light)', fontSize: 13, padding: 16 }}>
                  {exportFreebieRoster.ready ? 'No first time break buyers in this scope.' : 'Checking first time break buyers…'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {[
                        ['Username', 'left'], ['Spend', 'right'], ['Breaks', 'right'],
                        ['Spots', 'right'], ['Hits', 'right'], ['Hit %', 'right'], ['Freebies', 'left'],
                      ].map(([label, align]) => (
                        <th key={label} style={{
                          textAlign: align, padding: '8px', fontSize: 12, fontWeight: 700,
                          borderBottom: '2px solid var(--border)', background: 'var(--bg)',
                          position: 'sticky', top: 0, zIndex: 1,
                        }}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {exportVisibleCustomers.map(c => {
                      const details = c.break_details || [];
                      const expanded = details.length > 0 && exportExpanded.has(c.customer);
                      return (
                      <React.Fragment key={c.customer}>
                      <tr>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13, fontWeight: 600 }}>
                          <FreebieCustomerName customer={c.customer} inScope={c.in_scope !== false}
                            roster={exportFreebieRoster} onOpen={setFreebieSummaryBuyer}
                            expanded={expanded}
                            onToggle={details.length > 0 ? () => toggleExpanded(setExportExpanded, c.customer) : null} />
                        </td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13, textAlign: 'right' }}>{formatCurrency(c.spend)}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13, textAlign: 'right' }}>{c.breaks}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13, textAlign: 'right' }}>{c.spots}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13, textAlign: 'right' }}>{c.hits}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 13, textAlign: 'right' }}>{c.hit_rate_pct}%</td>
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                          <input
                            type="text"
                            value={freebieNotes[c.customer] || ''}
                            onChange={e => setFreebieNotes(prev => ({ ...prev, [c.customer]: e.target.value }))}
                            placeholder="Add freebie…"
                            style={{ width: '100%', minWidth: 160, padding: '5px 7px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 5 }} />
                        </td>
                      </tr>
                      {expanded && (
                        <FreebieBreakDetailsRow details={details} colSpan={7} showSpend />
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {canManage && (
                  <button className="btn btn-secondary btn-sm" onClick={openCogsPanel}>Add COGS</button>
                )}
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {Object.values(freebieNotes).filter(v => v && v.trim()).length} freebie(s) entered
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setExportDraft(null); setFreebieNotes({}); }}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={() => buildExportPdf(exportDraft, freebieNotes)}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Add COGS — day-level freebie cost, opened from either Freebies
          surface (the export step or the standalone panel). Sits above
          whichever one opened it so its customer table stays put. */}
      {(exportDraft || showFreebiePanel) && showCogsPanel && (() => {
        const dates = cogsScopeDates();
        const total = cogsEntries.reduce((sum, e) => sum + e.quantity * e.unit_cogs, 0);
        // Freebie entries store one flat unit cost; shape them the way
        // ProductTable reads products so the panel reuses the sidebar's list.
        const cogsProducts = cogsEntries.map(e => ({
          id: e.id,
          product_id: e.product_id,
          product_name: e.name,
          custom_name: e.name,
          cogs: e.unit_cogs,
          custom_cogs: e.unit_cogs,
          quantity: e.quantity,
        }));
        return (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              zIndex: 2400, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <div
              style={{
                background: 'white', borderRadius: 10, padding: 20, width: '90%', maxWidth: 620,
                maxHeight: '88vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Add COGS</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                    Products given away on this day. The cost is booked to the date below — not to any
                    break — and shows in Financials as <strong>Freebie COGS</strong>.
                  </div>
                </div>
                <button className="detail-panel-close" onClick={closeCogsPanel}>&times;</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Date:</span>
                {dates.length > 1 ? (
                  <select value={cogsDate}
                    onChange={e => { setCogsDate(e.target.value); loadCogsEntries(e.target.value); }}
                    style={{ padding: '5px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 5 }}>
                    {dates.map(d => <option key={d} value={d}>{formatDate(d)}</option>)}
                  </select>
                ) : (
                  <strong>{formatDate(cogsDate)}</strong>
                )}
              </div>

              {/* Same shape as the Break Products list in the break sidebar:
                  edit Qty in place, Remove per row, running totals underneath,
                  then the search box / + Other beneath the table. */}
              <div style={{ flex: 1, overflowY: 'auto', marginTop: 12 }}>
                <ProductTable
                  products={cogsProducts}
                  onUpdateQty={updateCogsEntryQty}
                  onRemove={removeCogsEntry}
                  hidePrice
                  emptyText="Nothing recorded for this date yet."
                />
              </div>

              <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <ProductSearchDropdown value={cogsSearch}
                  onChange={e => { setCogsSearch(e.target.value); searchProducts(e.target.value, 'freebie-cogs'); }}
                  onSelect={addCogsProduct} results={cogsResults} />
                <div style={{ marginTop: '6px' }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCogsOtherForm(true)}>+ Other</button>
                </div>
                {showCogsOtherForm && (
                  <OtherProductForm form={cogsOtherForm} setForm={setCogsOtherForm}
                    onAdd={addCogsOther} onCancel={() => setShowCogsOtherForm(false)} />
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14 }}>
                <div style={{ fontSize: 13 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Freebie COGS for {formatDate(cogsDate)}: </span>
                  <strong>{formatCurrency(total)}</strong>
                </div>
                <button className="btn btn-primary btn-sm" onClick={closeCogsPanel}>Done</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showSpots && selected?.category && (
        <BreakSpotsSheet
          // Keyed on the break so switching breaks (Start Next Break) remounts
          // the sheet with fresh spots/WS state instead of carrying the
          // previous break's rows over.
          key={selected.id}
          breakId={selected.id}
          category={selected.category}
          spotType={selected.spot_type || 'Team'}
          breakName={selected.name}
          breakNumber={selected.break_number}
          breakStatus={selected.status}
          finalScore={selected.final_score}
          targetPrice={selectedProducts.reduce((s, p) => s + getItemPrice(p) * p.quantity, 0)}
          breakCogs={selectedProducts.reduce((s, p) => s + (getItemCogs(p) || 0) * p.quantity, 0)}
          showHighBidOverlay={!!selected.show_high_bid_overlay}
          onNameChange={newName => { setSelected(prev => ({ ...prev, name: newName })); fetchBreaks(); }}
          onStatusChange={newStatus => { setSelected(prev => ({ ...prev, status: newStatus })); fetchBreaks(); }}
          onProductsChange={products => { setSelectedProducts(products); fetchBreaks(); }}
          nextBreak={nextBreakUp}
          onStartNextBreak={startNextBreak}
          timing={selected}
          onBreakEnded={updated => {
            setSelected(prev => ({ ...prev, ...updated }));
            fetchBreaks();
          }}
          onClose={() => {
            setShowSpots(false);
            navigate(`/breaks/${selected.break_number}`, { replace: true });
            // Refresh break detail, spots, and trades
            api.get(`/api/breaks/${selected.id}`).then(({ data }) => {
              setSelected(data.break);
              setSelectedProducts(data.break.products || []);
            }).catch(() => {});
            api.get(`/api/breaks/${selected.id}/spots`).then(({ data }) => { setDetailSpots(data.spots || []); setDetailHighBidSpotId(data.high_bid_spot_id ?? null); }).catch(() => {});
            api.get(`/api/breaks/${selected.id}/trades`).then(({ data }) => setDetailTrades(data.trades || [])).catch(() => {});
            api.get(`/api/breaks/${selected.id}/trade-ins`).then(({ data }) => setDetailTradeIns(data.trade_ins || [])).catch(() => {});
            fetchBreaks();
          }}
        />
      )}

      {/* Customer summary opened from a Freebies table's black icon. Sits
          above both Freebies surfaces (2300) and the Add COGS panel (2400).
          A username edit re-links the handle the rosters key on, so re-pull
          whichever one is open. */}
      {freebieSummaryBuyer && (
        <CustomerDetailModal
          customer={freebieSummaryBuyer}
          canEdit={canSeeFinancials}
          zIndex={2500}
          onClose={() => setFreebieSummaryBuyer(null)}
          onUsernameUpdated={() => { exportFreebieRoster.refresh(); panelFreebieRoster.refresh(); }}
        />
      )}
    </div>
  );
}
